"use client";

import CorrectionNote, { correctionHref } from "@/components/CorrectionNote";
import ScanPromo from "@/components/ScanPromo";
import { useState, useEffect, type ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { BlogPost, ContentBlock } from "@/lib/blog-posts";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";
import AlertSignup from "@/components/AlertSignup";
import BlogNextSteps, { type NextStep } from "@/components/BlogNextSteps";
import ArticleEndMarker from "@/components/ArticleEndMarker";
import { GlossaryText, GlossaryScope } from "@/components/GlossaryText";
import { track } from "@/lib/track";
import { PLAIN_CALLOUTS } from "@/lib/home-layout";
import BlogHeroAnimation from "@/components/BlogHeroAnimation";

/**
 * Floating scroll toggle. Bottom-right corner. Switches between a down arrow
 * (when the reader is near the top of a long blog post) and an up arrow
 * (once they've scrolled). Click jumps to the opposite end with smooth scroll.
 *
 * Useful for blog posts that run several thousand words on mobile, where
 * thumb-scrolling the full length is tedious. Hidden when the page hasn't
 * scrolled yet AND when the page is short enough that scrolling isn't a chore.
 */
function ScrollToggle() {
  const [direction, setDirection] = useState<"down" | "up">("down");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const update = () => {
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const viewportH = window.innerHeight;

      // Hide entirely on short pages where the toggle adds nothing.
      if (docHeight - viewportH < 800) {
        setVisible(false);
        return;
      }

      // Show after a small initial scroll so the hero header reads cleanly.
      // While the reader is in the first viewport, the button points down
      // (jump to bottom). After they've passed the first viewport, it
      // points up (back to top, the more common need).
      if (scrollY < 120) {
        // Still at the very top; keep the page un-cluttered above the fold.
        setVisible(false);
      } else if (scrollY < viewportH * 0.8) {
        setVisible(true);
        setDirection("down");
      } else {
        setVisible(true);
        setDirection("up");
      }
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const handleClick = () => {
    const target =
      direction === "up" ? 0 : document.documentElement.scrollHeight;
    window.scrollTo({ top: target, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={direction === "up" ? "Scroll to top" : "Scroll to bottom"}
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: "#0E5F56",
        color: "#FFFFFF",
        border: "none",
        boxShadow: "0 4px 12px rgba(14, 95, 86, 0.28), 0 1px 3px rgba(0,0,0,0.12)",
        cursor: "pointer",
        zIndex: 50,
        display: visible ? "flex" : "none",
        alignItems: "center",
        justifyContent: "center",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.2s ease, transform 0.2s ease",
        // Tap highlight cleanup for iOS Safari.
        WebkitTapHighlightColor: "transparent",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.06)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
    >
      <svg
        width={20}
        height={20}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{
          transform: direction === "up" ? "rotate(0deg)" : "rotate(180deg)",
          transition: "transform 0.2s ease",
        }}
      >
        <polyline points="18 15 12 9 6 15" />
      </svg>
    </button>
  );
}

/** Data-viz accents shared by the waffle, dot plot, block strip and ruler. */
const VIZ_HOT = "#C2410C";
const VIZ_COOL = "#E7E3DC";
/** Second-drug color for 2-medicine comparisons (price-cliff, year-ring). */
const PRICE_B = "#A16207";

const T = {
  bg: "#FAFAF8",
  surface: "#FFFFFF",
  border: "#E8E6E1",
  borderLight: "#F0EEEA",
  text: "#1C1917",
  textSecondary: "#57534E",
  textMuted: "#78716C",
  primary: "#0E5F56",
  primaryLight: "#EEF8F5",
  primaryMedium: "#C6E7E1",
  primaryDark: "#093F39",
  sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  serif: "'Lora', 'Georgia', 'Times New Roman', serif",
  r2: "10px",
  r3: "14px",
};

function formatDate(iso: string): string {
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/**
 * Parse markdown-style inline links [text](url) within plain text.
 * Returns an array of React nodes (strings and <a> elements).
 */
/**
 * Parse **bold** markers in plain text, returning React nodes with
 * GlossaryText applied to non-bold segments.
 */
function parseBoldText(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const boldRegex = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = boldRegex.exec(text)) !== null) {
    if (m.index > last) {
      parts.push(<GlossaryText key={`${keyPrefix}-t${last}`} text={text.slice(last, m.index)} />);
    }
    parts.push(<strong key={`${keyPrefix}-b${m.index}`} style={{ fontWeight: 700, color: T.text }}>{m[1]}</strong>);
    last = boldRegex.lastIndex;
  }
  if (last < text.length) {
    parts.push(<GlossaryText key={`${keyPrefix}-t${last}`} text={text.slice(last)} />);
  }
  return parts.length > 0 ? parts : [<GlossaryText key={`${keyPrefix}-full`} text={text} />];
}

function parseInlineLinks(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Text before the link (with bold + glossary support)
    if (match.index > lastIndex) {
      parts.push(...parseBoldText(text.slice(lastIndex, match.index), `gt-${lastIndex}`));
    }
    const linkText = match[1];
    const url = match[2];
    const isInternal = url.startsWith("/");
    parts.push(
      <a
        key={`link-${match.index}`}
        href={url}
        {...(isInternal ? {} : { target: "_blank", rel: "noopener" })}
        style={{
          color: T.primary,
          textDecoration: "underline",
          textDecorationColor: T.primaryMedium,
          textUnderlineOffset: "2px",
          transition: "text-decoration-color 0.15s",
        }}
      >
        {linkText}
      </a>
    );
    lastIndex = regex.lastIndex;
  }
  // Remaining text after last link (with bold + glossary support)
  if (lastIndex < text.length) {
    parts.push(...parseBoldText(text.slice(lastIndex), `gt-${lastIndex}`));
  }
  return parts.length > 0 ? parts : parseBoldText(text, "gt-full");
}

/* Weight dial: enzyme infusions are priced per unit and dosed per kg, so a
 * year's cost follows body weight; pills do not. Server renders defaultKg. */
/** "What applies to you" chooser for label-driven choices (fit-finder block). */
function FitFinder({ block }: { block: Extract<ContentBlock, { type: "fit-finder" }> }) {
  const [on, setOn] = useState<string[]>(block.defaultOn || []);
  const toggle = (id: string) => setOn((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const toneMark = { plus: "+", caution: "!", neutral: "\u2013" } as const;
  const toneWord = { plus: "In its favor", caution: "Caution on the label", neutral: "No difference noted" } as const;
  return (
    <figure className="blog-ff" style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
      {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
      {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
      <div className="blog-ff-prompt">{block.prompt}</div>
      <div className="blog-ff-chips" role="group" aria-label={block.prompt}>
        {block.questions.map((q) => (
          <button key={q.id} type="button" aria-pressed={on.includes(q.id)} className={on.includes(q.id) ? "on" : ""} onClick={() => toggle(q.id)}>
            <span className="blog-ff-box" aria-hidden="true">{on.includes(q.id) ? "\u2713" : ""}</span>{q.label}
          </button>
        ))}
      </div>
      <div className="blog-ff-cards">
        {block.options.map((o, j) => {
          const gap = Math.round((o.response.drug - o.response.placebo) * 10) / 10;
          const picked = block.questions.filter((q) => on.includes(q.id) && o.answers[q.id]);
          return (
            <div key={j} className="blog-ff-card">
              <div className="blog-ff-head">
                {o.href ? <a href={o.href}>{o.label}</a> : <b>{o.label}</b>}
                {o.sub && <span>{o.sub}</span>}
                {o.status && <em>{o.status}</em>}
              </div>
              <div className="blog-ff-resp" role="img" aria-label={`${block.responseLabel}: ${o.response.drug}% on ${o.label} vs ${o.response.placebo}% on placebo`}>
                <div className="blog-ff-resp-label">{block.responseLabel}</div>
                <div className="blog-ff-row"><span className="blog-ff-who">{o.label}</span><span className="blog-ff-track"><i style={{ width: `${o.response.drug}%` }} /></span><b>{o.response.drug}%</b></div>
                <div className="blog-ff-row pl"><span className="blog-ff-who">Placebo</span><span className="blog-ff-track"><i style={{ width: `${Math.max(o.response.placebo, 1.5)}%` }} /></span><b>{o.response.placebo}%</b></div>
                <div className="blog-ff-gap">+{gap} points over placebo <small>{o.response.source}</small></div>
              </div>
              <ul className="blog-ff-facts">{o.facts.map((f, k) => <li key={k}>{parseInlineLinks(f)}</li>)}</ul>
              <div className="blog-ff-for" aria-live="polite">
                {picked.length === 0 ? (
                  <p className="blog-ff-empty">Tap what applies to you above to see what this label says.</p>
                ) : picked.map((q) => {
                  const a = o.answers[q.id];
                  return (
                    <div key={q.id} className={`blog-ff-ans ${a.tone}`}>
                      <span className="blog-ff-mark" title={toneWord[a.tone]} aria-label={toneWord[a.tone]}>{toneMark[a.tone]}</span>
                      <div><b>{q.label}</b><p>{parseInlineLinks(a.text)}</p></div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
    </figure>
  );
}

function WeightDial({ block }: { block: Extract<ContentBlock, { type: "weight-dial" }> }) {
  const [kg, setKg] = useState(block.defaultKg);
  const [doseIdx, setDoseIdx] = useState(0);
  const dose = block.doses[doseIdx];
  const yearlyAt = (perUnit: number, w: number, d = dose) => perUnit * d.unitsPerKg * w * d.perYear;
  const top = Math.max(
    ...block.infusions.map((f) => yearlyAt(f.perUnit, block.maxKg, block.doses[0])),
    ...block.flats.map((f) => f.yearly),
  );
  const money = (v: number) => "$" + (Math.round(v / 100) * 100).toLocaleString("en-US");
  const lb = Math.round(kg * 2.20462);
  const ticks: number[] = [];
  for (let t = Math.ceil(block.minKg / 10) * 10; t <= block.maxKg; t += 10) ticks.push(t);
  const pos = (w: number) => ((w - block.minKg) / (block.maxKg - block.minKg)) * 100;
  return (
    <figure className="blog-wd" style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
      {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
      {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
      <div className="blog-wd-top">
        <div className="blog-wd-read" aria-live="polite">
          <span className="blog-wd-kg">{kg}<small> kg</small></span>
          <span className="blog-wd-lb">{lb} lb body weight</span>
        </div>
        {block.doses.length > 1 && (
          <div className="blog-wd-doses" role="group" aria-label="Dose">
            {block.doses.map((d, j) => (
              <button key={j} type="button" aria-pressed={doseIdx === j} onClick={() => setDoseIdx(j)} className={doseIdx === j ? "on" : ""}>{d.label}</button>
            ))}
          </div>
        )}
      </div>
      <div className="blog-wd-ruler">
        <input type="range" min={block.minKg} max={block.maxKg} step={1} value={kg} onChange={(e) => setKg(Number(e.target.value))} aria-label="Body weight in kilograms" aria-valuetext={`${kg} kilograms, ${lb} pounds`} />
        <div className="blog-wd-ticks" aria-hidden="true">
          {ticks.map((t) => <span key={t} style={{ left: `${pos(t)}%` }}>{t}</span>)}
        </div>
      </div>
      <div className="blog-wd-presets">
        {block.presets.map((p) => (
          <button key={p.kg} type="button" className={kg === p.kg ? "on" : ""} onClick={() => setKg(p.kg)}>{p.label}</button>
        ))}
      </div>
      <ul className="blog-wd-rows">
        {block.infusions.map((f) => {
          const v = yearlyAt(f.perUnit, kg); const units = dose.unitsPerKg * kg; const vials = units / f.vialUnits;
          const full = Math.floor(vials); const part = vials - full;
          return (
            <li key={f.label}>
              <div className="blog-wd-name">
                {f.href ? <a href={f.href}>{f.label}</a> : <b>{f.label}</b>}
                {f.sub && <span>{f.sub}</span>}
              </div>
              <div className="blog-wd-bar"><i className="inf" style={{ width: `${Math.max(2, (v / top) * 100)}%` }} /></div>
              <div className="blog-wd-val"><b>{money(v)}</b><span>a year</span></div>
              <div className="blog-wd-vials" aria-label={`${units.toLocaleString("en-US")} units, about ${vials.toFixed(1)} vials per infusion`}>
                {Array.from({ length: Math.min(full, 40) }).map((_, k) => <i key={k} />)}
                {part > 0.04 && <i className="part" style={{ ["--fill" as string]: `${Math.round(part * 100)}%` }} />}
                <span>{units.toLocaleString("en-US")} units, about {vials.toFixed(1)} vials per infusion</span>
              </div>
            </li>
          );
        })}
        {block.flats.map((f) => (
          <li key={f.label} className="flat">
            <div className="blog-wd-name">
              {f.href ? <a href={f.href}>{f.label}</a> : <b>{f.label}</b>}
              {f.sub && <span>{f.sub}</span>}
            </div>
            <div className="blog-wd-bar"><i className="pill" style={{ width: `${Math.max(2, (f.yearly / top) * 100)}%` }} /></div>
            <div className="blog-wd-val"><b>{money(f.yearly)}</b><span>a year, any weight</span></div>
          </li>
        ))}
      </ul>
      <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
    </figure>
  );
}

/** Month-view calendar in the style of Apple Calendar (November 4, 2026).
 *  One calendar at a time behind a segmented control, weeks as rows with
 *  week numbers, the infusion day circled in red like "today", multi-day
 *  rules drawn as pastel event bars and single-day medians as colored dots
 *  listed under the grid, the way the iPhone month view lists events. Every
 *  page's text stays in the DOM for search engines and screen readers. */
function DayCalendar({ block }: { block: Extract<ContentBlock, { type: "day-calendar" }> }) {
  const [sel, setSel] = useState(0);
  const BAR = { daily: { bg: "#DCEBFF", fg: "#0A4FA8", line: "#2F7DF6" }, near: { bg: "#DFF5E3", fg: "#1D6B33", line: "#34A853" } } as const;
  const DOT = { early: "#F2994A", late: "#E5484D" } as const;
  const weeks = Math.ceil((block.days + 1) / 7);
  const SF = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', 'Segoe UI', sans-serif";
  return (
    <figure style={{ margin: "32px 0", fontFamily: SF }}>
      {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
      {block.note && <div style={{ fontSize: 13.5, color: T.textSecondary, fontFamily: T.sans, marginBottom: 12, lineHeight: 1.55 }}>{block.note}</div>}
      <div style={{ border: "1px solid #E5E5EA", borderRadius: 12, background: "#fff", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 14px 10px", borderBottom: "1px solid #E5E5EA" }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#1C1C1E", letterSpacing: -0.3 }}>{block.pages[sel].name} <span style={{ fontWeight: 400, color: "#8E8E93", fontSize: 15 }}>after infusion</span></div>
          <div role="tablist" aria-label="Choose a treatment" style={{ display: "inline-flex", background: "#EEEEF0", borderRadius: 8, padding: 2 }}>
            {block.pages.map((pg, j) => (
              <button key={j} role="tab" aria-selected={sel === j} onClick={() => setSel(j)} style={{ border: "none", cursor: "pointer", fontFamily: SF, fontSize: 13, fontWeight: sel === j ? 600 : 500, color: "#1C1C1E", padding: "5px 12px", borderRadius: 6, background: sel === j ? "#fff" : "transparent", boxShadow: sel === j ? "0 1px 2px rgba(0,0,0,0.15)" : "none" }}>{pg.name}</button>
            ))}
          </div>
        </div>
        {block.pages.map((pg, j) => {
          const marks = pg.marks || [];
          const wins = pg.windows || [];
          return (
            <div key={j} role="tabpanel" hidden={sel !== j} aria-label={pg.name}>
              <div style={{ padding: "8px 14px 10px", fontSize: 12.5, color: "#3C3C43", lineHeight: 1.45, borderBottom: "1px solid #F2F2F7", display: "grid", gap: 3 }}>
                <div style={{ color: "#8E8E93" }}>{pg.sub}</div>
                {pg.facts.map((f, k) => <div key={k}><span style={{ color: "#8E8E93" }}>{f.label}:</span> {parseInlineLinks(f.value)}</div>)}
              </div>
              {pg.pending ? (
                <div style={{ padding: "26px 18px", textAlign: "center", color: "#636366", fontSize: 13.5, lineHeight: 1.55 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#1C1C1E", marginBottom: 6 }}>No events yet</div>
                  {parseInlineLinks(pg.pending)}
                </div>
              ) : (
                <>
                  {Array.from({ length: weeks }, (_, w) => {
                    const start = w * 7, end = start + 6;
                    const rowWins = wins.filter((x) => x.to >= start && x.from <= end).sort((a, b) => (a.tone === "daily" ? -1 : 1) - (b.tone === "daily" ? -1 : 1));
                    return (
                      <div key={w} style={{ position: "relative", display: "grid", gridTemplateColumns: "34px repeat(7, 1fr)", minHeight: 22 + rowWins.length * 17 + 8, borderBottom: w < weeks - 1 ? "1px solid #E5E5EA" : "none" }}>
                        <span style={{ fontSize: 10.5, color: "#AEAEB2", padding: "6px 0 0 8px", fontVariantNumeric: "tabular-nums" }}>W{w + 1}</span>
                        {Array.from({ length: 7 }, (_, c) => {
                          const d = start + c;
                          if (d > block.days) return <span key={c} style={{ borderLeft: "1px solid #F2F2F7", background: "#FAFAFA" }} />;
                          const m = marks.find((x) => x.day === d);
                          return (
                            <span key={c} title={m ? `Day ${d}: ${m.label}` : undefined} style={{ borderLeft: "1px solid #F2F2F7", padding: "4px 5px 0", display: "flex", justifyContent: "flex-end", alignItems: "flex-start", gap: 3 }}>
                              {m && <i aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: DOT[m.tone], marginTop: 6 }} />}
                              <span style={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums", minWidth: 20, height: 20, lineHeight: "20px", textAlign: "center", borderRadius: 10, padding: "0 3px", color: d === 0 ? "#fff" : "#1C1C1E", background: d === 0 ? "#FF3B30" : "transparent", fontWeight: d === 0 ? 700 : 400 }}>{d}</span>
                            </span>
                          );
                        })}
                        {rowWins.map((x, k) => {
                          const a = Math.max(x.from, start) - start, b = Math.min(x.to, end) - start;
                          const first = x.from >= start;
                          const c = BAR[x.tone];
                          return (
                            <span key={k} aria-hidden="true" style={{ position: "absolute", top: 26 + k * 17, left: `calc(34px + (100% - 34px) * ${a / 7} + 2px)`, width: `calc((100% - 34px) * ${(b - a + 1) / 7} - 4px)`, height: 15, borderRadius: 4, background: c.bg, borderLeft: first ? `3px solid ${c.line}` : "none", color: c.fg, fontSize: 10.5, fontWeight: 600, lineHeight: "15px", padding: "0 5px", boxSizing: "border-box", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{first ? x.label : ""}</span>
                          );
                        })}
                      </div>
                    );
                  })}
                  <div style={{ borderTop: "1px solid #E5E5EA", padding: "6px 14px 10px" }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "6px 0", borderBottom: "1px solid #F2F2F7", fontSize: 13 }}>
                      <span style={{ width: 52, flex: "0 0 52px", color: "#FF3B30", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>Day 0</span>
                      <span style={{ color: "#1C1C1E" }}>Infusion</span>
                    </div>
                    {wins.map((x, k) => (
                      <div key={`w${k}`} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "6px 0", borderBottom: "1px solid #F2F2F7", fontSize: 13 }}>
                        <span style={{ width: 52, flex: "0 0 52px", color: "#8E8E93", fontVariantNumeric: "tabular-nums" }}>{x.from}-{x.to}</span>
                        <span style={{ color: "#1C1C1E" }}><i aria-hidden="true" style={{ display: "inline-block", width: 3, height: 12, borderRadius: 2, background: BAR[x.tone].line, marginRight: 7, verticalAlign: -1 }} />{x.label}</span>
                      </div>
                    ))}
                    {marks.map((m, k) => (
                      <div key={`m${k}`} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "6px 0", borderBottom: k < marks.length - 1 ? "1px solid #F2F2F7" : "none", fontSize: 13 }}>
                        <span style={{ width: 52, flex: "0 0 52px", color: "#8E8E93", fontVariantNumeric: "tabular-nums" }}>Day {m.day}</span>
                        <span style={{ color: "#1C1C1E" }}><i aria-hidden="true" style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: DOT[m.tone], marginRight: 7 }} />{m.label}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "10px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
    </figure>
  );
}

function renderBlock(block: ContentBlock, i: number) {
  switch (block.type) {
    case "paragraph":
      return (
        <p key={i} style={{
          fontSize: 17, lineHeight: 1.85, color: T.textSecondary,
          margin: "0 0 20px", fontFamily: T.sans,
        }}>
          {parseInlineLinks(block.text)}
        </p>
      );

    case "body-map": {
      // Pins on a plain figure, numbered to match the cards beside it. The
      // figure is decoration; every number is in the cards' text.
      const PIN: Record<string, [number, number]> = { girdle: [66, 96], lungs: [80, 128], heart: [118, 156], blood: [146, 196], legs: [88, 318] };
      const half = Math.ceil(block.items.length / 2);
      const card = (it: (typeof block.items)[number], k: number) => (
        <div key={k} className="blog-bm-card">
          <div className="blog-bm-head"><span className="blog-bm-num" aria-hidden="true">{k + 1}</span><span className="blog-bm-label">{it.label}</span>{it.tag && <span className="blog-bm-tag">{it.tag}</span>}</div>
          <div className="blog-bm-measure">{it.measure}</div>
          <div className="blog-bm-row"><span className="blog-bm-who blog-bm-drug">Drug</span><span>{it.drug}</span></div>
          {it.placebo && <div className="blog-bm-row"><span className="blog-bm-who blog-bm-pbo">Placebo</span><span>{it.placebo}</span></div>}
          <div className="blog-bm-detail">{parseInlineLinks(it.detail)}</div>
        </div>
      );
      return (
        <figure key={i} className="blog-bm" style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-bm-grid">
            <div className="blog-bm-col">{block.items.slice(0, half).map((it, k) => card(it, k))}</div>
            <svg className="blog-bm-fig" viewBox="0 0 200 400" role="img" aria-label="Figure showing where in the body each result was measured">
              <circle cx="100" cy="42" r="26" fill={T.primaryLight} stroke={T.primary} strokeWidth="2" />
              <path d="M62 78 Q100 70 138 78 L150 92 Q156 150 150 210 L140 214 L138 120 L134 212 Q130 222 122 222 L78 222 Q70 222 66 212 L62 120 L60 214 L50 210 Q44 150 50 92 Z" fill={T.primaryLight} stroke={T.primary} strokeWidth="2" strokeLinejoin="round" />
              <path d="M78 222 L72 380 L94 380 L100 250 L106 380 L128 380 L122 222 Z" fill={T.primaryLight} stroke={T.primary} strokeWidth="2" strokeLinejoin="round" />
              {block.items.map((it, k) => {
                const [x, y] = PIN[it.region] || [100, 150];
                return (
                  <g key={k}>
                    <circle cx={x} cy={y} r="12" fill={T.primary} stroke={T.surface} strokeWidth="3" />
                    <text x={x} y={y + 4.5} textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff" fontFamily={T.sans}>{k + 1}</text>
                  </g>
                );
              })}
            </svg>
            <div className="blog-bm-col">{block.items.slice(half).map((it, k) => card(it, k + half))}</div>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "scan-promo":
      return <ScanPromo key={i} from={typeof window !== "undefined" ? `blog:${window.location.pathname.split("/").pop()}` : "blog"} />;

    case "bullets":
      return (
        <ul key={i} className="blog-bullets" style={{ margin: "0 0 22px", padding: 0, listStyle: "none", fontFamily: T.sans }}>
          {block.items.map((it, k) => (
            <li key={k} style={{ position: "relative", paddingLeft: 22, marginBottom: 10, fontSize: 17, lineHeight: 1.7, color: T.textSecondary }}>
              <span aria-hidden="true" style={{ position: "absolute", left: 4, top: "0.72em", width: 7, height: 7, borderRadius: "50%", background: T.primary }} />
              {parseInlineLinks(it)}
            </li>
          ))}
        </ul>
      );

    case "heading": {
      // Slugify heading text for id-based anchors so in-post navigation
      // (e.g. a "skip to FAQ" link from an intro callout) works.
      const id = block.text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");
      return (
        <h2 key={i} id={id} style={{
          fontSize: 24, fontWeight: 700, color: T.text,
          fontFamily: T.serif, letterSpacing: "-0.015em",
          margin: "8px 0 16px", lineHeight: 1.3,
          scrollMarginTop: 80, // room for any sticky header / float buttons
        }}>
          {block.text}
        </h2>
      );
    }

    case "subheading":
      return (
        <h3 key={i} style={{
          fontSize: 15, color: T.textMuted, fontFamily: T.sans,
          margin: "-8px 0 20px", fontStyle: "italic", fontWeight: 500,
        }}>
          {block.text}
        </h3>
      );

    case "faq-item": {
      // Prominent h3 question + paragraph answer. Visually styled to stand
      // out so readers can scan an FAQ section easily, while preserving the
      // semantic h3 hierarchy (page = h1, sections = h2, FAQ items = h3).
      // Split on \n\n so multi-paragraph answers render as separate <p>
      // elements, and route through parseInlineLinks so markdown-style
      // citation links (and bold/glossary terms) render properly inside FAQ
      // answers, not just in normal paragraphs.
      const paragraphs = block.answer.split(/\n\n+/);
      return (
        <div key={i} style={{ marginBottom: 28 }}>
          <h3 style={{
            fontSize: 19, fontWeight: 700, color: T.text,
            fontFamily: T.serif, letterSpacing: "-0.012em",
            margin: "0 0 10px", lineHeight: 1.35,
          }}>
            {block.question}
          </h3>
          {paragraphs.map((para, pi) => (
            <p key={pi} style={{
              fontSize: 17, lineHeight: 1.7, color: T.textSecondary,
              fontFamily: T.serif,
              margin: pi === paragraphs.length - 1 ? 0 : "0 0 14px",
            }}>
              {parseInlineLinks(para)}
            </p>
          ))}
        </div>
      );
    }

    case "callout": {
      const colors = {
        teal: { bg: "#F7FAFA", text: "#1C1917", icon: "💡" },
        amber: { bg: "#FEFCF3", text: "#1C1917", icon: "⚡" },
        slate: { bg: "#F5F5F0", text: "#1C1917", icon: "📌" },
      };
      const c = colors[block.style || "teal"];

      // Callouts can contain a numbered or bulleted list when the text uses
      // newline-delimited list-marker prefixes ("1. ", "2. ", "- ", "* ").
      // The renderer splits the text into segments. Each segment is either
      // a paragraph (no list markers) or a list (consecutive list-marker
      // lines). This preserves the callout visual styling while letting
      // posts render proper <ol>/<ul> lists inside callouts.
      type Segment =
        | { kind: "paragraph"; text: string }
        | { kind: "ol"; items: string[] }
        | { kind: "ul"; items: string[] };

      const lines = block.text.split(/\n/);
      const segments: Segment[] = [];
      let currentList: { kind: "ol" | "ul"; items: string[] } | null = null;
      let currentParaParts: string[] = [];

      const flushPara = () => {
        if (currentParaParts.length) {
          const text = currentParaParts.join(" ").trim();
          if (text) segments.push({ kind: "paragraph", text });
          currentParaParts = [];
        }
      };
      const flushList = () => {
        if (currentList) {
          segments.push(currentList);
          currentList = null;
        }
      };

      for (const raw of lines) {
        const line = raw.trim();
        if (!line) {
          flushPara();
          flushList();
          continue;
        }
        const olMatch = line.match(/^\d+\.\s+(.*)$/);
        const ulMatch = line.match(/^[-*]\s+(.*)$/);
        if (olMatch) {
          flushPara();
          if (!currentList || currentList.kind !== "ol") {
            flushList();
            currentList = { kind: "ol", items: [] };
          }
          currentList.items.push(olMatch[1]);
        } else if (ulMatch) {
          flushPara();
          if (!currentList || currentList.kind !== "ul") {
            flushList();
            currentList = { kind: "ul", items: [] };
          }
          currentList.items.push(ulMatch[1]);
        } else {
          flushList();
          currentParaParts.push(line);
        }
      }
      flushPara();
      flushList();

      const baseTextStyle = {
        fontSize: 15, lineHeight: 1.8, color: c.text,
        fontFamily: T.sans, fontWeight: 400,
        fontStyle: "italic" as const,
      };

      return (
        <aside key={i} role="note" style={PLAIN_CALLOUTS ? {
          borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
          padding: "20px 0",
          margin: "32px 0", position: "relative" as const,
        } : {
          background: c.bg,
          borderRadius: T.r3, padding: "24px 28px",
          margin: "32px 0", position: "relative" as const,
          border: "none",
        }}>
          {segments.map((seg, si) => {
            if (seg.kind === "paragraph") {
              return (
                <p key={si} style={{ ...baseTextStyle, margin: si === 0 ? 0 : "12px 0 0" }}>
                  {parseInlineLinks(seg.text)}
                </p>
              );
            }
            const Tag = seg.kind === "ol" ? "ol" : "ul";
            return (
              <Tag key={si} style={{
                ...baseTextStyle,
                margin: si === 0 ? 0 : "12px 0 0",
                paddingLeft: 24,
              }}>
                {seg.items.map((item, ii) => (
                  <li key={ii} style={{ marginBottom: 6 }}>
                    {parseInlineLinks(item)}
                  </li>
                ))}
              </Tag>
            );
          })}
          {block.button && (
            <div style={{ marginTop: 18 }}>
              <style>{`
                @keyframes tfCalloutGlow {
                  0% { box-shadow: 0 0 0 0 rgba(14,95,86,0.45); }
                  70% { box-shadow: 0 0 0 14px rgba(14,95,86,0); }
                  100% { box-shadow: 0 0 0 0 rgba(14,95,86,0); }
                }
                .tf-callout-btn { animation: tfCalloutGlow 2.2s infinite; transition: transform 0.15s, background 0.15s; }
                .tf-callout-btn:hover { transform: scale(1.04); background: ${T.primaryDark} !important; }
                @media (prefers-reduced-motion: reduce) { .tf-callout-btn { animation: none; } }
              `}</style>
              <a href={block.button.href} className="tf-callout-btn"
                onClick={() => track({ event: "cta_click", source: "blog-callout", meta: { href: block.button!.href } })}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: T.primary, color: "#FFFFFF", fontSize: 15, fontWeight: 700,
                  fontFamily: T.sans, fontStyle: "normal", textDecoration: "none",
                  borderRadius: 999, padding: "12px 26px",
                }}>
                {block.button.label} <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
          )}
        </aside>
      );
    }

    // Editorial "by the numbers" unit: the first stat is the lead fact, set
    // huge in serif on the left; remaining stats stack as a ruled ledger on
    // the right. Asymmetric hierarchy, magazine-sidebar style.
    case "stat-row":
    case "stat-card": {
      const [lead, ...rest] = block.stats;
      if (!lead) return null;
      return (
        <figure key={i} className="blog-stat-row" role="list" aria-label="Key statistics" style={{ margin: "52px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 26 }}>
            <span style={{
              fontFamily: T.sans, fontSize: 11, fontWeight: 700,
              letterSpacing: "0.16em", textTransform: "uppercase" as const,
              color: T.primary, whiteSpace: "nowrap" as const,
            }}>
              By the numbers
            </span>
            <span aria-hidden style={{ flex: 1, height: 1, background: T.borderLight }} />
          </div>
          <div className="blog-stat-grid" style={{
            display: "grid",
            gridTemplateColumns: rest.length > 0 ? "5fr 7fr" : "1fr",
            gap: "0 44px",
          }}>
            <div role="listitem">
              <div className="blog-stat-lead-value" style={{
                fontFamily: T.serif, fontWeight: 600,
                fontSize: "clamp(48px, 7vw, 68px)",
                letterSpacing: "-0.04em", lineHeight: 0.98, color: T.text,
              }}>
                {lead.value}
              </div>
              <div style={{
                fontFamily: T.sans, fontSize: 14, color: T.textSecondary,
                lineHeight: 1.55, marginTop: 14, maxWidth: 250,
              }}>
                {lead.label}
              </div>
            </div>
            {rest.length > 0 && (
              <div className="blog-stat-rest" style={{
                borderLeft: `1px solid ${T.borderLight}`,
                paddingLeft: 44,
                display: "flex",
                flexDirection: "column" as const,
                justifyContent: "center" as const,
              }}>
                {rest.map((s, j) => (
                  <div key={j} role="listitem" style={{
                    padding: "15px 0",
                    borderTop: j > 0 ? `1px solid ${T.borderLight}` : "none",
                  }}>
                    <div style={{
                      fontFamily: T.serif, fontSize: 26, fontWeight: 600,
                      color: T.text, letterSpacing: "-0.02em", lineHeight: 1.1,
                    }}>
                      {s.value}
                    </div>
                    <div style={{
                      fontFamily: T.sans, fontSize: 13, color: T.textSecondary,
                      lineHeight: 1.5, marginTop: 3,
                    }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </figure>
      );
    }

    // "stat-card" (gradient tiles, icon squares) was retired October 25, 2026:
    // Jason read the colored tiles as template output. Both block types now
    // render as the plain "By the numbers" figure set.
    case "divider":
      return (
        <hr key={i} style={{
          border: "none",
          display: "flex", alignItems: "center",
          margin: "36px 0 32px",
          height: 5,
          background: "transparent",
        }}
        aria-hidden="true"
        />
      );

    case "quote":
      return (
        <blockquote key={i} style={PLAIN_CALLOUTS ? {
          margin: "36px 0", padding: "22px 0 0",
          borderTop: `1px solid ${T.border}`,
          background: "transparent",
        } : {
          margin: "32px 0", padding: "24px 32px",
          borderLeft: `3px solid ${T.primary}`,
          background: "transparent",
        }}>
          <p style={{
            fontSize: 22, fontWeight: 600, color: T.text,
            fontFamily: T.serif, fontStyle: "italic",
            lineHeight: 1.4, margin: 0,
          }}>
            &ldquo;{block.text}&rdquo;
          </p>
          {block.attribution && (
            <cite style={{
              fontSize: 13, color: T.textMuted, fontFamily: T.sans,
              display: "block", margin: "10px 0 0", fontStyle: "normal",
            }}>
              {block.attribution}
            </cite>
          )}
        </blockquote>
      );

    case "definition-list": {
      const isCompact = block.style === "compact";
      // PLAIN_CALLOUTS (October 24, 2026): the rainbow of pastel gradient
      // boxes read as template output. Items now sit on the page like an
      // editorial index: a hairline rule, the term in the serif face beside
      // the text on wide screens, stacked on phones. No fills, no colors.
      if (PLAIN_CALLOUTS) {
        return (
          <dl key={i} className={`blog-dl${isCompact ? " blog-dl-compact" : ""}`} style={{ margin: "30px 0 34px", padding: 0, borderBottom: `1px solid ${T.border}` }}>
            {block.items.map((item, j) => (
              <div key={j} className="blog-dl-row" style={{ borderTop: `1px solid ${T.border}` }}>
                <dt className="blog-dl-term" style={{ fontFamily: T.serif, fontWeight: 700, color: T.text, margin: 0 }}>
                  {item.term}
                </dt>
                <dd className="blog-dl-def" style={{ fontFamily: T.sans, color: T.textSecondary, margin: 0, fontWeight: 400 }}>
                  {parseInlineLinks(item.definition)}
                </dd>
              </div>
            ))}
          </dl>
        );
      }
      const dlPalette = [
        { bg: "linear-gradient(135deg, #EEF8F5 0%, #FFFFFF 100%)", accent: "#0E5F56", border: "#C6E7E1" },
        { bg: "linear-gradient(135deg, #F5F3FF 0%, #FFFFFF 100%)", accent: "#6D28D9", border: "#DDD6FE" },
        { bg: "linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)", accent: "#C2410C", border: "#FED7AA" },
        { bg: "linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)", accent: "#1D4ED8", border: "#BFDBFE" },
        { bg: "linear-gradient(135deg, #FDF2F8 0%, #FFFFFF 100%)", accent: "#BE185D", border: "#FBCFE8" },
        { bg: "linear-gradient(135deg, #EDF8F1 0%, #FFFFFF 100%)", accent: "#047857", border: "#A7F3D0" },
      ];
      return (
        <dl key={i} style={{
          margin: "28px 0",
          padding: 0,
          display: "flex",
          flexDirection: "column" as const,
          gap: isCompact ? 8 : 10,
        }}>
          {block.items.map((item, j) => {
            const pal = dlPalette[j % dlPalette.length];
            return (
              <div key={j} style={{
                padding: isCompact ? "14px 18px 14px 20px" : "18px 22px 18px 24px",
                background: pal.bg,
                borderRadius: "12px",
                border: `1px solid ${pal.border}`,
                
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}>
                <dt style={{
                  fontSize: isCompact ? 13 : 14,
                  fontWeight: 700,
                  color: pal.accent,
                  fontFamily: T.sans,
                  letterSpacing: "0.01em",
                  marginBottom: isCompact ? 4 : 6,
                  lineHeight: 1.3,
                }}>
                  {item.term}
                </dt>
                <dd style={{
                  fontSize: isCompact ? 14 : 16,
                  lineHeight: 1.7,
                  color: T.textSecondary,
                  fontFamily: T.sans,
                  margin: 0,
                  fontWeight: 400,
                }}>
                  {parseInlineLinks(item.definition)}
                </dd>
              </div>
            );
          })}
        </dl>
      );
    }

    case "pie-chart": {
      // Server-safe inline SVG donut. Crisp at any size, no image request,
      // and the palette matches the site theme.
      const PIE_COLORS = ["#0E5F56", "#2E9C8E", "#8FD9CC", "#D97706", "#57534E", "#1D4ED8", "#BE185D"];
      const total = block.slices.reduce((s, x) => s + x.value, 0) || 1;
      const R = 80, INNER = 46, CX = 100, CY = 100;
      let angle = -Math.PI / 2;
      const arcs = block.slices.map((sl, j) => {
        const frac = sl.value / total;
        const a0 = angle;
        const a1 = angle + frac * Math.PI * 2;
        angle = a1;
        const large = a1 - a0 > Math.PI ? 1 : 0;
        const p = (a: number, r: number) => `${CX + r * Math.cos(a)},${CY + r * Math.sin(a)}`;
        const d = `M ${p(a0, R)} A ${R} ${R} 0 ${large} 1 ${p(a1, R)} L ${p(a1, INNER)} A ${INNER} ${INNER} 0 ${large} 0 ${p(a0, INNER)} Z`;
        return { d, color: sl.color || PIE_COLORS[j % PIE_COLORS.length], label: sl.label, pct: Math.round(frac * 100) };
      });
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 20px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && (
            <figcaption style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 16, textAlign: "center" as const }}>
              {block.title}
            </figcaption>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 28, flexWrap: "wrap" as const }}>
            <svg viewBox="0 0 200 200" width={190} height={190} role="img" aria-label={block.title || "Pie chart"}>
              {arcs.map((a, j) => (
                <path key={j} d={a.d} fill={a.color} stroke="#FFFFFF" strokeWidth={1.5} />
              ))}
            </svg>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" as const, gap: 8, minWidth: 200 }}>
              {arcs.map((a, j) => (
                <li key={j} style={{ display: "flex", alignItems: "baseline", gap: 10, fontSize: 14, fontFamily: T.sans, color: T.textSecondary }}>
                  <span style={{ width: 11, height: 11, borderRadius: 3, background: a.color, flexShrink: 0, alignSelf: "center" }} />
                  <span style={{ fontWeight: 700, color: T.text, minWidth: 38 }}>{a.pct}%</span>
                  <span>{a.label}</span>
                </li>
              ))}
            </ul>
          </div>
          {block.note && (
            <div style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans, marginTop: 14, textAlign: "center" as const, lineHeight: 1.5 }}>
              {block.note}
            </div>
          )}
        </figure>
      );
    }

    case "cta": {
      return (
        <div key={i} style={{ margin: "36px 0", background: T.primaryDark, borderRadius: 16, padding: "30px 28px", textAlign: "center" as const }}>
          <style>{`
            @keyframes tfCtaPulse {
              0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.55); }
              70% { box-shadow: 0 0 0 16px rgba(255,255,255,0); }
              100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
            }
            .tf-cta-btn { animation: tfCtaPulse 2.2s infinite; }
            .tf-cta-btn:hover { transform: scale(1.04); }
            @media (prefers-reduced-motion: reduce) { .tf-cta-btn { animation: none; } }
          `}</style>
          <div style={{ fontSize: 21, fontWeight: 700, color: "#FFFFFF", fontFamily: T.serif, marginBottom: 8, letterSpacing: "-0.01em" }}>
            {block.heading}
          </div>
          {block.sub && (
            <div style={{ fontSize: 14.5, color: "rgba(255,255,255,0.82)", fontFamily: T.sans, lineHeight: 1.6, maxWidth: 460, margin: "0 auto 18px" }}>
              {block.sub}
            </div>
          )}
          <a href={block.href} className="tf-cta-btn"
            onClick={() => track({ event: "cta_click", source: "blog-cta", meta: { href: block.href, heading: block.heading.slice(0, 60) } })}
            style={{
            display: "inline-block", background: "#FFFFFF", color: T.primaryDark,
            fontSize: 15.5, fontWeight: 700, fontFamily: T.sans, textDecoration: "none",
            borderRadius: 999, padding: "13px 30px", transition: "transform 0.15s",
          }}>
            {block.buttonLabel}
          </a>
        </div>
      );
    }

    case "timeline-gap": {
      // Server-safe HTML gap chart. Design notes: no full-width track (the
      // "progress bar" look), just the measured bar against faint vertical
      // gridlines; the day-count label rides the end of its own bar; edge
      // ticks align inward so nothing clips the container.
      const FAST = T.primary;
      const SLOW = "#B45309";
      const tickPos = (j: number, count: number): React.CSSProperties =>
        j === 0 ? {} : j === count - 1 ? { transform: "translateX(-100%)" } : { transform: "translateX(-50%)" };
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "26px 24px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && (
            <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>
              {block.title}
            </figcaption>
          )}
          {block.note && (
            <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 20, lineHeight: 1.5 }}>
              {block.note}
            </div>
          )}
          <div role="img" aria-label={block.title || "Timeline comparison chart"}>
            <div style={{ position: "relative", height: 18, marginBottom: 8 }} aria-hidden="true">
              {block.ticks.map((t, j) => (
                <span key={j} style={{ position: "absolute", left: `${(t.day / block.maxDays) * 100}%`, fontSize: 11, color: T.textMuted, fontFamily: T.sans, whiteSpace: "nowrap" as const, letterSpacing: "0.02em", ...tickPos(j, block.ticks.length) }}>
                  {t.label}
                </span>
              ))}
            </div>
            {block.rows.map((r, j) => {
              const w = Math.max((r.days / block.maxDays) * 100, 1.5);
              const color = r.tone === "slow" ? SLOW : FAST;
              const labelAfterBar = w <= 72;
              return (
                <div key={j} style={{ padding: "10px 0 12px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" as const, marginBottom: 7 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif }}>{r.label}</span>
                    <span style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans }}>{r.sub}</span>
                    {!labelAfterBar && (
                      <span style={{ fontSize: 12.5, fontWeight: 700, color, fontFamily: T.sans, marginLeft: "auto" }}>{r.display}</span>
                    )}
                  </div>
                  {/* Bar strip: gridline ticks live only inside this 20px band,
                      so they never cross the text. */}
                  <div style={{ position: "relative", height: 20 }}>
                    {block.ticks.map((t, k) => (
                      <div key={k} aria-hidden="true" style={{ position: "absolute", top: 0, bottom: 0, left: `${(t.day / block.maxDays) * 100}%`, width: 1, background: k === 0 ? T.border : T.borderLight }} />
                    ))}
                    <div style={{ position: "absolute", left: 0, top: 6, height: 8, width: `${w}%`, background: color, borderRadius: "0 2px 2px 0" }} />
                    <div style={{ position: "absolute", left: `${w}%`, top: 10, transform: "translate(-50%, -50%)", width: 12, height: 12, borderRadius: "50%", background: color, border: `2.5px solid ${T.surface}` }} />
                    {labelAfterBar && (
                      <span style={{ position: "absolute", left: `${w}%`, top: 10, transform: "translateY(-50%)", marginLeft: 12, fontSize: 12.5, fontWeight: 700, color, fontFamily: T.sans, whiteSpace: "nowrap" as const, lineHeight: 1 }}>{r.display}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, marginTop: 7, lineHeight: 1.5 }}>{r.detail}</div>
                </div>
              );
            })}
          </div>
        </figure>
      );
    }

    case "contrast-pair": {
      // Two self-contained panels divided by a rule. Each states its own
      // context, verdict, and explanation, so a reader who skims straight
      // into the right-hand panel still knows what was compared.
      const ALERT_BG = "#FEF8F0";
      const ALERT_TEXT = "#B45309";
      return (
        <figure key={i} className="blog-contrast-pair-wrap" style={{ margin: "40px 0", border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
          <div className="blog-contrast-pair" style={{ display: "grid", gridTemplateColumns: `repeat(${block.panels.length}, 1fr)` }}>
            {block.panels.map((p, j) => {
              const alert = p.tone === "alert";
              return (
                <div key={j} style={{
                  padding: "22px 22px 24px",
                  background: alert ? ALERT_BG : T.surface,
                  borderLeft: j === 0 ? "none" : `1px solid ${T.border}`,
                }}>
                  <div style={{
                    fontFamily: T.sans, fontSize: 10.5, fontWeight: 700,
                    letterSpacing: "0.14em", textTransform: "uppercase" as const,
                    color: alert ? ALERT_TEXT : T.textMuted, marginBottom: 12, lineHeight: 1.4,
                  }}>
                    {p.context}
                  </div>
                  <div style={{
                    fontFamily: T.serif, fontSize: 25, fontWeight: 700, lineHeight: 1.2,
                    color: alert ? ALERT_TEXT : T.text, marginBottom: 11,
                  }}>
                    {p.verdict}
                  </div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.65, color: T.textSecondary, fontFamily: T.sans }}>
                    {parseInlineLinks(p.body)}
                  </div>
                </div>
              );
            })}
          </div>
        </figure>
      );
    }

    case "metric-bars": {
      // Each metric gets its own column and its own scale, so a % and a
      // month count can sit side by side without one pretending to be the
      // other. Stacks to one column per row on phones (see .blog-metric-row).
      const TONE: Record<string, { bg: string; fg: string }> = {
        good: { bg: "#EDF8F1", fg: "#0B5A3E" },
        bad: { bg: "#FEF2F2", fg: "#991B1B" },
        next: { bg: "#FEF3C7", fg: "#92400E" },
        neutral: { bg: "#F5F4F2", fg: T.textSecondary },
      };
      const cols = `minmax(150px, 1.1fr) repeat(${block.metrics.length}, minmax(0, 1fr))`;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="table" aria-label={block.title || "Comparison chart"}>
            <div role="row" className="blog-metric-head" style={{ display: "grid", gridTemplateColumns: cols, gap: 14, padding: "0 10px 8px", borderBottom: `1px solid ${T.border}` }}>
              <span role="columnheader" />
              {block.metrics.map((m, k) => (
                <span key={k} role="columnheader" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" as const, color: T.textMuted, fontFamily: T.sans }}>{m.label}</span>
              ))}
            </div>
            {block.rows.map((r, j) => {
              const tone = TONE[r.tagTone || "neutral"];
              return (
                <div key={j} role="row" className="blog-metric-row" style={{
                  display: "grid", gridTemplateColumns: cols, gap: 14, alignItems: "center",
                  padding: "12px 10px", borderBottom: j === block.rows.length - 1 ? "none" : `1px solid ${T.borderLight}`,
                  background: r.highlight ? T.primaryLight : "transparent", borderRadius: r.highlight ? 10 : 0,
                }}>
                  <div role="rowheader">
                    <div style={{ fontSize: 15, fontWeight: 700, color: r.highlight ? T.primaryDark : T.text, fontFamily: T.serif, lineHeight: 1.25 }}>{r.label}</div>
                    {r.sub && <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.sans, marginTop: 2 }}>{r.sub}</div>}
                    {r.tag && <span style={{ display: "inline-block", marginTop: 5, fontSize: 10.5, fontWeight: 700, fontFamily: T.sans, padding: "2px 7px", borderRadius: 4, background: tone.bg, color: tone.fg }}>{r.tag}</span>}
                  </div>
                  {block.metrics.map((m, k) => {
                    const v = r.values[k];
                    return (
                      <div key={k} role="cell">
                        <span className="blog-metric-mlabel" style={{ display: "none", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: T.textMuted, fontFamily: T.sans, marginBottom: 3 }}>{m.label}</span>
                        {v === null || v === undefined ? (
                          <span style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans, fontStyle: "italic" as const }}>not reported</span>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, height: 10, background: T.borderLight, borderRadius: 5, overflow: "hidden", minWidth: 40 }}>
                              <div style={{ width: `${Math.min(100, (v / m.max) * 100)}%`, height: "100%", background: r.highlight ? T.primary : "#7FA9A3", borderRadius: 5 }} />
                            </div>
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: r.highlight ? T.primaryDark : T.text, fontFamily: T.sans, whiteSpace: "nowrap" as const, minWidth: 54, textAlign: "right" as const }}>{v}{m.unit}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </figure>
      );
    }

    case "footstep-path": {
      // Footsteps on a walkway. Each footprint is placed at its real date, so
      // the gap between 2 prints is the time that passed. Feet alternate left
      // and right. A shaded window marks dates known only to the year or quarter.
      const FP: Record<string, string> = { forward: T.primary, setback: "#B45309", wait: "#8A817C", next: T.primary };
      const toM = (d: string) => { const [y, m = "1", dd = "1"] = d.split("-"); return (+y) * 12 + (+m - 1) + (+dd - 1) / 30.5; };
      const m0 = toM(block.start), m1 = toM(block.end), span = m1 - m0;
      // An optional skipped stretch with no events is squeezed to a short break,
      // so the busy years get the room. BREAK is its share of the walkway.
      const sk = block.skip ? [toM(block.skip.from), toM(block.skip.to)] as const : null;
      const BREAK = sk ? 0.06 : 0;
      const live = span - (sk ? sk[1] - sk[0] : 0);
      const frac = (m: number) => {
        if (!sk) return (m - m0) / span;
        if (m <= sk[0]) return ((m - m0) / live) * (1 - BREAK);
        if (m >= sk[1]) return ((m - m0 - (sk[1] - sk[0])) / live) * (1 - BREAK) + BREAK;
        return ((sk[0] - m0) / live) * (1 - BREAK) + BREAK * ((m - sk[0]) / (sk[1] - sk[0]));
      };
      const firstYear = Math.ceil(m0 / 12), lastYear = Math.floor(m1 / 12);
      const years: number[] = []; for (let y = firstYear; y <= lastYear; y++) years.push(y);
      const shownYears = () => years.filter((y) => !sk || y * 12 < sk[0] || y * 12 > sk[1]);
      // One footprint, toes pointing up, centered on 0,0. side 1 = right foot, -1 = left foot.
      const foot = (tone: string, side: 1 | -1, key: string) => {
        const c = FP[tone];
        const hollow = tone === "next";
        const parts = [
          { t: "e", cx: 0, cy: 8, rx: 4.2, ry: 5 },
          { t: "e", cx: side * 2.3, cy: 0.5, rx: 2.3, ry: 5 },
          { t: "e", cx: side * 0.5, cy: -6.5, rx: 5, ry: 4.4 },
          { t: "c", cx: side * -3.4, cy: -13.4, r: 1.9 },
          { t: "c", cx: side * -1, cy: -14.4, r: 1.35 },
          { t: "c", cx: side * 1, cy: -14, r: 1.25 },
          { t: "c", cx: side * 2.8, cy: -13, r: 1.15 },
          { t: "c", cx: side * 4.3, cy: -11.6, r: 1.05 },
        ];
        const shape = (pt: (typeof parts)[number], k: string, grow: number, fill: string, op: number, stroke?: boolean) =>
          pt.t === "e"
            ? <ellipse key={k} cx={pt.cx} cy={pt.cy} rx={(pt.rx as number) + grow} ry={(pt.ry as number) + grow} fill={stroke ? "none" : fill} fillOpacity={op} stroke={stroke ? c : undefined} strokeWidth={stroke ? 1.2 : undefined} strokeDasharray={stroke ? "2 1.6" : undefined} />
            : <circle key={k} cx={pt.cx} cy={pt.cy} r={(pt.r as number) + grow} fill={stroke ? "none" : fill} fillOpacity={op} stroke={stroke ? c : undefined} strokeWidth={stroke ? 1.1 : undefined} strokeDasharray={stroke ? "2 1.6" : undefined} />;
        return hollow
          ? <g key={key}>{parts.map((pt, k) => shape(pt, `${key}h${k}`, 0, c, 1, true))}</g>
          : <g key={key}>{parts.map((pt, k) => shape(pt, `${key}a${k}`, 1.6, c, 0.22))}{parts.map((pt, k) => shape(pt, `${key}b${k}`, 0, c, 0.9))}</g>;
      };
      const pos = block.steps.map((s) => frac(toM(s.at)));
      const monthsBetween = (a: string, b: string) => Math.round(toM(b) - toM(a));
      const gapText = (j: number) => {
        if (j === 0) return null;
        const prev = block.steps[j - 1], cur = block.steps[j];
        if (prev.span || cur.span) return null;
        const n = monthsBetween(prev.at, cur.at);
        if (n < 1) return "Less than a month after the step before";
        return `About ${n} month${n === 1 ? "" : "s"} after the step before`;
      };
      // Wide walkway for larger screens: walking left to right.
      const W = 700, padX = 26, matTop = 34, matH = 96, laneA = matTop + 26, laneB = matTop + matH - 26;
      const xAt = (f: number) => padX + f * (W - padX * 2);
      const wide = (
        <svg className="blog-fp-wide" viewBox={`0 0 ${W} 184`} width="100%" role="img" aria-label={block.caption} style={{ display: "block" }}>
          <defs>
            <pattern id={`fpgrid${i}`} width="7" height="7" patternUnits="userSpaceOnUse"><path d="M7 0H0V7" fill="none" stroke="#E4DED2" strokeWidth="0.6" /></pattern>
            <pattern id={`fphatch${i}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="6" stroke="#CFC6B6" strokeWidth="2" /></pattern>
          </defs>
          <rect x={padX - 12} y={matTop} width={W - (padX - 12) * 2} height={matH} rx="8" fill="#F7F4EE" />
          <rect x={padX - 12} y={matTop} width={W - (padX - 12) * 2} height={matH} rx="8" fill={`url(#fpgrid${i})`} stroke="#DDD5C7" />
          {block.steps.map((s, j) => s.span ? <rect key={`sp${j}`} x={xAt(frac(toM(s.span[0])))} y={matTop + 1} width={Math.max(4, xAt(frac(toM(s.span[1]))) - xAt(frac(toM(s.span[0]))))} height={matH - 2} fill={`url(#fphatch${i})`} opacity="0.55" /> : null)}
          {sk && block.skip && (() => { const a = xAt(frac(sk[0])), b = xAt(frac(sk[1])); return <g><rect x={a} y={matTop + 1} width={b - a} height={matH - 2} fill="#FFFFFF" /><path d={`M${a} ${matTop} l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 M${b} ${matTop} l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6 l4 6 l-4 6`} fill="none" stroke="#C9BFAE" strokeWidth="1.2" /><text x={(a + b) / 2} y={matTop + matH / 2 + 4} textAnchor="middle" fontSize="10.5" fontFamily={T.sans} fill={T.textMuted} transform={`rotate(-90 ${(a + b) / 2} ${matTop + matH / 2})`}>{block.skip.label}</text></g>; })()}
          {shownYears().map((y) => { const x = xAt(frac(y * 12)); return <g key={y}><line x1={x} x2={x} y1={matTop + matH} y2={matTop + matH + 8} stroke={T.textMuted} strokeWidth="1" /><text x={x} y={matTop + matH + 22} textAnchor="middle" fontSize="12" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill={T.textSecondary}>{y}</text></g>; })}
          {block.steps.map((s, j) => {
            const x = xAt(pos[j]); const top = j % 2 === 0; const y = top ? laneA : laneB;
            return (
              <g key={`f${j}`}>
                <g transform={`translate(${x} ${y}) rotate(90) scale(1.35)`}>{foot(s.tone, top ? -1 : 1, `fw${j}`)}</g>
                <circle cx={x} cy={top ? matTop - 14 : matTop + matH + 40} r="9" fill={s.tone === "next" ? "#FFFFFF" : FP[s.tone]} stroke={FP[s.tone]} strokeWidth="1.5" />
                <text x={x} y={(top ? matTop - 14 : matTop + matH + 40) + 4} textAnchor="middle" fontSize="11" fontWeight="700" fontFamily={T.sans} fill={s.tone === "next" ? FP[s.tone] : "#FFFFFF"}>{j + 1}</text>
              </g>
            );
          })}
        </svg>
      );
      // Tall walkway for phones: walking top to bottom.
      const TW = 320, padY = 22, perMonth = 12, TH = padY * 2 + (live * perMonth) / (1 - BREAK), matL = 120, matW = 96;
      const yAt = (f: number) => padY + f * (TH - padY * 2);
      const tall = (
        <svg className="blog-fp-tall" viewBox={`0 0 ${TW} ${TH}`} width="100%" role="img" aria-label={block.caption} style={{ display: "none", maxWidth: 340, margin: "0 auto" }}>
          <defs>
            <pattern id={`fpgridt${i}`} width="7" height="7" patternUnits="userSpaceOnUse"><path d="M7 0H0V7" fill="none" stroke="#E4DED2" strokeWidth="0.6" /></pattern>
            <pattern id={`fphatcht${i}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="6" stroke="#CFC6B6" strokeWidth="2" /></pattern>
          </defs>
          <rect x={matL} y={padY - 12} width={matW} height={TH - (padY - 12) * 2} rx="8" fill="#F7F4EE" />
          <rect x={matL} y={padY - 12} width={matW} height={TH - (padY - 12) * 2} rx="8" fill={`url(#fpgridt${i})`} stroke="#DDD5C7" />
          {block.steps.map((s, j) => s.span ? <rect key={`tsp${j}`} x={matL + 1} y={yAt(frac(toM(s.span[0])))} width={matW - 2} height={Math.max(4, yAt(frac(toM(s.span[1]))) - yAt(frac(toM(s.span[0]))))} fill={`url(#fphatcht${i})`} opacity="0.55" /> : null)}
          {sk && block.skip && (() => { const a = yAt(frac(sk[0])), b = yAt(frac(sk[1])); return <g><rect x={matL + 1} y={a} width={matW - 2} height={b - a} fill="#FFFFFF" /><path d={`M${matL} ${a} l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 M${matL} ${b} l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4 l6 4 l6 -4`} fill="none" stroke="#C9BFAE" strokeWidth="1.2" /><text x={matL + matW / 2} y={(a + b) / 2 + 6} textAnchor="middle" fontSize="10.5" fontFamily={T.sans} fill={T.textMuted}>{block.skip.label}</text></g>; })()}
          {shownYears().map((y) => { const yy = yAt(frac(y * 12)); return <g key={`t${y}`}><line x1={52} x2={matL - 36} y1={yy} y2={yy} stroke={T.textMuted} strokeWidth="1" strokeDasharray="2 3" /><text x={8} y={yy + 4} textAnchor="start" fontSize="12" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fill={T.textSecondary}>{y}</text></g>; })}
          {block.steps.map((s, j) => {
            const y = yAt(pos[j]); const left = j % 2 === 0; const x = left ? matL + 30 : matL + matW - 30;
            const bx = left ? matL - 22 : matL + matW + 22;
            return (
              <g key={`tf${j}`}>
                <g transform={`translate(${x} ${y}) rotate(180) scale(1.15)`}>{foot(s.tone, left ? 1 : -1, `ft${j}`)}</g>
                <circle cx={bx} cy={y} r="9" fill={s.tone === "next" ? "#FFFFFF" : FP[s.tone]} stroke={FP[s.tone]} strokeWidth="1.5" />
                <text x={bx} y={y + 4} textAnchor="middle" fontSize="11" fontWeight="700" fontFamily={T.sans} fill={s.tone === "next" ? FP[s.tone] : "#FFFFFF"}>{j + 1}</text>
              </g>
            );
          })}
        </svg>
      );
      const legend: [string, string][] = [["forward", "A step forward"], ["setback", "A setback"], ["wait", "A delay or change of plan"], ["next", "Still ahead"]];
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          <div style={{ fontSize: 13.5, color: T.textSecondary, fontFamily: T.sans, marginBottom: 10, lineHeight: 1.5 }}>Each footprint is one step, placed on the date it happened. The farther apart 2 footprints are, the more time passed between them.</div>
          <div style={{ display: "flex", flexWrap: "wrap" as const, gap: "6px 16px", fontSize: 12, color: T.textSecondary, fontFamily: T.sans, marginBottom: 6 }}>
            {legend.map(([k, label]) => (
              <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <svg width="14" height="22" viewBox="-8 -18 16 30" aria-hidden="true">{foot(k, 1, `lg${k}`)}</svg>{label}
              </span>
            ))}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span aria-hidden="true" style={{ width: 16, height: 12, borderRadius: 2, background: "repeating-linear-gradient(45deg,#E2DACB 0 2px,#F7F4EE 2px 5px)", border: "1px solid #DDD5C7" }} />Only the year or quarter is known</span>
          </div>
          {wide}
          {tall}
          <ol style={{ listStyle: "none", margin: "14px 0 0", padding: 0 }}>
            {block.steps.map((s, j) => {
              const gap = gapText(j);
              return (
                <li key={j} className="blog-fp-row">
                  <span aria-hidden="true" className="blog-fp-num" style={{ background: s.tone === "next" ? "#FFFFFF" : FP[s.tone], color: s.tone === "next" ? FP[s.tone] : "#FFFFFF", border: `1.5px solid ${FP[s.tone]}` }}>{j + 1}</span>
                  <div className="blog-fp-when">{s.when}{gap && <span style={{ display: "block", fontSize: 11.5, color: T.textMuted, fontWeight: 400, marginTop: 2, fontFamily: T.sans }}>{gap}</span>}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif, lineHeight: 1.3 }}>{s.label}</div>
                    <div style={{ fontSize: 13.5, color: T.textSecondary, fontFamily: T.sans, marginTop: 3, lineHeight: 1.55 }}>{parseInlineLinks(s.detail)}</div>
                  </div>
                </li>
              );
            })}
          </ol>
          {block.note && <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.sans, marginTop: 12, lineHeight: 1.5 }}>{block.note}</div>}
          <p style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>{block.caption}</p>
        </figure>
      );
    }

    case "milestone-rail": {
      const DOT: Record<string, string> = { good: T.primary, bad: "#B91C1C", next: "#D97706", neutral: "#A8A29E" };
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 10px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 18, lineHeight: 1.5 }}>{block.note}</div>}
          <ol style={{ listStyle: "none", margin: 0, padding: 0, position: "relative" }}>
            {block.items.map((it, j) => {
              const c = DOT[it.tone || "neutral"];
              const last = j === block.items.length - 1;
              return (
                <li key={j} className="blog-rail-item" style={{ display: "grid", gridTemplateColumns: "112px 22px 1fr", gap: 10, paddingBottom: last ? 12 : 18, position: "relative" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: T.textSecondary, fontFamily: T.sans, textAlign: "right" as const, paddingTop: 1, lineHeight: 1.35 }}>{it.date}</div>
                  <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
                    {!last && <div aria-hidden="true" style={{ position: "absolute", top: 14, bottom: -18, width: 2, background: T.border }} />}
                    <div aria-hidden="true" style={{ width: 14, height: 14, borderRadius: "50%", background: it.tone === "next" ? T.surface : c, border: `3px solid ${c}`, marginTop: 2, position: "relative", zIndex: 1, boxSizing: "border-box" as const }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif, lineHeight: 1.3 }}>{it.label}</div>
                    <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, marginTop: 3, lineHeight: 1.55 }}>{parseInlineLinks(it.detail)}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </figure>
      );
    }

    case "site-pins": {
      // One pin per listed trial site. US sites are filled and come first,
      // the rest are hollow, so a 40-of-117 trial and a 1-of-24 trial look
      // different before anyone reads the numbers.
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 14px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 10, lineHeight: 1.5 }}>{block.note}</div>}
          <div aria-hidden="true" style={{ display: "flex", gap: 16, fontSize: 12, color: T.textSecondary, fontFamily: T.sans, marginBottom: 4 }}>
            <span><span className="blog-pin blog-pin-us" style={{ marginRight: 6, verticalAlign: "-1px" }} />US site</span>
            <span><span className="blog-pin" style={{ marginRight: 6, verticalAlign: "-1px" }} />Site outside the US</span>
          </div>
          {block.rows.map((r, j) => (
            <div key={j} style={{ padding: "16px 0 12px", borderTop: j === 0 ? "none" : `1px solid ${T.borderLight}` }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" as const }}>
                <a href={`https://clinicaltrials.gov/study/${r.nct}`} target="_blank" rel="noopener" style={{ fontSize: 15.5, fontWeight: 700, color: T.text, fontFamily: T.serif, textDecoration: "none" }}>{r.label}</a>
                <span style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans }}>{r.drug} · {r.how} · {r.phase} · about {r.people.toLocaleString("en-US")} people</span>
                <span style={{ marginLeft: "auto", fontFamily: T.sans, whiteSpace: "nowrap" as const }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: T.primary }}>{r.us}</span>
                  <span style={{ fontSize: 12, color: T.textSecondary, fontWeight: 600 }}> of {r.total} sites in the US</span>
                </span>
              </div>
              <div role="img" aria-label={`${r.label}: ${r.us} of ${r.total} listed sites are in the US`} style={{ display: "flex", flexWrap: "wrap" as const, gap: 3, margin: "10px 0 8px" }}>
                {Array.from({ length: r.total }, (_, k) => <span key={k} className={k < r.us ? "blog-pin blog-pin-us" : "blog-pin"} />)}
              </div>
              <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.5 }}>{parseInlineLinks(r.detail)} <span style={{ color: T.textMuted }}>{r.nct}</span></div>
            </div>
          ))}
          <p style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>{block.caption}</p>
        </figure>
      );
    }

    case "dose-stairs": {
      // Each lane is a staircase built from week columns. Bars sit flush so
      // the steps read as one climb; the dose prints on every step that
      // changes it. A dashed gate marks a step the label will not let anyone
      // climb past without a test.
      const COLS = block.weeks + 1;
      const H = 64;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          {block.rows.map((r, j) => {
            const doseAt = (w: number) => { let d = 0; for (const st of r.steps) if (st.week <= w) d = st.dose; return d; };
            return (
              <div key={j} style={{ padding: "14px 0 6px", borderTop: j === 0 ? "none" : `1px solid ${T.borderLight}` }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" as const, marginBottom: 6 }}>
                  {r.href ? <Link href={r.href} style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif, textDecoration: "none" }}>{r.label}</Link> : <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif }}>{r.label}</span>}
                  {r.sub && <span style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans }}>{r.sub}</span>}
                  <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, fontFamily: T.sans, padding: "2px 8px", borderRadius: 999, background: T.primaryLight, color: T.primaryDark }}>{r.perDay}</span>
                </div>
                <div role="img" aria-label={`${r.label}: ${r.steps.map((st) => `week ${st.week}, ${st.dose} ${r.unit}`).join("; ")}`} style={{ display: "grid", gridTemplateColumns: `repeat(${COLS}, 1fr)`, alignItems: "end", height: H + 18, position: "relative" }}>
                  {Array.from({ length: COLS }, (_, w) => {
                    const d = doseAt(w);
                    const changed = r.steps.some((st) => st.week === w);
                    const atGate = r.gate && r.gate.week === w;
                    const h = Math.max(4, Math.round((d / r.max) * H));
                    return (
                      <div key={w} style={{ position: "relative", height: H + 18, display: "flex", flexDirection: "column", justifyContent: "flex-end", borderLeft: atGate ? "2px dashed #B42318" : "none" }}>
                        {changed && <span style={{ fontSize: 10.5, fontWeight: 800, color: T.primaryDark, fontFamily: T.sans, textAlign: "center", lineHeight: 1.1, marginBottom: 2, whiteSpace: "nowrap" as const }}>{d}</span>}
                        <div style={{ height: h, background: w === COLS - 1 || d === r.max ? T.primary : "#5FA89D", borderTop: `2px solid ${T.primaryDark}` }} />
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" as const, marginTop: 6, fontSize: 12, fontFamily: T.sans, color: T.textSecondary, lineHeight: 1.45 }}>
                  <span>{parseInlineLinks(r.end)}</span>
                  {r.gate && <span style={{ color: "#B42318", fontWeight: 700 }}>Dashed line: {r.gate.label}</span>}
                </div>
              </div>
            );
          })}
          <div aria-hidden="true" style={{ display: "grid", gridTemplateColumns: `repeat(${COLS}, 1fr)`, marginTop: 8, fontSize: 10.5, color: T.textMuted, fontFamily: T.sans, textAlign: "center" }}>
            {Array.from({ length: COLS }, (_, w) => <span key={w}>{w === 0 ? "Start" : `Wk ${w}`}</span>)}
          </div>
          <p style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>{block.caption}</p>
        </figure>
      );
    }

    case "stance-switch": {
      // Two rails run down the page: left when the regulator accepts the
      // company's plan, right when it asks for more. Company milestones sit
      // on the center line. The track jumps rails at every reversal, so a
      // back-and-forth story shows up as a zigzag before a word is read.
      const X: Record<string, number> = { left: 14, center: 36, right: 58 };
      const COLOR: Record<string, string> = { left: "#0E5F56", right: "#B42318", center: "#78716C" };
      const fmt = (iso: string) => new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 12, lineHeight: 1.5 }}>{block.note}</div>}
          <div aria-hidden="true" style={{ display: "flex", flexWrap: "wrap" as const, gap: "6px 16px", fontSize: 12, fontFamily: T.sans, color: T.textSecondary, marginBottom: 14 }}>
            <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 999, background: COLOR.left, marginRight: 6 }} />Left rail: {block.leftLabel}</span>
            <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 999, background: COLOR.right, marginRight: 6 }} />Right rail: {block.rightLabel}</span>
            <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 999, background: COLOR.center, marginRight: 6 }} />Center: {block.centerLabel}</span>
          </div>
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {block.items.map((it, k) => {
              const prev = k === 0 ? it.side : block.items[k - 1].side;
              const x0 = X[prev], x1 = X[it.side];
              const c = it.next ? "#D97706" : COLOR[it.side];
              return (
                <li key={k} style={{ display: "grid", gridTemplateColumns: "72px 1fr", gap: 12 }}>
                  <div style={{ position: "relative", display: "flex", flexDirection: "column" }} aria-hidden="true">
                    <svg width="72" height="22" style={{ display: "block", flex: "none" }}>
                      <line x1="14" x2="14" y1="0" y2="22" stroke="#E7E5E4" strokeWidth="2" />
                      <line x1="58" x2="58" y1="0" y2="22" stroke="#E7E5E4" strokeWidth="2" />
                      {k > 0 && <line x1={x0} y1="0" x2={x1} y2="22" stroke={it.next ? "#D97706" : "#44403C"} strokeWidth="2.5" strokeDasharray={it.next ? "4 3" : undefined} />}
                    </svg>
                    <svg width="72" height="16" style={{ display: "block", flex: "none" }}>
                      <line x1="14" x2="14" y1="0" y2="16" stroke="#E7E5E4" strokeWidth="2" />
                      <line x1="58" x2="58" y1="0" y2="16" stroke="#E7E5E4" strokeWidth="2" />
                      <circle cx={x1} cy="8" r="7" fill={it.next ? "#FFFFFF" : c} stroke={c} strokeWidth="2.5" strokeDasharray={it.next ? "3 2" : undefined} />
                    </svg>
                    <div style={{ flex: 1, position: "relative", minHeight: 8 }}>
                      <div style={{ position: "absolute", top: 0, bottom: 0, left: 13, width: 2, background: "#E7E5E4" }} />
                      <div style={{ position: "absolute", top: 0, bottom: 0, left: 57, width: 2, background: "#E7E5E4" }} />
                      {k < block.items.length - 1 && <div style={{ position: "absolute", top: 0, bottom: 0, left: x1 - 1.25, width: 2.5, background: block.items[k + 1].next ? "transparent" : "#44403C" }} />}
                    </div>
                  </div>
                  <div style={{ padding: "20px 0 14px" }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase" as const, color: c, fontFamily: T.sans }}>{it.next ? "Expected " : ""}{fmt(it.date)}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif, margin: "2px 0 3px" }}>{it.label}</div>
                    <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.5 }}>{parseInlineLinks(it.detail)}</div>
                  </div>
                </li>
              );
            })}
          </ol>
          <p style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>{block.caption}</p>
        </figure>
      );
    }

    case "dose-burden": {
      // One 6px square per dose, same scale on every row, so the reader
      // sees a year of pills as a block and a year of quarterly shots as
      // 4 dots. The number is printed too; the squares are the argument.
      const KIND: Record<string, { fill: string; bg: string; fg: string; word: string }> = {
        pill: { fill: "#0E5F56", bg: "#EEF8F5", fg: "#0B5A3E", word: "By mouth at home" },
        shot: { fill: "#D97706", bg: "#FEF3C7", fg: "#92400E", word: "Injection" },
        infusion: { fill: "#B91C1C", bg: "#FEF2F2", fg: "#991B1B", word: "IV infusion" },
      };
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 12px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="list">
            {block.rows.map((r, j) => {
              const k = KIND[r.kind];
              const squares = Math.max(0, Math.round(r.count));
              return (
                <div key={j} role="listitem" style={{ padding: "14px 0", borderTop: j === 0 ? "none" : `1px solid ${T.borderLight}` }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" as const, marginBottom: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif }}>{r.label}</span>
                    {r.sub && <span style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans }}>{r.sub}</span>}
                    <span style={{ marginLeft: "auto", fontSize: 22, fontWeight: 800, color: k.fill, fontFamily: T.sans, lineHeight: 1 }}>
                      {r.count.toLocaleString("en-US")} <span style={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, letterSpacing: "0.04em", textTransform: "uppercase" as const }}>{r.unit}</span>
                    </span>
                  </div>
                  <div role="img" aria-label={`${r.count.toLocaleString("en-US")} ${r.unit} for ${r.label}`} className="blog-dose-grid" style={{ ["--dose" as string]: k.fill }}>
                    {Array.from({ length: squares }, (_, s) => <i key={s} className="blog-dose-sq" />)}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" as const }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, fontFamily: T.sans, padding: "2px 7px", borderRadius: 4, background: k.bg, color: k.fg, whiteSpace: "nowrap" as const }}>{r.setting || k.word}</span>
                    <span style={{ fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.5 }}>{parseInlineLinks(r.detail)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </figure>
      );
    }

    case "tape-timeline": {
      // A tape measure pulled down the page: one tick per month, so a
      // 4-year wait takes 4 years of tape. Events hang off the tape on
      // label-maker strips, fanned out so crowded months never collide.
      const mIdx = (iso: string) => { const [y, m, d] = iso.split("-").map(Number); return (y * 12 + (m - 1)) + ((d || 1) - 1) / 30; };
      const [sy, sm] = block.startMonth.split("-").map(Number);
      const m0 = sy * 12 + (sm - 1);
      const PX = 10.5, TOP = 64;
      const ty = (iso: string) => TOP + (mIdx(iso) - m0) * PX;
      const evs = block.items.map((it, k) => ({ ...it, k, y: ty(it.date) }));
      const fan = (gap: number) => { let last = -Infinity; return evs.map((e) => { const ly = Math.max(e.y - 16, last + gap); last = ly; return ly; }); };
      const lyD = fan(74), lyM = fan(112);
      const endD = Math.max(evs[evs.length - 1].y, lyD[lyD.length - 1] + 70) + 40;
      const endM = Math.max(evs[evs.length - 1].y, lyM[lyM.length - 1] + 104) + 40;
      const months = Math.ceil((Math.max(endD, endM) - TOP) / PX);
      const fmt = (iso: string) => new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
      const TX = 22, TW = 50, LX = 120;
      // Ticks stop 2 months past the last event; the rest of the tape is
      // blank so no future years are implied.
      const lastTick = evs[evs.length - 1].y + 2 * PX;
      const tape = (end: number, sfx: string) => (
        <svg aria-hidden="true" width={LX} height={end} style={{ position: "absolute", left: 0, top: 0 }}>
          <defs>
            <linearGradient id={`tape${i}${sfx}`} x1="0" x2="1"><stop offset="0" stopColor="#F2B705" /><stop offset="0.5" stopColor="#FFD23F" /><stop offset="1" stopColor="#E9A800" /></linearGradient>
          </defs>
          <rect x={TX} y={TOP - 4} width={TW} height={end - TOP - 6} fill={`url(#tape${i}${sfx})`} />
          {Array.from({ length: months + 1 }, (_, n) => {
            const yy = TOP + n * PX; if (yy > end - 12 || yy > lastTick) return null;
            const abs = m0 + n, isJan = abs % 12 === 0, isHalf = abs % 6 === 0;
            return (
              <g key={n}>
                <line x1={TX} x2={TX + (isJan ? 30 : isHalf ? 18 : 9)} y1={yy} y2={yy} stroke="#1C1917" strokeWidth={isJan ? 2 : 1} />
                {isJan && <text x={TX + 33} y={yy + 4} fontSize="10.5" fontWeight="800" fill="#B42318" fontFamily="Inter, sans-serif">{Math.floor(abs / 12)}</text>}
              </g>
            );
          })}
          <rect x={TX - 14} y={6} width={TW + 28} height={TOP - 12} rx="12" fill="#2F2A26" />
          <rect x={TX - 8} y={12} width={TW + 16} height={TOP - 24} rx="8" fill="#4A433D" />
          <circle cx={TX + TW / 2} cy={TOP / 2} r="9" fill="#2F2A26" stroke="#7A716A" strokeWidth="2" />
          <path d={`M${TX - 4} ${end - 12} h${TW + 8} v8 h-${TW + 8} z`} fill="#A8A29E" />
          {evs.map((e) => <circle key={e.k} cx={TX + TW} cy={e.y} r="4.5" fill={e.tone === "next" ? "#fff" : "#1C1917"} stroke="#1C1917" strokeWidth="2" />)}
        </svg>
      );
      const leaders = (ly: number[], end: number, cls: string) => (
        <svg aria-hidden="true" className={cls} width={LX - TX - TW + 8} height={end} style={{ position: "absolute", left: TX + TW, top: 0 }}>
          {evs.map((e, n) => <path key={e.k} d={`M0 ${e.y} C 26 ${e.y}, 22 ${ly[n] + 13}, ${LX - TX - TW + 4} ${ly[n] + 13}`} stroke="#1C1917" strokeWidth="1.2" fill="none" strokeDasharray={e.tone === "next" ? "3 3" : undefined} />)}
        </svg>
      );
      // Put the gap stamp in the widest empty stretch of the gap, clear of cards.
      const g = (() => {
        if (!block.gap) return null;
        const y1 = ty(block.gap.from), y2 = ty(block.gap.to);
        const edges = [y1 - 16, ...lyD.filter((ly) => ly > y1 - 16 && ly < y2).map((ly) => ly), y2 - 16].sort((a, b) => a - b);
        let best = { a: y1, b: y2, w: -1 };
        for (let n = 0; n < edges.length - 1; n++) { const a = edges[n] + 62, b = edges[n + 1] - 8; if (b - a > best.w) best = { a, b, w: b - a }; }
        return { top: best.a, h: Math.max(40, best.w) };
      })();
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: "#FBF8F1", border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 10, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="img" aria-label={block.caption} className="blog-tape" style={{ position: "relative", height: endD, ["--hm" as string]: `${endM}px` }}>
            <div className="blog-tape-d">{tape(endD, "d")}{leaders(lyD, endD, "")}</div>
            <div className="blog-tape-m">{tape(endM, "m")}{leaders(lyM, endM, "")}</div>
            {g && block.gap && (
              <div aria-hidden="true" className="blog-tape-gap" style={{ position: "absolute", left: LX + 10, top: g.top, height: g.h, display: "flex", alignItems: "center" }}>
                <span className="blog-stamp blog-stamp-red" style={{ fontSize: 15, padding: "6px 12px", transform: "rotate(-7deg)" }}>{block.gap.text}</span>
              </div>
            )}
            {evs.map((e, n) => (
              <div key={e.k} className="blog-tape-item" style={{ position: "absolute", left: LX, right: 0, top: lyD[n], ["--lym" as string]: `${lyM[n]}px` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" as const }}>
                  <span className={`blog-dymo${e.tone === "next" ? " blog-dymo-next" : ""}`} style={{ transform: `rotate(${n % 2 ? 0.8 : -0.8}deg)` }}>{e.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: e.tone === "next" ? "#B45309" : T.textSecondary, fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace" }}>{fmt(e.date)}</span>
                  {e.tone === "next" && <span className="blog-stamp blog-stamp-amber" style={{ fontSize: 9.5, padding: "2px 6px" }}>Target date</span>}
                </div>
                <div style={{ fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, marginTop: 5, lineHeight: 1.4 }}>{parseInlineLinks(e.detail)}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans, lineHeight: 1.5, margin: "10px 0 0" }}>{block.caption}</p>
        </figure>
      );
    }

    case "question-card": {
      // A clinic notepad: lined paper, a red margin, empty boxes to tick in
      // the appointment. The print button prints only this card.
      const id = `qcard-${i}`;
      return (
        <figure key={i} id={id} className="blog-qcard" style={{ margin: "32px 0" }}>
          <div className="blog-qcard-holes" aria-hidden="true">{Array.from({ length: 9 }, (_, n) => <i key={n} />)}</div>
          <div className="blog-qcard-paper">
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" as const, marginBottom: 6 }}>
              <figcaption style={{ fontSize: 18, fontWeight: 700, color: "#1E3A5F", fontFamily: T.serif, lineHeight: 1.3, maxWidth: 440 }}>{block.title}</figcaption>
              {block.stamp && <span className="blog-stamp blog-stamp-red" style={{ fontSize: 11, transform: "rotate(5deg)" }}>{block.stamp}</span>}
            </div>
            <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {block.items.map((it, n) => (
                <li key={n} style={{ display: "grid", gridTemplateColumns: "26px 1fr", gap: 10, padding: "9px 0 10px" }}>
                  <span aria-hidden="true" style={{ width: 17, height: 17, border: "2px solid #1E3A5F", borderRadius: 3, marginTop: 3, background: "#fff" }} />
                  <div>
                    <div style={{ fontSize: 15.5, fontWeight: 700, color: "#1E3A5F", fontFamily: T.serif, lineHeight: 1.45 }}>{it.q}</div>
                    <div style={{ fontSize: 13.5, color: "#44403C", fontFamily: T.sans, lineHeight: 1.6 }}>{parseInlineLinks(it.note)}</div>
                  </div>
                </li>
              ))}
            </ol>
            <button type="button" className="blog-qcard-print" onClick={() => { document.documentElement.setAttribute("data-print", id); window.print(); setTimeout(() => document.documentElement.removeAttribute("data-print"), 500); }}>Print this list</button>
          </div>
        </figure>
      );
    }

    case "growth-ruler": {
      // A pencil mark on a door frame, the way parents measure children.
      // The ruler is drawn to scale from 0, so 1.5 and 1.74cm look as close
      // as they are; labels fan out on leader lines instead of overlapping.
      const H = 440, PAD = 30, RW = 96, LW = 64;
      const y = (v: number) => PAD + ((block.max - v) / block.max) * (H - 2 * PAD);
      const sorted = [...block.marks].map((m, k) => ({ ...m, k, my: y(m.value) })).sort((a, b) => a.value === b.value ? a.k - b.k : b.value - a.value);
      const fan = (gap: number) => { let last = -Infinity; return sorted.map((m) => { const ly = Math.max(m.my - 14, last + gap); last = ly; return { ...m, ly }; }); };
      const placed = fan(82);
      const placedM = fan(132);
      const HM = Math.max(H, placedM[placedM.length - 1].ly + 130);
      const nearMark = (t: number) => block.marks.some((m) => Math.abs(m.value - t) < block.max * 0.04);
      const ticks: number[] = [];
      for (let t = 0; t <= block.max + 1e-9; t += block.step) ticks.push(Math.round(t * 100) / 100);
      const minor: number[] = [];
      for (let t = 0; t <= block.max + 1e-9; t += block.step / 5) minor.push(Math.round(t * 1000) / 1000);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 12, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="img" aria-label={block.caption} className="blog-ruler" style={{ position: "relative", height: H, marginTop: 6, ["--hm" as string]: `${HM}px` }}>
            <svg aria-hidden="true" width={RW} height={H} style={{ position: "absolute", left: 0, top: 0 }}>
              <defs>
                <linearGradient id={`wood${i}`} x1="0" x2="1">
                  <stop offset="0" stopColor="#E7D3AE" /><stop offset="0.55" stopColor="#F1E2C4" /><stop offset="1" stopColor="#DCC49A" />
                </linearGradient>
              </defs>
              <rect x="6" y="4" width="70" height={H - 8} rx="4" fill={`url(#wood${i})`} stroke="#CDB385" />
              {[18, 41, 60].map((gx, g) => <path key={g} d={`M${gx} 8 C ${gx + 4} ${H * 0.3}, ${gx - 4} ${H * 0.6}, ${gx + 2} ${H - 8}`} stroke="#D6BE92" strokeWidth="1" fill="none" opacity="0.7" />)}
              {minor.map((t, k) => <line key={`m${k}`} x1={76 - 7} x2="76" y1={y(t)} y2={y(t)} stroke="#8A7350" strokeWidth="1" />)}
              {ticks.map((t, k) => (
                <g key={`t${k}`}>
                  <line x1={76 - 16} x2="76" y1={y(t)} y2={y(t)} stroke="#5C4A2E" strokeWidth="1.6" />
                  {!nearMark(t) && <text x="12" y={y(t) + 4} fontSize="11" fontWeight="700" fill="#5C4A2E" fontFamily="Inter, sans-serif">{t} {block.unit}</text>}
                </g>
              ))}
              {block.marks.map((m, k) => <line key={`p${k}`} x1="2" x2={RW} y1={y(m.value)} y2={y(m.value)} stroke="#2F2A26" strokeWidth="2.4" strokeLinecap="round" opacity="0.85" />)}
            </svg>
            {[["blog-ruler-d", placed, H], ["blog-ruler-m", placedM, HM]].map(([cls, pl, hh]) => (
              <svg key={cls as string} aria-hidden="true" className={cls as string} width={LW} height={hh as number} style={{ position: "absolute", left: RW, top: 0 }}>
                {(pl as typeof placed).map((m) => <path key={m.k} d={`M0 ${m.my} C ${LW * 0.5} ${m.my}, ${LW * 0.5} ${m.ly + 12}, ${LW} ${m.ly + 12}`} stroke="#2F2A26" strokeWidth="1.3" fill="none" strokeDasharray="3 3" />)}
              </svg>
            ))}
            {placed.map((m, idx) => (
              <div key={m.k} className="blog-ruler-label" style={{ position: "absolute", left: RW + LW + 6, right: 0, top: m.ly, ["--lym" as string]: `${placedM[idx].ly}px` }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" as const }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: T.sans, lineHeight: 1 }}>{m.display}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif }}>{m.href ? <Link href={m.href} style={{ color: T.text }}>{m.label}</Link> : m.label}</span>
                </div>
                {m.sub && <div style={{ fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, marginTop: 3, lineHeight: 1.4 }}>{m.sub}</div>}
                {m.stamp && <span className={`blog-stamp blog-stamp-${m.stamp.tone || "teal"}`} style={{ marginTop: 6, fontSize: 10.5 }}>{m.stamp.text}</span>}
              </div>
            ))}
            {block.stamp && (
              <span aria-hidden="true" className={`blog-stamp blog-stamp-big blog-stamp-${block.stamp.tone || "red"}`}>{block.stamp.text}</span>
            )}
          </div>
          {block.stamp && <div aria-hidden="true" className="blog-ruler-stamp-m"><span className={`blog-stamp blog-stamp-${block.stamp.tone || "red"}`} style={{ fontSize: 14, padding: "6px 12px", transform: "rotate(-5deg)" }}>{block.stamp.text}</span></div>}
          <p style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans, lineHeight: 1.5, margin: "10px 0 0" }}>{block.caption}</p>
        </figure>
      );
    }

    case "meal-window": {
      // A day around one calcium-rich meal. Each vial is filled to the share
      // of a normal dose that reached the blood when the dose was taken at
      // that hour; the tinted band is the stretch the label says to avoid.
      const W = 720, H = 300, L = 36, R = 36, AX = 236;
      const x = (h: number) => L + ((h - block.minHour) / (block.maxHour - block.minHour)) * (W - L - R);
      const VH = 120, VW = 46, VTOP = AX - 28 - VH;
      const hours: number[] = [];
      for (let h = Math.ceil(block.minHour); h <= block.maxHour; h++) hours.push(h);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 8, lineHeight: 1.5 }}>{block.note}</div>}
          <style>{`.blog-mw-phone{display:none}@media (max-width:560px){.blog-mw-wide{display:none!important}.blog-mw-phone{display:block!important}}`}</style>
          {(() => {
            // Phone layout: a narrower canvas with labels stacked above each
            // vial, so the text stays readable at 360px wide.
            const PW = 360, PH = 318, PL = 18, PR = 18, PAX = 250;
            const px = (h: number) => PL + ((h - block.minHour) / (block.maxHour - block.minHour)) * (PW - PL - PR);
            const PVH = 104, PVW = 34, PVTOP = PAX - 18 - PVH;
            return (
              <svg className="blog-mw-phone" role="img" aria-label={block.caption} viewBox={`0 0 ${PW} ${PH}`} style={{ width: "100%", height: "auto" }}>
                <rect x={px(block.band.from)} y={12} width={px(block.band.to) - px(block.band.from)} height={PAX - 12} fill="#FDF0E7" />
                <text x={(px(block.band.from) + px(block.band.to)) / 2} y={30} textAnchor="middle" fontSize="13" fontWeight="700" fill={VIZ_HOT} fontFamily="Inter, sans-serif">{block.band.label}</text>
                <line x1={px(block.band.from)} x2={px(block.band.from)} y1={12} y2={PAX} stroke={VIZ_HOT} strokeDasharray="4 4" />
                <line x1={px(block.band.to)} x2={px(block.band.to)} y1={12} y2={PAX} stroke={VIZ_HOT} strokeDasharray="4 4" />
                <line x1={PL} x2={PW - PR} y1={PAX} y2={PAX} stroke="#2F2A26" strokeWidth="1.6" />
                {hours.filter((h) => h % 2 === 0 || h === 0).map((h) => (
                  <g key={h}>
                    <line x1={px(h)} x2={px(h)} y1={PAX} y2={PAX + 5} stroke="#2F2A26" />
                    <text x={px(h)} y={PAX + 20} textAnchor="middle" fontSize="13" fill={T.textSecondary} fontFamily="Inter, sans-serif">{h === 0 ? "meal" : `${h > 0 ? "+" : ""}${h} h`}</text>
                  </g>
                ))}
                <g transform={`translate(${px(0)}, ${PAX + 46})`}>
                  <ellipse cx="0" cy="0" rx="26" ry="9" fill="#F3EEE6" stroke="#B8AFA3" />
                  <ellipse cx="0" cy="-2" rx="15" ry="4" fill="#FFFFFF" stroke="#D8D0C4" />
                  <text x="34" y="5" fontSize="13" fontWeight="600" fill={T.text} fontFamily="Inter, sans-serif">{block.mealLabel}</text>
                </g>
                {block.points.map((p, k) => {
                  const cx = px(p.hour), fillH = (Math.max(0, Math.min(100, p.absorbed)) / 100) * PVH;
                  const low = p.absorbed < 50;
                  return (
                    <g key={k}>
                      <text x={cx} y={PVTOP - 46} textAnchor="middle" fontSize="12.5" fontWeight="700" fill={T.text} fontFamily="Inter, sans-serif">{p.label}</text>
                      {p.sub && <text x={cx} y={PVTOP - 31} textAnchor="middle" fontSize="12" fill={T.textSecondary} fontFamily="Inter, sans-serif">{p.sub}</text>}
                      <text x={cx} y={PVTOP - 8} textAnchor="middle" fontSize="19" fontWeight="800" fill={low ? VIZ_HOT : T.primary} fontFamily="Inter, sans-serif">{p.absorbed}%</text>
                      <rect x={cx - PVW / 2} y={PVTOP} width={PVW} height={PVH} rx="8" fill="#FFFFFF" stroke="#2F2A26" strokeWidth="1.5" />
                      <rect x={cx - PVW / 2 + 3} y={PVTOP + PVH - fillH + 3} width={PVW - 6} height={Math.max(0, fillH - 6)} rx="5" fill={low ? VIZ_HOT : T.primary} />
                      <line x1={cx} x2={cx} y1={PVTOP + PVH + 4} y2={PAX} stroke="#2F2A26" strokeDasharray="2 3" />
                    </g>
                  );
                })}
              </svg>
            );
          })()}
          <svg className="blog-mw-wide" role="img" aria-label={block.caption} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
            <rect x={x(block.band.from)} y={16} width={x(block.band.to) - x(block.band.from)} height={AX - 16} fill="#FDF0E7" />
            <text x={(x(block.band.from) + x(block.band.to)) / 2} y={32} textAnchor="middle" fontSize="12.5" fontWeight="700" fill={VIZ_HOT} fontFamily="Inter, sans-serif">{block.band.label}</text>
            <line x1={x(block.band.from)} x2={x(block.band.from)} y1={16} y2={AX} stroke={VIZ_HOT} strokeDasharray="4 4" />
            <line x1={x(block.band.to)} x2={x(block.band.to)} y1={16} y2={AX} stroke={VIZ_HOT} strokeDasharray="4 4" />
            <line x1={L} x2={W - R} y1={AX} y2={AX} stroke="#2F2A26" strokeWidth="1.6" />
            {hours.map((h) => (
              <g key={h}>
                <line x1={x(h)} x2={x(h)} y1={AX} y2={AX + 6} stroke="#2F2A26" />
                <text x={x(h)} y={AX + 22} textAnchor="middle" fontSize="12" fill={T.textSecondary} fontFamily="Inter, sans-serif">{h === 0 ? "meal" : `${h > 0 ? "+" : ""}${h} h`}</text>
              </g>
            ))}
            <g transform={`translate(${x(0)}, ${AX + 46})`}>
              <ellipse cx="0" cy="0" rx="34" ry="11" fill="#F3EEE6" stroke="#B8AFA3" />
              <ellipse cx="0" cy="-2" rx="20" ry="5" fill="#FFFFFF" stroke="#D8D0C4" />
              <text x="44" y="5" fontSize="12.5" fontWeight="600" fill={T.text} fontFamily="Inter, sans-serif">{block.mealLabel}</text>
            </g>
            {block.points.map((p, k) => {
              const cx = x(p.hour), fillH = (Math.max(0, Math.min(100, p.absorbed)) / 100) * VH;
              const low = p.absorbed < 50;
              return (
                <g key={k}>
                  <line x1={cx} x2={cx} y1={VTOP + VH + 6} y2={AX} stroke="#2F2A26" strokeDasharray="2 3" />
                  <rect x={cx - VW / 2} y={VTOP} width={VW} height={VH} rx="9" fill="#FFFFFF" stroke="#2F2A26" strokeWidth="1.6" />
                  <rect x={cx - VW / 2 + 3} y={VTOP + VH - fillH + 3} width={VW - 6} height={Math.max(0, fillH - 6)} rx="6" fill={low ? VIZ_HOT : T.primary} />
                  <text x={cx} y={VTOP - 10} textAnchor="middle" fontSize="20" fontWeight="800" fill={low ? VIZ_HOT : T.primary} fontFamily="Inter, sans-serif">{p.absorbed}%</text>
                  <text x={cx + VW / 2 + 8} y={VTOP + 18} fontSize="12.5" fontWeight="700" fill={T.text} fontFamily="Inter, sans-serif">{p.label}</text>
                  {p.sub && <text x={cx + VW / 2 + 8} y={VTOP + 34} fontSize="11.5" fill={T.textSecondary} fontFamily="Inter, sans-serif">{p.sub}</text>}
                </g>
              );
            })}
          </svg>
          <p style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans, lineHeight: 1.5, margin: "10px 0 0" }}>{block.caption}</p>
        </figure>
      );
    }

    case "waffle": {
      // 100 squares, one per 1% of the whole. The big number carries the
      // exact count; the grid carries the proportion at a glance.
      const pct = block.total > 0 ? (block.value / block.total) * 100 : 0;
      const filled = Math.round(pct);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-waffle-wrap">
            <div>
              <div style={{ fontSize: 56, fontWeight: 800, color: VIZ_HOT, fontFamily: T.serif, lineHeight: 1, letterSpacing: "-0.02em" }}>{Math.round(pct)}%</div>
              <div style={{ fontSize: 14, color: T.text, fontFamily: T.sans, marginTop: 8, lineHeight: 1.45 }}>
                <strong>{block.value.toLocaleString("en-US")}</strong> of {block.total.toLocaleString("en-US")}
              </div>
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 6, marginTop: 14, fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans }}>
                <span style={{ display: "flex", gap: 7, alignItems: "center" }}><i aria-hidden="true" style={{ width: 11, height: 11, borderRadius: 2, background: VIZ_HOT, display: "inline-block", flexShrink: 0 }} />{block.valueLabel}</span>
                <span style={{ display: "flex", gap: 7, alignItems: "center" }}><i aria-hidden="true" style={{ width: 11, height: 11, borderRadius: 2, background: VIZ_COOL, display: "inline-block", flexShrink: 0 }} />{block.restLabel}</span>
              </div>
            </div>
            <div className="blog-waffle" role="img" aria-label={`${block.value.toLocaleString("en-US")} of ${block.total.toLocaleString("en-US")} (${Math.round(pct)}%) ${block.valueLabel.toLowerCase()}`}>
              {Array.from({ length: 100 }, (_, c) => (
                <i key={c} style={{ background: c < filled ? VIZ_HOT : VIZ_COOL }} />
              ))}
            </div>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "16px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "dot-plot": {
      // Position is the share; dot area is the sample size, so a 52% from
      // 23 trials visibly weighs less than a 47% from 216.
      const maxSize = Math.max(1, ...block.rows.map((r) => r.size));
      const minSize = Math.min(...block.rows.map((r) => r.size));
      const dia = (n: number) => 7 + 21 * Math.sqrt(n / maxSize);
      const ticks = [0, 25, 50, 75].filter((t) => t <= block.max);
      const unit = block.unit ?? "%";
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="list">
            {block.rows.map((r, j) => {
              const left = Math.min(100, (r.value / block.max) * 100);
              const d = dia(r.size);
              const label = r.href ? <Link href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</Link> : r.label;
              return (
                <div key={j} role="listitem" className="blog-dot-row">
                  <div className="blog-dot-label">{label}</div>
                  <div className="blog-dot-track" aria-label={`${r.label}: ${r.value}${unit} of ${r.size.toLocaleString("en-US")} ${block.sizeLabel}`}>
                    {ticks.map((t) => <span key={t} aria-hidden="true" style={{ position: "absolute", left: `${(t / block.max) * 100}%`, top: 0, bottom: 0, width: 1, background: T.borderLight }} />)}
                    <span aria-hidden="true" style={{ position: "absolute", left: 0, width: `${left}%`, top: "50%", height: 2, marginTop: -1, background: VIZ_HOT, opacity: 0.28 }} />
                    <span aria-hidden="true" style={{ position: "absolute", left: `${left}%`, top: "50%", width: d, height: d, marginLeft: -d / 2, marginTop: -d / 2, borderRadius: "50%", background: VIZ_HOT, opacity: 0.88, boxShadow: `0 0 0 2px ${T.surface}` }} />
                    <span style={{ position: "absolute", left: `calc(${left}% + ${d / 2 + 6}px)`, top: "50%", transform: "translateY(-50%)", fontSize: 12.5, fontWeight: 700, color: T.text, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>
                      {r.value}{unit} <span style={{ fontWeight: 500, color: T.textMuted }}>({r.size.toLocaleString("en-US")})</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="blog-dot-row" aria-hidden="true" style={{ borderTop: "none", paddingTop: 2 }}>
            <div className="blog-dot-label" />
            <div style={{ position: "relative", height: 16 }}>
              {ticks.map((t) => <span key={t} style={{ position: "absolute", left: `${(t / block.max) * 100}%`, transform: "translateX(-50%)", fontSize: 11, color: T.textMuted, fontFamily: T.sans }}>{t}{unit}</span>)}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <i style={{ width: dia(minSize), height: dia(minSize), borderRadius: "50%", background: VIZ_HOT, opacity: 0.88, display: "inline-block" }} />
              <i style={{ width: dia(maxSize), height: dia(maxSize), borderRadius: "50%", background: VIZ_HOT, opacity: 0.88, display: "inline-block" }} />
            </span>
            <span>Dot size shows the number of {block.sizeLabel}, from {minSize.toLocaleString("en-US")} to {maxSize.toLocaleString("en-US")}. The number in brackets is that count.</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "heatmap": {
      // One teal ramp; darker = a larger share of that disease's trials
      // exclude that kind of medication. Empty cells mean the disease's
      // checker has no drug of that kind, which is not the same as 0%.
      const vals = block.rows.flatMap((r) => r.values.filter((v): v is number => v !== null));
      const hi = Math.max(1, ...vals);
      const unit = block.unit ?? "%";
      const shade = (v: number) => {
        const t = Math.min(1, v / hi);
        const mix = (a: number, b: number) => Math.round(a + (b - a) * t);
        return `rgb(${mix(240, 14)}, ${mix(248, 95)}, ${mix(246, 86)})`;
      };
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-scroll-hint" aria-hidden="true">Swipe sideways to see the whole chart</div>
          <div style={{ overflowX: "auto" as const, WebkitOverflowScrolling: "touch" as const }}>
            <table className="blog-heatmap" style={{ borderCollapse: "separate" as const, borderSpacing: 3, width: "100%", minWidth: 520, fontFamily: T.sans }}>
              <thead>
                <tr>
                  <th scope="col" style={{ textAlign: "left" as const, fontSize: 11.5, color: T.textMuted, fontWeight: 600, padding: "0 6px 6px 0", position: "sticky" as const, left: 0, background: T.surface, zIndex: 1 }}>Disease</th>
                  {block.columns.map((c) => <th key={c} scope="col" style={{ fontSize: 11.5, color: T.textSecondary, fontWeight: 700, padding: "0 4px 6px", textAlign: "center" as const, lineHeight: 1.25, verticalAlign: "bottom" as const }}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((r, j) => (
                  <tr key={j}>
                    <th scope="row" style={{ textAlign: "left" as const, fontSize: 12.5, fontWeight: 600, color: T.text, padding: "0 8px 0 0", whiteSpace: "nowrap" as const, position: "sticky" as const, left: 0, background: T.surface, zIndex: 1 }}>
                      {r.href ? <Link href={r.href} style={{ color: T.text, textDecoration: "none" }}>{r.label}</Link> : r.label}
                    </th>
                    {r.values.map((v, k) => v === null ? (
                      <td key={k} aria-label="No medicines of this kind in this disease's checker" style={{ background: T.bg, border: `1px dashed ${T.borderLight}`, borderRadius: 4, height: 30 }} />
                    ) : (
                      <td key={k} style={{ background: shade(v), color: v / hi > 0.45 ? "#FFFFFF" : T.text, borderRadius: 4, textAlign: "center" as const, fontSize: 12.5, fontWeight: 700, height: 30, minWidth: 64 }}>{v}{unit}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span aria-hidden="true" style={{ width: 90, height: 10, borderRadius: 3, background: `linear-gradient(90deg, ${shade(0)}, ${shade(hi)})`, display: "inline-block" }} />
            <span>0{unit} to {hi}{unit} of the disease&apos;s trials. A dashed cell means no medicine of that kind is in that disease&apos;s checker.</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "log-ladder": {
      // Position is log10 of the share, so 0.2% and 90% both fit and the
      // gaps between them are honest in ratio terms, which is what "how
      // rare" means. Rungs are labeled as "1 in N" because that is how
      // people think about odds.
      const lo = Math.log10(block.min); const hi = Math.log10(block.max);
      const pos = (v: number) => Math.max(0, Math.min(100, ((Math.log10(v) - lo) / (hi - lo)) * 100));
      const rungs = [0.1, 1, 10, 100].filter((r) => r >= block.min && r <= block.max);
      const rungLabel = (r: number) => r >= 100 ? "all" : r >= 10 ? `1 in ${Math.round(100 / r)}` : `1 in ${Math.round(100 / r).toLocaleString("en-US")}`;
      const tone = (g?: string) => g === "high" ? VIZ_HOT : g === "mid" ? "#D97706" : T.primary;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-ladder-axis" aria-hidden="true">
            <div className="blog-dot-label" />
            <div style={{ position: "relative", height: 18 }}>
              {rungs.map((r) => <span key={r} style={{ position: "absolute", left: `${pos(r)}%`, transform: "translateX(-50%)", fontSize: 11, color: T.textMuted, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>{rungLabel(r)}</span>)}
            </div>
          </div>
          <div role="list">
            {block.rows.map((r, j) => {
              const left = pos(r.value);
              const label = r.href ? <Link href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</Link> : r.label;
              const shown = r.value >= 10 ? `${Math.round(r.value)}%` : `${r.value.toFixed(2)}%`;
              return (
                <div key={j} role="listitem" className="blog-dot-row">
                  <div className="blog-dot-label">{label}{r.sub && <span style={{ display: "block", fontSize: 11, color: T.textMuted, fontWeight: 400 }}>{r.sub}</span>}</div>
                  <div className="blog-dot-track" aria-label={`${r.label}: ${shown}${r.n ? ` of ${r.n.toLocaleString("en-US")}` : ""}`}>
                    {rungs.map((t) => <span key={t} aria-hidden="true" style={{ position: "absolute", left: `${pos(t)}%`, top: 0, bottom: 0, width: 1, background: T.borderLight }} />)}
                    <span aria-hidden="true" style={{ position: "absolute", left: 0, width: `${left}%`, top: "50%", height: 2, marginTop: -1, background: tone(r.group), opacity: 0.25 }} />
                    <span aria-hidden="true" style={{ position: "absolute", left: `${left}%`, top: "50%", width: 14, height: 14, marginLeft: -7, marginTop: -7, borderRadius: "50%", background: tone(r.group), boxShadow: `0 0 0 2px ${T.surface}` }} />
                    <span style={{ position: "absolute", left: left > 78 ? "auto" : `calc(${left}% + 13px)`, right: left > 78 ? `calc(${100 - left}% + 13px)` : "auto", top: "50%", transform: "translateY(-50%)", fontSize: 12.5, fontWeight: 700, color: T.text, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>
                      {shown}{r.n ? <span style={{ fontWeight: 500, color: T.textMuted }}> ({r.n.toLocaleString("en-US")})</span> : null}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 14, marginTop: 12, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            {([["common", "Common cancers"], ["mid", "Enriched"], ["high", "Defined by the fusion"]] as const).map(([g, l]) => (
              <span key={g} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 10, height: 10, borderRadius: "50%", background: tone(g), display: "inline-block" }} />{l}</span>
            ))}
            <span>Scale is logarithmic: each rung is 10 times the last. Brackets show how many tumors were tested.</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "route-gates": {
      // Every route to a drug as a gate. A dated strip on top shows the 2
      // separate threads of change (the drug's own supply and customs), and
      // each row shows whether that route is shut, shutting, open or narrow.
      const gate = (st: "closed" | "closing" | "open" | "narrow") => {
        const c = st === "open" ? "#0E5F56" : st === "narrow" ? "#0E5F56" : st === "closing" ? "#B45309" : "#9F1239";
        return (
          <svg width="34" height="30" viewBox="0 0 34 30" aria-hidden="true" style={{ flex: "0 0 auto" }}>
            <rect x="2" y="3" width="3" height="25" rx="1" fill={c} />
            <rect x="29" y="3" width="3" height="25" rx="1" fill={c} />
            {st === "closed" && <g stroke={c} strokeWidth="2.2" strokeLinecap="round"><line x1="6" y1="9" x2="28" y2="9" /><line x1="6" y1="16" x2="28" y2="16" /><line x1="6" y1="23" x2="28" y2="23" /><line x1="6" y1="9" x2="28" y2="23" /></g>}
            {st === "closing" && <g stroke={c} strokeWidth="2.2" strokeLinecap="round"><line x1="6" y1="9" x2="24" y2="11" /><line x1="6" y1="16" x2="24" y2="17" /><line x1="6" y1="23" x2="24" y2="23" /><path d="M26 14 l2 2 l-2 2" fill="none" /></g>}
            {st === "narrow" && <g stroke={c} strokeWidth="2.2" strokeLinecap="round"><line x1="6" y1="9" x2="21" y2="9" /><line x1="6" y1="16" x2="21" y2="16" /><line x1="6" y1="23" x2="21" y2="23" /></g>}
            {st === "open" && <g stroke={c} strokeWidth="2.2" strokeLinecap="round"><line x1="6" y1="9" x2="12" y2="4" /><line x1="6" y1="16" x2="12" y2="12" /><line x1="6" y1="23" x2="12" y2="20" /></g>}
          </svg>
        );
      };
      const fmt = (d: string) => {
        const [y, m, dd] = d.split("-").map(Number);
        const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][(m || 1) - 1];
        return dd ? `${mon} ${dd}, ${y}` : m ? `${mon} ${y}` : `${y}`;
      };
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 18, lineHeight: 1.5 }}>{block.note}</div>}
          <ol className="blog-rg-strip" aria-label="Dated changes, in order, not drawn to scale">
            {block.events.map((e, j) => (
              <li key={j} className={`blog-rg-ev blog-rg-${e.thread}`}>
                <span className="blog-rg-dot" aria-hidden="true" />
                <span className="blog-rg-date">{fmt(e.date)}</span>
                <span className="blog-rg-lbl">{e.label}</span>
              </li>
            ))}
          </ol>
          <div style={{ display: "flex", gap: 16, margin: "2px 0 18px", fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-rg-dot blog-rg-key-drug" aria-hidden="true" />The drug and the FDA</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-rg-dot blog-rg-key-customs" aria-hidden="true" />Customs and mail</span>
            <span>Spaced in order, not to scale</span>
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {block.routes.map((r, j) => (
              <li key={j} className={`blog-rg-row blog-rg-st-${r.status}`}>
                <div className="blog-rg-route">
                  {gate(r.status)}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans, lineHeight: 1.35 }}>{r.label}</div>
                    <span className={`blog-rg-badge blog-rg-badge-${r.status}`}>{r.statusLabel}</span>
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4, lineHeight: 1.4 }}>{r.who}</div>
                  </div>
                </div>
                <div className="blog-rg-col"><span className="blog-rg-h">What changed</span>{r.changed}</div>
                <div className="blog-rg-col"><span className="blog-rg-h">What it takes now</span>{r.now}</div>
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "artery-pair": {
      // 2 vessels side by side. The wall is drawn thick and dark to read as
      // inflamed artery; inside, every open trial is a cell, filled when a
      // drug company leads it. Approved drugs sit on the outer wall as tags.
      const W = 150, H = 170, wall = 14;
      const cells = (n: number, filled: boolean, startIdx: number) => {
        const cols = 5, r = 7.5, gx = (W - 2 * wall - 10) / cols;
        return Array.from({ length: n }, (_, k) => {
          const idx = startIdx + k, col = idx % cols, row = Math.floor(idx / cols);
          const cx = wall + 5 + gx * col + gx / 2 + ((row % 2) ? 3 : -3), cy = 24 + row * 20;
          return <circle key={`${filled}${k}`} cx={cx} cy={cy} r={r} fill={filled ? T.primary : "#fff"} stroke={filled ? T.primary : "#8A9A95"} strokeWidth={filled ? 0 : 1.6} />;
        });
      };
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-ap">
            {block.vessels.map((v, j) => {
              const total = v.industry + v.other;
              const rows = Math.ceil(total / 5);
              const h = Math.max(H, 24 + rows * 20 + 20);
              return (
                <div key={j} className="blog-ap-vessel">
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif, textAlign: "center", marginBottom: 2 }}>
                    {v.href ? <a href={v.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{v.label}</a> : v.label}
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.sans, textAlign: "center", marginBottom: 10, lineHeight: 1.4 }}>{v.sub}</div>
                  <div className="blog-ap-body">
                    <svg viewBox={`0 0 ${W} ${h}`} width={W} height={h} role="img" aria-label={`${v.label}: ${total} open trials, ${v.industry} led by a drug company, ${v.other} by hospitals, universities or agencies`}>
                      <defs>
                        <linearGradient id={`apw${i}${j}`} x1="0" x2="1">
                          <stop offset="0" stopColor="#B3464E" /><stop offset="0.5" stopColor="#8E2E37" /><stop offset="1" stopColor="#B3464E" />
                        </linearGradient>
                      </defs>
                      <rect x="0" y="0" width={W} height={h} rx="26" fill={`url(#apw${i}${j})`} />
                      <rect x={wall} y={wall} width={W - 2 * wall} height={h - 2 * wall} rx="16" fill="#FBEDEE" />
                      <g transform={`translate(0 ${wall})`}>
                        {cells(v.industry, true, 0)}
                        {cells(v.other, false, v.industry)}
                      </g>
                    </svg>
                    <ul className="blog-ap-tags" aria-label={`Approved drugs for ${v.label}`}>
                      {v.approvals.length === 0 && <li className="blog-ap-tag blog-ap-none">No US-approved drug</li>}
                      {v.approvals.map((a, k) => <li key={k} className="blog-ap-tag"><span className="blog-ap-year">{a.year}</span>{a.label}</li>)}
                    </ul>
                  </div>
                  <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
                    <strong style={{ color: T.text }}>{total} open trials.</strong> {v.verdict}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 16, margin: "14px 0 0", fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const, justifyContent: "center" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-ap-key" style={{ background: T.primary }} aria-hidden="true" />Trial led by a drug company</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-ap-key" style={{ background: "#fff", border: "1.5px solid #8A9A95" }} aria-hidden="true" />Led by a hospital, university or agency</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "result-race": {
      // Each lab test is a lane on a shared day axis; the band is the window
      // its result usually arrives in, with a darker stripe for the most
      // common part. Dashed uprights mark when treatment tends to start, so a
      // band that reaches past an upright is a result the patient may not
      // have yet at that moment.
      const W = 780, L = 205, R = 24, top = 34, rowH = 46, bottom = 56;
      const H = top + block.lanes.length * rowH + bottom;
      const x = (d: number) => L + (Math.min(Math.max(d, 0), block.days) / block.days) * (W - L - R);
      const ticks = Array.from({ length: Math.floor(block.days / 7) + 1 }, (_, k) => k * 7);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-rr-wrap">
            <svg className="blog-rr" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={block.caption}>
              {ticks.map((d) => (
                <g key={d}>
                  <line x1={x(d)} x2={x(d)} y1={top - 6} y2={H - bottom + 10} stroke={T.border} strokeWidth={1} />
                  <text x={x(d)} y={H - bottom + 26} textAnchor="middle" fontSize={11} fill={T.textMuted} fontFamily={T.sans}>{d === 0 ? "Diagnosis" : `Day ${d}`}</text>
                </g>
              ))}
              {block.lanes.map((ln, k) => {
                const y = top + k * rowH + 10;
                // Amber when the window runs past the last treatment marker.
                const lastMarker = Math.max(...block.markers.map((m) => m.day));
                const late = ln.to > lastMarker;
                const noteLeft = x(ln.to) + 8 > W - R - 150;
                return (
                  <g key={k}>
                    <text x={L - 12} y={y + 14} textAnchor="end" fontSize={12} fontWeight={700} fill={T.text} fontFamily={T.sans}>{ln.label}</text>
                    {ln.sub && <text x={L - 12} y={y + 28} textAnchor="end" fontSize={10.5} fill={T.textMuted} fontFamily={T.sans}>{ln.sub}</text>}
                    <rect x={x(ln.from)} y={y} width={Math.max(4, x(ln.to) - x(ln.from))} height={22} rx={11} fill={late ? "#F3E2C7" : T.primaryLight} stroke={late ? "#D9A441" : T.primaryMedium} strokeWidth={1} />
                    {ln.typical && <rect x={x(ln.typical[0])} y={y + 5} width={Math.max(4, x(ln.typical[1]) - x(ln.typical[0]))} height={12} rx={6} fill={late ? "#D9A441" : T.primary} opacity={0.85} />}
                    {noteLeft
                      ? <text x={x(ln.from) - 8} y={y + 15} textAnchor="end" fontSize={10.5} fill={T.textSecondary} fontFamily={T.sans}>{ln.note}</text>
                      : <text x={x(ln.to) + 8} y={y + 15} fontSize={10.5} fill={T.textSecondary} fontFamily={T.sans}>{ln.note}</text>}
                  </g>
                );
              })}
              {block.markers.map((m, k) => (
                <g key={k}>
                  <line x1={x(m.day)} x2={x(m.day)} y1={top - 14} y2={H - bottom + 10} stroke={T.text} strokeWidth={1.5} strokeDasharray="4 3" />
                  <text x={x(m.day) + 5} y={top - 16 + (k % 2) * 12} fontSize={11} fontWeight={700} fill={T.text} fontFamily={T.sans}>{m.label}</text>
                  {m.sub && <text x={x(m.day) + 5} y={top - 5 + (k % 2) * 12} fontSize={10} fill={T.textMuted} fontFamily={T.sans}>{m.sub}</text>}
                </g>
              ))}
            </svg>
          </div>
          <ul className="blog-rr-list" aria-hidden="true">
            {block.lanes.map((ln, k) => <li key={k}><strong>{ln.label}</strong>{ln.sub ? ` (${ln.sub})` : ""}: day {ln.from} to {ln.to}. {ln.note}</li>)}
          </ul>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, color: T.textMuted, fontFamily: T.sans, marginTop: 6 }}>
            <span><span style={{ display: "inline-block", width: 22, height: 10, borderRadius: 5, background: T.primaryLight, border: `1px solid ${T.primaryMedium}`, verticalAlign: "middle", marginRight: 5 }} />Usually back by the day 3 in 4 patients have started</span>
            <span><span style={{ display: "inline-block", width: 22, height: 10, borderRadius: 5, background: "#F3E2C7", border: "1px solid #D9A441", verticalAlign: "middle", marginRight: 5 }} />Often still pending at that point</span>
            <span><span style={{ display: "inline-block", width: 22, height: 6, borderRadius: 3, background: T.primary, verticalAlign: "middle", marginRight: 5 }} />Most common part of the window</span>
          </div>
          <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.55, marginTop: 12 }}>{block.caption}</div>
        </figure>
      );
    }
    case "pill-sieve": {
      // Medicines fall through a stack of label questions. Each stops on the
      // first mesh its label does not pass; the bottom slot shows what is
      // left, and a dashed capsule marks a decision still pending.
      const stops = block.gates.map((_, g) => block.items.filter((it) => it.stopAt === g));
      const through = block.items.filter((it) => it.stopAt >= block.gates.length);
      const pill = (it: (typeof block.items)[number], k: number, caught: boolean) => (
        <li key={k} className={`blog-ps-pill${caught ? " blog-ps-caught" : ""}`} title={it.why}>
          <span className="blog-ps-cap" aria-hidden="true" />
          {it.href ? <a href={it.href}>{it.label}</a> : <span>{it.label}</span>}
          <span className="blog-ps-why">{it.why}</span>
        </li>
      );
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-ps" role="list">
            {block.gates.map((g, gi) => (
              <div key={gi} className="blog-ps-stage" role="listitem">
                <div className="blog-ps-mesh" aria-hidden="true" />
                <div className="blog-ps-gate">
                  <span className="blog-ps-num">{gi + 1}</span>
                  <span><strong>{g.label}</strong>{g.sub && <em>{g.sub}</em>}</span>
                </div>
                <ul className="blog-ps-held" aria-label={`Stopped at: ${g.label}`}>
                  {stops[gi].length === 0 ? <li className="blog-ps-empty">None stopped here</li> : stops[gi].map((it, k) => pill(it, k, true))}
                </ul>
              </div>
            ))}
            <div className="blog-ps-tray">
              <div className="blog-ps-tray-label">Passes every question</div>
              <ul aria-label="Passes every question">
                {through.map((it, k) => pill(it, k, false))}
                {through.length === 0 && !block.pending && <li className="blog-ps-empty">Nothing yet</li>}
                {block.pending && (
                  <li className="blog-ps-pending">
                    <span className="blog-ps-cap" aria-hidden="true" />
                    {block.pending.href ? <a href={block.pending.href}>{block.pending.label}</a> : <span>{block.pending.label}</span>}
                    <span className="blog-ps-why">{block.pending.sub}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "drop-pairs": {
      // Bars hang down from a zero line, so losing more points reads as a
      // longer fall. Teal is a year on the drug, grey a year on placebo.
      const H = 150;
      const h = (v: number) => Math.max(3, (Math.abs(v) / block.max) * H);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-dp">
            {block.groups.map((g, gi) => (
              <div key={gi} className="blog-dp-group">
                <div className="blog-dp-label">{g.label}{g.sub && <span>{g.sub}</span>}</div>
                <div className="blog-dp-plot" role="img" aria-label={`${g.label}: ${g.bars.map((b, k) => `${block.periods[k]} ${b.value} ${block.unit} on ${b.on === "drug" ? "the drug" : "placebo"}`).join(", ")}`}>
                  <div className="blog-dp-zero" aria-hidden="true"><span>0</span></div>
                  {g.bars.map((b, k) => (
                    <div key={k} className="blog-dp-col">
                      <div className="blog-dp-period">{block.periods[k]}</div>
                      <div className="blog-dp-well" style={{ height: Math.max(...g.bars.map((x) => h(x.value))) + 30 }}>
                        <div className={`blog-dp-bar blog-dp-${b.on}`} style={{ height: h(b.value) }}>
                          <span className="blog-dp-drip" aria-hidden="true" />
                        </div>
                        <div className="blog-dp-val" style={{ top: h(b.value) + 6 }}>{b.value.toFixed(2)}</div>
                      </div>
                      <div className="blog-dp-on">{b.on === "drug" ? "on deramiocel" : "on placebo"}{b.n ? <span>{b.n} boys</span> : null}</div>
                    </div>
                  ))}
                </div>
                <div className="blog-dp-verdict">{g.verdict}</div>
              </div>
            ))}
          </div>
          {block.flag && <div className="blog-dp-flag">{block.flag}</div>}
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "weight-dial":
      return <WeightDial key={i} block={block} />;

    case "fit-finder":
      return <FitFinder key={i} block={block} />;

    case "trial-doors": {
      // Each open trial is a door. Open (ajar, teal): the listing lets the
      // group in. Shut (dark): it requires something they cannot do.
      // Unmarked (outline): the listing sets no rule either way. A small
      // flag marks a US site.
      const order = { open: 0, unmarked: 1, shut: 2 } as const;
      const doors = [...block.doors].sort((a, b) => order[a.state] - order[b.state]);
      const count = (st: "open" | "shut" | "unmarked") => block.doors.filter((d) => d.state === st).length;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-td-key" aria-hidden="true">
            <span><i className="blog-td-door blog-td-open" />{block.legend.open} <b>{count("open")}</b></span>
            <span><i className="blog-td-door blog-td-unmarked" />{block.legend.unmarked} <b>{count("unmarked")}</b></span>
            <span><i className="blog-td-door blog-td-shut" />{block.legend.shut} <b>{count("shut")}</b></span>
            <span><i className="blog-td-door blog-td-unmarked blog-td-us" />Has a US site</span>
          </div>
          <ul className="blog-td-wall">
            {doors.map((d) => (
              <li key={d.nct} className={`blog-td-door blog-td-${d.state}${d.us ? " blog-td-us" : ""}`}>
                <a href={`https://clinicaltrials.gov/study/${d.nct}`} target="_blank" rel="noopener" title={`${d.label} (${d.nct})${d.detail ? `: ${d.detail}` : ""}`} aria-label={`${d.label}, ${d.nct}, ${d.state === "open" ? block.legend.open : d.state === "shut" ? block.legend.shut : block.legend.unmarked}${d.us ? ", has a US site" : ""}`}>
                  <span className="blog-td-leaf" aria-hidden="true"><span className="blog-td-knob" /></span>
                </a>
                <span className="blog-td-name">{d.label}</span>
              </li>
            ))}
          </ul>
          <ul className="blog-td-list">
            {block.doors.filter((d) => d.state === "open").map((d) => (
              <li key={d.nct}>
                <a href={`https://clinicaltrials.gov/study/${d.nct}`} target="_blank" rel="noopener">{d.label}</a>
                <span> {d.nct}{d.us ? ", US site" : ", no US site"}{d.detail ? `. ${d.detail}` : ""}</span>
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "trial-tiles": {
      // 1 tile per open trial. Solid: a drug company leads it. Outlined:
      // a hospital, university or agency tests a treatment. Hatched: the
      // study only collects data. A dot in the corner marks a US site.
      const tile = (kind: "i" | "t" | "o", us: boolean, k: string) => (
        <span key={k} className={`blog-tt-tile blog-tt-${kind}${us ? " blog-tt-us" : ""}`} />
      );
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-tt-key" aria-hidden="true">
            <span><span className="blog-tt-tile blog-tt-i" />A drug company leads it</span>
            <span><span className="blog-tt-tile blog-tt-t" />A hospital, university or agency tests a treatment</span>
            <span><span className="blog-tt-tile blog-tt-o" />Only collects data</span>
            <span><span className="blog-tt-tile blog-tt-t blog-tt-us" />Has a US site</span>
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {block.rows.map((r, j) => {
              const t = r.tiles; const total = t.i + t.iu + t.t + t.tu + t.o + t.ou; const ind = t.i + t.iu; const us = t.iu + t.tu + t.ou;
              return (
                <li key={j} className="blog-tt-row">
                  <div className="blog-tt-label">
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>
                      {r.href ? <a href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</a> : r.label}
                    </span>
                    <span className="blog-tt-sub">{total} open{ind ? `, ${ind} company-led` : ", none company-led"}, {us} with a US site</span>
                    {r.flag && <span className="blog-tt-flag">{r.flag}</span>}
                  </div>
                  <div className="blog-tt-tiles" role="img" aria-label={`${r.label}: ${total} open trials, ${ind} led by a drug company, ${t.o + t.ou} hospital-led studies that only collect data, ${us} with a US site`}>
                    {Array.from({ length: t.iu }, (_, k) => tile("i", true, `iu${k}`))}
                    {Array.from({ length: t.i }, (_, k) => tile("i", false, `i${k}`))}
                    {Array.from({ length: t.tu }, (_, k) => tile("t", true, `tu${k}`))}
                    {Array.from({ length: t.t }, (_, k) => tile("t", false, `t${k}`))}
                    {Array.from({ length: t.ou }, (_, k) => tile("o", true, `ou${k}`))}
                    {Array.from({ length: t.o }, (_, k) => tile("o", false, `o${k}`))}
                  </div>
                </li>
              );
            })}
          </ul>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "age-gates": {
      // A child's age along the bottom, birth to maxAge. Each row starts
      // where its label lets a child in. Weight-gated rows are hatched
      // because a scale, not a birthday, opens them; a pending decision is
      // dashed because no label exists yet to say which ages it covers.
      const pct = (a: number) => `${Math.max(0, Math.min(100, (a / block.maxAge) * 100))}%`;
      const step = block.tickStep && block.tickStep > 0 ? block.tickStep : 1;
      const ticks = Array.from({ length: Math.floor(block.maxAge / step) + 1 }, (_, k) => k * step);
      return (
        <figure key={i} style={{ position: "relative", margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 18, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-ag">
            <div className="blog-ag-zones" aria-hidden="true">
              {block.zones.map((z, j) => (
                <div key={j} className={`blog-ag-zone blog-ag-zone-${j % 2}`} style={{ left: pct(z.from), width: `calc(${pct(z.to)} - ${pct(z.from)})` }}>
                  <span>{z.label}</span>
                </div>
              ))}
            </div>
            <div className="blog-ag-body">
              {block.gap && (
                <div className="blog-ag-gap" style={{ width: `calc((100% - var(--ag-lab)) * ${Math.min(1, block.gap.to / block.maxAge)})` }} aria-hidden="true" />
              )}
              <ul style={{ listStyle: "none", margin: 0, padding: 0, position: "relative" }}>
                {block.rows.map((r, j) => {
                  const start = r.kind === "weight" || r.kind === "pending" ? 0 : (r.from ?? 0);
                  return (
                    <li key={j} className="blog-ag-row">
                      <div className="blog-ag-label">
                        <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>
                          {r.href ? <a href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</a> : r.label}
                        </span>
                        {r.sub && <span style={{ display: "block", fontSize: 12, color: T.textMuted, marginTop: 2, lineHeight: 1.35 }}>{r.sub}</span>}
                      </div>
                      <div className="blog-ag-track">
                        <div className={`blog-ag-bar blog-ag-${r.kind}`} style={{ left: pct(start), width: r.to != null ? `calc(${pct(r.to)} - ${pct(start)})` : `calc(100% - ${pct(start)})` }}>
                          <span>{r.barLabel}</span>
                        </div>
                        {r.kind !== "weight" && r.kind !== "pending" && (r.from ?? 0) > 0 && (
                          <span className="blog-ag-gate" style={{ left: pct(r.from ?? 0) }} aria-hidden="true" />
                        )}
                        {r.to != null && r.to < block.maxAge && (
                          <span className="blog-ag-gate" style={{ left: pct(r.to) }} aria-hidden="true" />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="blog-ag-axis" aria-hidden="true">
              {ticks.map((t) => (
                <span key={t} style={{ left: pct(t) }}>{t === 0 ? "Birth" : block.openEnd && t === ticks[ticks.length - 1] ? `${t}+` : `${t}`}</span>
              ))}
            </div>
            <div style={{ textAlign: "right" as const, fontSize: 11.5, color: T.textMuted, fontFamily: T.sans, marginTop: 2 }}>Age in years</div>
          </div>
          {block.legend ? (
          <div style={{ display: "flex", gap: 14, margin: "12px 0 0", fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            {block.legend.map((l, j) => <span key={j} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className={`blog-ag-key blog-ag-${l.kind}`} />{l.label}</span>)}
          </div>
          ) : (
          <div style={{ display: "flex", gap: 14, margin: "12px 0 0", fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-ag-key blog-ag-approved" />FDA-approved, by age</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-ag-key blog-ag-weight" />FDA-approved, by weight</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-ag-key blog-ag-trial" />Recruiting trials</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-ag-key blog-ag-pending" />Under FDA review</span>
            {block.gap && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-ag-key blog-ag-gapkey" />{block.gap.label}</span>}
          </div>
          )}
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
          {/* The same rows as a plain table for screen readers and for search
              and AI tools that read text, not drawings. */}
          <div style={{ position: "absolute", left: 0, top: 0, width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", clipPath: "inset(50%)" }}>
          <table>
            <caption>{block.title || block.caption}</caption>
            <thead><tr><th scope="col">{block.rowHead || "Treatment"}</th><th scope="col">Who it reaches</th><th scope="col">Status</th></tr></thead>
            <tbody>
              {block.rows.map((r, j) => (
                <tr key={j}><th scope="row">{r.label}</th><td>{r.barLabel}</td><td>{r.sub || ""}</td></tr>
              ))}
            </tbody>
          </table>
          </div>
        </figure>
      );
    }

    case "check-grid": {
      // What each free check can show you. A filled circle means the check
      // shows that kind of tracking, a half circle means it shows some of it,
      // and an empty ring means it is not built to show it.
      // Optional per-post legend words (October 28, 2026); the defaults are
      // the original privacy-check wording.
      const lg = block.legend || { yes: "Shows this", partly: "Shows some of it", no: "Not built to show it" };
      const mark = (v: "yes" | "partly" | "no", label: string) => {
        const txt = v === "yes" ? lg.yes : v === "partly" ? lg.partly : lg.no;
        return (
          <span role="img" aria-label={`${label}: ${txt}`} title={txt} className={`blog-cg-mark blog-cg-${v}`} />
        );
      };
      const cols = block.columns.length;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-cg-mobile">
            {block.rows.map((r, j) => (
              <div key={j} className="blog-cg-mrow">
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{r.href ? <a href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noopener" style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</a> : r.label}</div>
                {r.detail && <div style={{ fontSize: 12, color: T.textSecondary, margin: "2px 0 8px", lineHeight: 1.4 }}>{r.detail}</div>}
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                  {block.columns.map((c) => {
                    const v = r.cells[c.key] || "no";
                    return <span key={c.key} className={`blog-cg-chip blog-cg-chip-${v}`}>{mark(v, `${r.label}, ${c.label}`)}{c.label}</span>;
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="blog-cg-scroll">
            <div className="blog-cg" style={{ gridTemplateColumns: `minmax(170px, 1.6fr) repeat(${cols}, minmax(70px, 1fr))` }}>
              <div className="blog-cg-head" />
              {block.columns.map((c) => <div key={c.key} className="blog-cg-head blog-cg-colhead">{c.label}</div>)}
              {block.rows.map((r, j) => (
                <div key={j} style={{ display: "contents" }}>
                  <div className="blog-cg-rowhead">
                    {r.href ? <a href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noopener" style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</a> : r.label}
                    {r.sub && <span style={{ display: "block", fontSize: 11.5, color: T.textMuted, fontWeight: 400, marginTop: 2 }}>{r.sub}</span>}
                    {r.detail && <span style={{ display: "block", fontSize: 11.5, color: T.textSecondary, fontWeight: 400, marginTop: 4, lineHeight: 1.4 }}>{r.detail}</span>}
                  </div>
                  {block.columns.map((c) => <div key={c.key} className="blog-cg-cell">{mark(r.cells[c.key] || "no", `${r.label}, ${c.label}`)}</div>)}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 14, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-cg-mark blog-cg-yes" aria-hidden="true" />{lg.yes}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-cg-mark blog-cg-partly" aria-hidden="true" />{lg.partly}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="blog-cg-mark blog-cg-no" aria-hidden="true" />{lg.no}</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "data-trail": {
      // Each case as a trail: who collected it, what left, who received it,
      // and how it ended. Chips carry the data so a reader can scan across.
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {block.cases.map((c, j) => (
              <li key={j} className="blog-dt-row">
                <div className="blog-dt-who">
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{c.who}</span>
                  <span style={{ display: "block", fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>{c.when} · {c.source}</span>
                </div>
                <div className="blog-dt-flow">
                  <div className="blog-dt-chips">{c.data.map((d) => <span key={d} className="blog-dt-chip blog-dt-data">{d}</span>)}</div>
                  <span className="blog-dt-arrow" aria-label="sent to">→</span>
                  <div className="blog-dt-chips">{c.to.map((d) => <span key={d} className="blog-dt-chip blog-dt-to">{d}</span>)}</div>
                </div>
                <div className="blog-dt-outcome">{c.outcome}</div>
              </li>
            ))}
          </ol>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "copper-bands": {
      // Bands on a shared axis per panel. Log panels let 20 and 2,000 sit on
      // one line with honest ratios; linear panels suit small blood values.
      const CU_LOW = "#FDE2D3", CU_LOW_EDGE = "#C2410C", CU_OK = "#CDEBE3", CU_OK_EDGE = "#0E5F56", CU_START = "#FEF3C7", CU_START_EDGE = "#B45309";
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          {block.panels.map((pn, pi) => {
            const lo = pn.scale === "log" ? Math.log10(pn.min) : pn.min;
            const hi = pn.scale === "log" ? Math.log10(pn.max) : pn.max;
            const pos = (v: number) => {
              const x = pn.scale === "log" ? Math.log10(Math.max(v, pn.min)) : v;
              return Math.max(0, Math.min(100, ((x - lo) / (hi - lo)) * 100));
            };
            const fmt = (v: number) => v.toLocaleString("en-US");
            return (
              <div key={pi} style={{ marginTop: pi ? 26 : 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontFamily: T.sans, marginBottom: 2 }}>{pn.title}</div>
                <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.sans, marginBottom: 8 }}>{pn.unit}{pn.scale === "log" ? ", on a scale where each step is 10 times the last" : ""}</div>
                <div className="blog-cu-row blog-cu-axis" aria-hidden="true">
                  <div className="blog-cu-label" />
                  <div style={{ position: "relative", height: 16 }}>
                    {pn.ticks.map((t) => <span key={t} style={{ position: "absolute", left: `${pos(t)}%`, transform: pos(t) <= 0 ? "none" : pos(t) >= 100 ? "translateX(-100%)" : "translateX(-50%)", fontSize: 11, color: T.textMuted, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>{fmt(t)}</span>)}
                  </div>
                </div>
                <div role="list">
                  {pn.rows.map((r, j) => {
                    const label = r.href ? <Link href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</Link> : r.label;
                    const desc = [
                      r.low != null ? `below ${fmt(r.low)} may mean too much treatment` : "",
                      r.stable ? `stable target ${fmt(r.stable[0])} to ${fmt(r.stable[1])}` : "",
                      r.start ? `early treatment ${fmt(r.start[0])} to ${fmt(r.start[1])}` : "",
                      ...(r.marks || []).map((m) => m.text),
                    ].filter(Boolean).join("; ");
                    return (
                      <div key={j} role="listitem" className="blog-cu-row">
                        <div className="blog-cu-label">{label}{r.sub && <span style={{ display: "block", fontSize: 11, color: T.textMuted, fontWeight: 400, marginTop: 2 }}>{r.sub}</span>}</div>
                        <div className="blog-cu-track" aria-label={`${r.label}: ${desc}`}>
                          {pn.ticks.map((t) => <span key={t} aria-hidden="true" style={{ position: "absolute", left: `${pos(t)}%`, top: 0, bottom: 0, width: 1, background: T.borderLight }} />)}
                          {r.low != null && <span aria-hidden="true" title={`Below ${fmt(r.low)}`} style={{ position: "absolute", left: 0, width: `${pos(r.low)}%`, top: 8, height: 18, background: `repeating-linear-gradient(135deg, ${CU_LOW} 0 6px, #FFF7F2 6px 10px)`, borderRight: `2px solid ${CU_LOW_EDGE}`, borderRadius: "4px 0 0 4px" }} />}
                          {r.stable && <span aria-hidden="true" style={{ position: "absolute", left: `${pos(r.stable[0])}%`, width: `${pos(r.stable[1]) - pos(r.stable[0])}%`, top: 8, height: 18, background: CU_OK, border: `1.5px solid ${CU_OK_EDGE}`, borderRadius: 4, boxSizing: "border-box" as const }} />}
                          {r.start && <span aria-hidden="true" style={{ position: "absolute", left: `${pos(r.start[0])}%`, width: `${pos(r.start[1]) - pos(r.start[0])}%`, top: 8, height: 18, background: CU_START, border: `1.5px dashed ${CU_START_EDGE}`, borderRadius: 4, boxSizing: "border-box" as const }} />}
                          {(r.marks || []).map((m, k) => {
                            const left = pos(m.value);
                            const right = m.to != null ? pos(m.to) : null;
                            const flip = left > 50;
                            return (
                              <span key={k} aria-hidden="true">
                                {right != null
                                  ? <span style={{ position: "absolute", left: `${left}%`, width: `${right - left}%`, top: 30, height: 4, background: T.text, borderRadius: 2 }} />
                                  : <span style={{ position: "absolute", left: `${left}%`, top: 3, height: 28, width: 2.5, marginLeft: -1.25, background: T.text, borderRadius: 1 }} />}
                                <span style={{ position: "absolute", top: 36 + k * 15, left: flip ? "auto" : `${left}%`, right: flip ? `${100 - (right ?? left)}%` : "auto", fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>{m.text}</span>
                              </span>
                            );
                          })}
                          <span aria-hidden="true" style={{ display: "block", height: 36 + (r.marks?.length || 0) * 15 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div style={{ display: "flex", gap: 16, marginTop: 14, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 16, height: 10, borderRadius: 2, display: "inline-block", background: `repeating-linear-gradient(135deg, ${CU_LOW} 0 4px, #FFF7F2 4px 7px)`, border: `1px solid ${CU_LOW_EDGE}` }} />May mean too much treatment</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 16, height: 10, borderRadius: 2, display: "inline-block", background: CU_OK, border: `1px solid ${CU_OK_EDGE}` }} />Stable target</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 16, height: 10, borderRadius: 2, display: "inline-block", background: CU_START, border: `1px dashed ${CU_START_EDGE}` }} />First weeks of a chelator</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 3, height: 12, display: "inline-block", background: T.text }} />A label's or trial's own figure</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "bar-clear": {
      // The bar to clear. A gauge on the left carries the scale; 3 lines cross
      // the plot at the typical start, the floor and the typical goal. Each
      // group is a crowd of dots, 1 per person: people who cleared the goal
      // stack above its line, everyone else waits below the start line.
      const span = block.max - block.min;
      const yp = (v: number) => ((block.max - v) / span) * 100;
      const ticks: number[] = [];
      for (let v = Math.ceil(block.min / block.step) * block.step; v <= block.max + 1e-9; v += block.step) ticks.push(Math.round(v * 10) / 10);
      const lines = [
        { v: block.target.value, label: block.target.label, color: VIZ_HOT, style: "solid", w: 2.5, weight: 700 },
        { v: block.floor.value, label: block.floor.label, color: T.textSecondary, style: "dashed", w: 1.5, weight: 600 },
        { v: block.start.value, label: block.start.label, color: T.textMuted, style: "dotted", w: 1.5, weight: 500 },
      ];
      const tone = (g: (typeof block.groups)[number]) => (g.hot === false ? T.primary : VIZ_HOT);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="bc-head" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${block.groups.length}, 1fr)` }}>
            {block.groups.map((g, gi) => <span key={gi} style={{ color: tone(g) }}>{g.label}</span>)}
          </div>
          <div className="bc-plot" role="img" aria-label={block.groups.map((g) => `${g.label}: ${g.cleared} of ${g.total} reached the goal`).join("; ")}>
            {ticks.map((t) => (
              <span key={t} className="bc-tick" style={{ top: `${yp(t)}%` }}>{t}</span>
            ))}
            <span className="bc-gauge" />
            <span className="bc-gauge-fill" style={{ top: `${yp(block.start.value)}%` }} />
            <span className="bc-gauge-climb" style={{ top: `${yp(block.target.value)}%`, height: `${yp(block.start.value) - yp(block.target.value)}%` }}>
              <b>+{Math.round((block.target.value - block.start.value) * 10) / 10}</b>
            </span>
            {lines.map((l, k) => (
              <span key={k} className="bc-line" style={{ top: `${yp(l.v)}%`, borderTop: `${l.w}px ${l.style} ${l.color}` }}>
                <em style={{ color: l.color, fontWeight: l.weight, ...(k === 0 ? { top: 4, bottom: "auto" } : {}) }}>{l.label}</em>
              </span>
            ))}
            <div className="bc-crowds" style={{ gridTemplateColumns: `repeat(${block.groups.length}, 1fr)` }}>
              {block.groups.map((g, gi) => (
                <div key={gi} className="bc-crowd">
                  <div className="bc-up" style={{ bottom: `calc(${100 - yp(block.target.value)}% + 6px)` }}>
                    {Array.from({ length: g.cleared }, (_, k) => <i key={k} style={{ background: tone(g) }} />)}
                  </div>
                  <div className="bc-down" style={{ top: `calc(${yp(block.start.value)}% + 8px)` }}>
                    {Array.from({ length: g.total - g.cleared }, (_, k) => <i key={k} />)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bc-head bc-counts" style={{ gridTemplateColumns: `repeat(${block.groups.length}, 1fr)` }}>
            {block.groups.map((g, gi) => (
              <span key={gi}>
                <strong style={{ color: tone(g) }}>{g.cleared} of {g.total}</strong>
                {g.sub && <small>{g.sub}</small>}
              </span>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" as const, marginTop: 12, padding: "10px 12px", background: T.bg, borderRadius: 10, fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.45 }}>
            <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              {[0, 1, 2].map((k) => <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><i style={{ width: 11, height: 11, borderRadius: "50%", background: VIZ_HOT, display: "inline-block" }} />{k < 2 && <i style={{ width: 18, height: 2, background: VIZ_HOT, opacity: 0.5, display: "inline-block" }} />}</span>)}
            </span>
            <span style={{ flex: "1 1 200px" }}>{block.hold}</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "voice-ledger": {
      // 1 square per condition on top, then the conditions by name. The
      // tones never use alarm red: a missing record is a gap, not a danger.
      const toneColor = { heard: T.primary, umbrella: "#C98A1B", none: "#FFFFFF" } as const;
      const total = block.groups.reduce((n, g) => n + g.items.length, 0);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="vl-strip" role="img" aria-label={block.groups.map((g) => `${g.items.length} of ${total} ${g.label}`).join("; ")}>
            {block.groups.flatMap((g, gi) => g.items.map((_, k) => <i key={`${gi}-${k}`} className={`vl-sq vl-${g.tone}`} style={{ background: toneColor[g.tone] }} />))}
          </div>
          {block.groups.map((g, gi) => (
            <section key={gi} className="vl-group">
              <div className="vl-head">
                <span className={`vl-sq vl-${g.tone} vl-key`} style={{ background: toneColor[g.tone] }} aria-hidden="true" />
                <strong className="vl-count" style={{ color: g.tone === "none" ? T.text : toneColor[g.tone] }}>{g.items.length}</strong>
                <span className="vl-label">{g.label}<small>{g.sub}</small></span>
              </div>
              <ul className="vl-chips">
                {g.items.map((it, k) => (
                  <li key={k} className={`vl-chip vl-chip-${g.tone}`}>
                    <Link href={it.href}>{it.name}</Link>
                    {it.meta && <em>{it.meta}</em>}
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "comment-sketch": {
      // A comment drawn as a sheet of paper. The margin says what each part
      // does; the body shows a short example of it.
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="cs-sheet">
            <div className="cs-top"><span>{block.header}</span><b>{block.due}</b></div>
            <ol className="cs-parts">
              {block.parts.map((pt, k) => (
                <li key={k} className="cs-part">
                  <div className="cs-margin"><span className="cs-tag">{pt.tag}</span><span className="cs-what">{pt.label}</span></div>
                  <p className="cs-body">{pt.example}</p>
                </li>
              ))}
            </ol>
            <div className="cs-foot">{block.footer}</div>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "mirror-bars": {
      // A butterfly: comparison bars grow left, the drug's bars grow right,
      // so the eye reads each side effect as a pair, not 2 separate lists.
      const pctW = (v: number) => `${Math.min(100, (v / block.max) * 100)}%`;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-mirror-row blog-mirror-head" aria-hidden="true">
            <span style={{ textAlign: "right" as const }}>{block.leftLabel}</span><span /><span>{block.rightLabel}</span>
          </div>
          <div role="list">
            {block.rows.map((r, j) => (
              <div key={j} role="listitem" className="blog-mirror-row" aria-label={`${r.label}: ${r.right}${block.unit} ${block.rightLabel}, ${r.left}${block.unit} ${block.leftLabel}`}>
                <div className="blog-mirror-side" style={{ justifyContent: "flex-end" }}>
                  <b style={{ color: T.textMuted }}>{r.left}{block.unit}</b>
                  <span style={{ width: pctW(r.left), background: VIZ_COOL, borderRadius: "6px 0 0 6px" }} />
                </div>
                <div className="blog-mirror-label">{r.label}</div>
                <div className="blog-mirror-side">
                  <span style={{ width: pctW(r.right), background: VIZ_HOT, borderRadius: "0 6px 6px 0" }} />
                  <b style={{ color: VIZ_HOT }}>{r.right}{block.unit}</b>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "leak-steps": {
      // Each step is a band whose width is the share that gets through.
      // The grey remainder is the leak. Steps come from different studies,
      // so they are drawn as separate rows, not multiplied into one funnel.
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {block.steps.map((st, j) => (
              <li key={j} className="blog-leak-step">
                <div className="blog-leak-head">
                  <span className="blog-leak-n" aria-hidden="true">{j + 1}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{st.label}</span>
                </div>
                <div className="blog-leak-bar" role="img" aria-label={`${st.label}: ${st.value}% get through, ${100 - st.value}% do not`}>
                  <span style={{ width: `${st.value}%`, background: VIZ_HOT }} />
                  <span style={{ width: `${100 - st.value}%`, background: VIZ_COOL }} />
                  <b style={{ left: `calc(${Math.min(st.value, 86)}% + 8px)` }}>{st.value}% get through</b>
                </div>
                <div style={{ fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.5 }}>{st.detail} <span style={{ color: T.textMuted }}>({st.source})</span></div>
              </li>
            ))}
          </ol>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "liter-jugs": {
      // One outline per liter up to the largest volume reported. Solid jugs
      // are the average, the last one filled only to its decimal, so the
      // reader can count liters instead of reading an axis.
      const jugs = Array.from({ length: block.rangeMax }, (_, j) => Math.max(0, Math.min(1, block.filled - j)));
      const at = (v: number) => `${(v / block.rangeMax) * 100}%`;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-jug-grid" role="img" aria-label={`${block.filledLabel}. ${block.rangeLabel}.`}>
            {jugs.map((f, j) => (
              <i key={j} className="blog-jug" style={{ borderColor: f > 0 ? T.primary : "#C9C3B8", background: f > 0 ? `linear-gradient(to top, ${T.primary} ${Math.round(f * 100)}%, transparent ${Math.round(f * 100)}%)` : "transparent" }} />
            ))}
          </div>
          <div className="blog-jug-track" aria-hidden="true">
            <span style={{ left: at(block.rangeMin), width: `calc(100% - ${at(block.rangeMin)})` }} />
            <b style={{ left: at(block.filled) }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" as const, fontSize: 12.5, fontFamily: T.sans, marginTop: 8 }}>
            <span style={{ color: T.primaryDark, fontWeight: 700 }}>{block.filledLabel}</span>
            <span style={{ color: T.textMuted }}>{block.rangeLabel}</span>
          </div>
          <dl className="blog-jug-facts">
            {block.facts.map((f, j) => (
              <div key={j}><dt>{f.label}</dt><dd>{f.value}</dd></div>
            ))}
          </dl>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "voice-proof": {
      // Left: the share of patients who picked each item, on one shared
      // scale. Right: what the trial showed for it, as a plain-word chip.
      const top = Math.max(10, Math.ceil(Math.max(...block.rows.map((r) => r.share)) / 10) * 10);
      const CHIP: Record<string, { text: string; color: string; bg: string }> = {
        "shown": { text: "Shown in the trial", color: T.primaryDark, bg: "#E3F1EE" },
        "partly": { text: "Partly shown", color: "#0F5F56", bg: "#EEF5F3" },
        "not-shown": { text: "Not shown", color: VIZ_HOT, bg: "#FBE9DF" },
        "not-measured": { text: "Not measured on its own", color: "#57534E", bg: "#EFECE7" },
        "outside": { text: "Outside what a trial tests", color: "#334155", bg: "#E6EAF0" },
      };
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-vp-head" aria-hidden="true"><span>{block.leftHead}</span><span>{block.rightHead}</span></div>
          <div role="list">
            {block.rows.map((r, j) => {
              const c = CHIP[r.verdict];
              return (
                <div key={j} role="listitem" className="blog-vp-row">
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{r.need}</div>
                    {r.share > 0 ? (
                      <div className="blog-vp-bar" role="img" aria-label={`${r.share}% of poll answers`}>
                        <span style={{ width: `${(r.share / top) * 100}%` }} />
                        <b>{r.share}%</b>
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.45 }}>{r.measure}</div>
                    <span className="blog-vp-chip" style={{ color: c.color, background: c.bg }}>{c.text}</span>
                    <div style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans, lineHeight: 1.5, marginTop: 4 }}>{r.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11.5, color: T.textMuted, fontFamily: T.sans, marginTop: 8 }}>Bars run from 0% to {top}%.</div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "lung-thermo": {
      // Two thermometers per timepoint on one scale. The fill is the change
      // from where each group started, so the reader compares heights.
      const H = 170;
      const h = (v: number) => Math.max(4, (v / block.scaleMax) * H);
      const tube = (v: number, color: string, label: string) => (
        <div className="blog-thermo" aria-hidden="true">
          <em style={{ color }}>{v.toFixed(1)}</em>
          <div className="blog-thermo-tube" style={{ height: H }}>
            <span style={{ height: h(v), background: color }} />
          </div>
          <i style={{ background: color }} />
          <small>{label}</small>
        </div>
      );
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-thermo-row">
            {block.points.map((p, j) => (
              <div key={j} className="blog-thermo-group" role="img" aria-label={`${p.label}: ${block.drugLabel} up ${p.drug} ${block.unit}, ${block.placeboLabel} up ${p.placebo} ${block.unit}. ${p.gapNote}`}>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 10, justifyContent: "center" }}>
                  {tube(p.drug, T.primary, block.drugLabel)}
                  {tube(p.placebo, "#A8A29E", block.placeboLabel)}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans, textAlign: "center" as const, marginTop: 10 }}>{p.label}</div>
                <div style={{ fontSize: 12.5, color: T.primaryDark, fontFamily: T.sans, textAlign: "center" as const, marginTop: 2, lineHeight: 1.4 }}>{p.gapNote}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: T.textMuted, fontFamily: T.sans, marginTop: 10, textAlign: "center" as const }}>Each tube runs from 0 to {block.scaleMax} {block.unit} of improvement.</div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "review-tracks": {
      // Each row is one review, placed by days from its own original goal
      // date (day 0), so the same step in 2 different years lines up.
      const span = block.max - block.min;
      const pos = (d: number) => `${((Math.min(block.max, Math.max(block.min, d)) - block.min) / span) * 100}%`;
      const days = (from: string, to: string) => Math.round((Date.parse(to + "T12:00:00Z") - Date.parse(from + "T12:00:00Z")) / 86_400_000);
      const color = (k: string) => (k === "approval" ? T.primary : k === "launch" ? PRICE_B : k === "goal" ? T.text : k === "ahead" ? "#fff" : "#A8A29E");
      const nice = (iso: string) => new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          {block.rows.map((r, j) => {
            const pts = r.points.map((p) => ({ ...p, d: days(r.anchor, p.date) })).sort((a, b) => a.d - b.d);
            return (
              <div key={j} style={{ marginBottom: j === block.rows.length - 1 ? 4 : 22 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{r.label}</div>
                {r.sub && <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.sans, marginTop: 1 }}>{r.sub}</div>}
                <div className="blog-rt-track" aria-hidden="true">
                  <span className="blog-rt-zero" style={{ left: pos(0) }} />
                  {pts.map((p, k) => (
                    <i key={k} style={{ left: pos(p.d), background: color(p.kind), borderColor: p.kind === "ahead" ? T.primary : "#fff", borderStyle: p.kind === "ahead" ? "dashed" : "solid", color: p.kind === "goal" || p.kind === "approval" || p.kind === "launch" ? "#fff" : p.kind === "ahead" ? T.primary : T.text }}>{k + 1}</i>
                  ))}
                </div>
                <ol className="blog-rt-list">
                  {pts.map((p, k) => (
                    <li key={k}>
                      <b style={{ background: color(p.kind), color: p.kind === "goal" || p.kind === "approval" || p.kind === "launch" ? "#fff" : p.kind === "ahead" ? T.primary : T.text, border: p.kind === "ahead" ? `1.5px dashed ${T.primary}` : "none" }}>{k + 1}</b>
                      <span><strong>{p.approx ? new Date(p.date + "T12:00:00Z").toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }) : nice(p.date)}</strong>, {p.approx ? "about " : ""}{p.d === 0 ? "day 0" : `${p.d > 0 ? "day +" : "day "}${p.d}`}. {p.label}</span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          })}
          <div style={{ fontSize: 11.5, color: T.textMuted, fontFamily: T.sans, marginTop: 6 }}>Track runs from day {block.min} to day +{block.max}. Day 0 is each review&apos;s original goal date.</div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "face-off": {
      // A real <table> for extraction, with each drug's column header in its
      // own color. On phones each row becomes a card and every cell carries
      // its drug name, so nothing depends on seeing the header row.
      return (
        <figure key={i} style={{ margin: "32px 0", padding: 0 }}>
          <div className="blog-faceoff-wrap" style={{ border: `1px solid ${T.border}`, borderRadius: 14, background: T.surface, overflow: "hidden" }}>
            <table className="blog-faceoff" style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.sans }}>
              <caption style={{ captionSide: "top", textAlign: "left", padding: "18px 18px 12px", fontFamily: T.serif, fontSize: 16, fontWeight: 700, color: T.text }}>{block.caption}</caption>
              <thead>
                <tr>
                  <th scope="col" style={{ width: "24%", padding: "10px 14px", background: T.bg, borderBottom: `1px solid ${T.border}` }}><span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Question</span></th>
                  <th scope="col" style={{ padding: "10px 14px", background: T.bg, borderBottom: `3px solid ${T.primary}`, textAlign: "left", fontSize: 14, fontWeight: 800, color: T.primary }}>{block.a}</th>
                  <th scope="col" style={{ padding: "10px 14px", background: T.bg, borderBottom: `3px solid ${PRICE_B}`, textAlign: "left", fontSize: 14, fontWeight: 800, color: PRICE_B }}>{block.b}</th>
                </tr>
              </thead>
              <tbody>
                {block.rows.map((r, j) => (
                  <tr key={j} style={{ borderBottom: j === block.rows.length - 1 ? "none" : `1px solid ${T.borderLight}` }}>
                    <th scope="row" style={{ textAlign: "left", padding: "12px 14px", fontSize: 13, fontWeight: 700, color: T.primaryDark, verticalAlign: "top", lineHeight: 1.45 }}>{r.label}</th>
                    <td className="fo-a" data-label={block.a} style={{ padding: "12px 14px", fontSize: 14, lineHeight: 1.55, color: T.textSecondary, verticalAlign: "top" }}>{parseInlineLinks(r.a)}</td>
                    <td className="fo-b" data-label={block.b} style={{ padding: "12px 14px", fontSize: 14, lineHeight: 1.55, color: T.textSecondary, verticalAlign: "top" }}>{parseInlineLinks(r.b)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.note && <figcaption style={{ fontSize: 12.5, color: T.textMuted, fontFamily: T.sans, marginTop: 8, lineHeight: 1.5 }}>{parseInlineLinks(block.note)}</figcaption>}
        </figure>
      );
    }

    case "policy-gates": {
      // Each plan is a short numbered path. A step that needs a gene test is
      // shaded and badged, which is the comparison the reader came for.
      const colorOf = (d: "a" | "b") => (d === "a" ? T.primary : PRICE_B);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-gates-grid">
            {block.policies.map((pol, j) => (
              <section key={j} className="blog-gates-card" aria-label={`${pol.plan}, ${pol.drugName}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: T.text, fontFamily: T.sans, lineHeight: 1.3 }}>{pol.plan}</div>
                  <span style={{ flexShrink: 0, fontSize: 11.5, fontWeight: 700, color: "#fff", background: colorOf(pol.drug), borderRadius: 999, padding: "3px 9px", fontFamily: T.sans }}>{pol.drugName}</span>
                </div>
                <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.sans, margin: "3px 0 10px" }}>{pol.detail}</div>
                <ol className="blog-gates-list">
                  {pol.gates.map((g, k) => (
                    <li key={k} className={g.gene ? "is-gene" : undefined}>
                      <i aria-hidden="true">{k + 1}</i>
                      <span>{g.text}{g.gene && <b className="blog-gates-badge">Gene test</b>}</span>
                    </li>
                  ))}
                </ol>
              </section>
            ))}
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "link-card": {
      // A linked card with the target page's share image (October 30, 2026,
      // Jason: blog posts should point to their FDA decision page with its
      // thumbnail). Image left on wide screens, on top on phones.
      return (
        <a key={i} href={block.href} className="blog-linkcard" style={{ display: "flex", flexWrap: "wrap", margin: "28px 0", border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden", background: T.surface, textDecoration: "none", color: "inherit" }}>
          <img src={block.image} alt={block.imageAlt} width={1200} height={630} loading="lazy" style={{ flex: "1 1 240px", maxWidth: "100%", width: 240, height: "auto", aspectRatio: "1200 / 630", objectFit: "cover", display: "block", background: T.bg }} />
          <span style={{ flex: "999 1 260px", padding: "14px 18px", fontFamily: T.sans, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.primary }}>{block.kicker}</span>
            <span style={{ fontFamily: T.serif, fontSize: 17, fontWeight: 700, color: T.text, margin: "4px 0 6px", lineHeight: 1.3 }}>{block.title}</span>
            <span style={{ fontSize: 13.5, color: T.textSecondary, lineHeight: 1.5 }}>{block.text}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.primary, marginTop: 8 }}>Open the decision page →</span>
          </span>
        </a>
      );
    }

    case "day-calendar":
      return <DayCalendar key={i} block={block} />;

    case "thermometer": {
      // A real thermometer on a linear Celsius scale (November 2, 2026). Marks
      // sit at their true temperatures, so the reader sees that the disease
      // line is a warm room, not ice. 15px per degree keeps the labels apart
      // on a phone.
      const PX = 15, TOP = 18, H = (block.maxC - block.minC) * PX;
      const yOf = (c: number) => TOP + (block.maxC - c) * PX;
      const TONE = { hot: "#B42318", warm: "#B45309", neutral: "#78716C", cold: "#1D5FA8", coldest: "#123C70" } as const;
      const merc = block.mercuryC ?? block.maxC - 3;
      const ZONE = "#2F6FB5";
      const ticks: number[] = [];
      for (let c = block.minC; c <= block.maxC; c += 5) ticks.push(c);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "22px 20px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13.5, color: T.textSecondary, fontFamily: T.sans, marginBottom: 10, lineHeight: 1.55 }}>{block.note}</div>}
          {block.zone && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px", fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, marginBottom: 12 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 3, background: "#2F6FB5", display: "inline-block" }} />{block.zone.label}</span>
              {block.zone.maybeLabel && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 3, background: "#9CC3EA", display: "inline-block" }} />{block.zone.maybeLabel}</span>}
            </div>
          )}
          <div role="img" aria-label={(block.zone ? block.zone.label + " below " + block.zone.toC + "°C. " : "") + block.marks.map((m) => `${m.temp}: ${m.label}. ${m.detail}`).join(" ")} style={{ position: "relative", height: H + TOP + 64, fontFamily: T.sans }}>
            <svg width="78" height={H + TOP + 64} style={{ position: "absolute", left: 0, top: 0 }} aria-hidden="true">
              {ticks.map((c) => (
                <g key={c}>
                  <line x1="46" x2="54" y1={yOf(c)} y2={yOf(c)} stroke="#A8A29E" strokeWidth="1" />
                  <text x="40" y={yOf(c) + 4} textAnchor="end" fontSize="11" fill={T.textMuted} style={{ fontVariantNumeric: "tabular-nums" }}>{c}°</text>
                </g>
              ))}
              <rect x="54" y={TOP - 8} width="16" height={H + 26} rx="8" fill="#F5F5F4" stroke="#D6D3D1" />
              {block.zone ? (
                <>
                  {block.zone.maybeToC !== undefined && (
                    <rect x="58" y={yOf(block.zone.maybeToC)} width="8" height={(block.zone.maybeToC - block.zone.toC) * PX} rx="4" fill="#9CC3EA" />
                  )}
                  <rect x="58" y={yOf(block.zone.toC)} width="8" height={H + TOP + 22 - yOf(block.zone.toC)} rx="4" fill={ZONE} />
                  <circle cx="62" cy={TOP + H + 30} r="14" fill={ZONE} />
                </>
              ) : (
                <>
                  <rect x="58" y={yOf(merc)} width="8" height={H + TOP + 22 - yOf(merc)} rx="4" fill={TONE.hot} opacity="0.9" />
                  <circle cx="62" cy={TOP + H + 30} r="14" fill={TONE.hot} />
                </>
              )}
              {block.marks.map((m, j) => m.cTo !== undefined ? (
                <rect key={j} x="70" y={yOf(Math.max(m.c, m.cTo))} width="6" height={Math.abs(m.cTo - m.c) * PX} rx="2" fill={TONE[m.tone]} />
              ) : (
                <rect key={j} x="70" y={yOf(m.c) - 1.5} width="8" height="3" rx="1" fill={TONE[m.tone]} />
              ))}
            </svg>
            {block.marks.map((m, j) => {
              const mid = m.cTo !== undefined ? (m.c + m.cTo) / 2 : m.c;
              return (
                <div key={j} style={{ position: "absolute", left: 88, right: 0, top: yOf(mid) - 11, borderLeft: `3px solid ${TONE[m.tone]}`, paddingLeft: 10 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: T.text, lineHeight: 1.35 }}><span style={{ color: TONE[m.tone], fontVariantNumeric: "tabular-nums" }}>{m.temp}</span> · {m.label}</div>
                  <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.45, marginTop: 2 }}>{parseInlineLinks(m.detail)}</div>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "8px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "surgery-tally": {
      // One circle per operation, filled in proportion, so an average of
      // 4.1 reads as 4 full circles and a sliver (October 30, 2026).
      const top = Math.ceil(Math.max(...block.rows.map((r) => r.value)));
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "22px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          {block.rows.map((r, j) => (
            <div key={j} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 14px", padding: "10px 0", borderTop: j ? `1px solid ${T.borderLight}` : "none", fontFamily: T.sans }}>
              <span style={{ flex: "1 1 170px", fontSize: 14, fontWeight: r.highlight ? 700 : 600, color: T.text }}>{r.label}</span>
              <span role="img" aria-label={`${r.value} surgeries a year on average`} style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
                {Array.from({ length: top }, (_, k) => {
                  const fill = Math.max(0, Math.min(1, r.value - k));
                  return <span key={k} style={{ width: 24, height: 24, borderRadius: "50%", border: `2px solid ${r.highlight ? T.primary : "#A8A29E"}`, background: `linear-gradient(90deg, ${r.highlight ? T.primary : "#A8A29E"} ${fill * 100}%, transparent ${fill * 100}%)` }} />;
                })}
              </span>
              <span style={{ flex: "0 0 48px", textAlign: "right", fontSize: 20, fontWeight: 800, color: r.highlight ? T.primary : T.text, fontVariantNumeric: "tabular-nums" }}>{r.value}</span>
            </div>
          ))}
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "price-cliff": {
      // Columns on one linear scale so the oldest price towers over the
      // newer ones, which is the finding. Dollar labels sit above each bar.
      const colorOf = (d: "a" | "b") => (d === "a" ? T.primary : PRICE_B);
      const max = Math.max(...block.items.map((it) => it.price));
      const H = 220;
      const usd = (n: number) => `$${n.toLocaleString("en-US")}`;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 12, lineHeight: 1.5 }}>{block.note}</div>}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" as const, fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, marginBottom: 10 }}>
            {(["a", "b"] as const).map((d) => (
              <span key={d} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 3, background: colorOf(d), display: "inline-block" }} />{block.legend[d]}</span>
            ))}
          </div>
          <div className="blog-cliff-row" role="list">
            {block.items.map((it, j) => (
              <div key={j} className="blog-cliff-col" role="listitem" aria-label={`${it.name}, ${it.date}: list price ${usd(it.price)} a year. ${it.tag}`}>
                <div className="blog-cliff-top" style={{ height: H + 30 }}>
                  <b style={{ color: colorOf(it.drug) }}>{usd(it.price)}</b>
                  <div className="blog-cliff-track" style={{ height: Math.max(6, (it.price / max) * H) }}>
                    <span style={{ height: "100%", background: colorOf(it.drug) }} />
                  </div>
                </div>
                <strong>{it.name}</strong>
                <small>{it.date}</small>
                <em>{it.tag}</em>
              </div>
            ))}
          </div>
          <div className="blog-cliff-dose">
            {block.perDose.map((p, j) => (
              <div key={j}>
                <div style={{ fontSize: 20, fontWeight: 800, color: colorOf(p.drug), fontFamily: T.serif, lineHeight: 1.1 }}>{p.value}</div>
                <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, marginTop: 4, lineHeight: 1.45 }}>{p.detail}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "year-ring": {
      // One dial for a year. Each ring is a medicine and each dot a dose,
      // placed at the month it falls in, so 12 and 4 read without a key.
      const colorOf = (d: "a" | "b") => (d === "a" ? T.primary : PRICE_B);
      const C = 160;
      const radii = [118, 78];
      const months = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
      const pt = (r: number, k: number, n: number) => {
        const a = (-90 + (k * 360) / n) * (Math.PI / 180);
        return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) };
      };
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 12, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-ring-wrap">
            <svg viewBox="0 0 320 320" role="img" aria-label={block.rings.map((r) => `${r.name}: ${r.doses} doses a year`).join("; ")} style={{ width: "100%", maxWidth: 300, height: "auto", display: "block" }}>
              {months.map((m, k) => {
                const p = pt(146, k, 12);
                const t1 = pt(132, k, 12);
                const t2 = pt(138, k, 12);
                return (
                  <g key={k}>
                    <line x1={t1.x} y1={t1.y} x2={t2.x} y2={t2.y} stroke="#D6D0C6" strokeWidth="1.5" />
                    <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize="12" fill={T.textMuted} fontFamily="Inter, sans-serif">{m}</text>
                  </g>
                );
              })}
              {block.rings.map((r, ri) => {
                const rad = radii[ri] ?? 60;
                return (
                  <g key={ri}>
                    <circle cx={C} cy={C} r={rad} fill="none" stroke={VIZ_COOL} strokeWidth="10" />
                    {Array.from({ length: r.doses }, (_, k) => {
                      const p = pt(rad, k, r.doses);
                      return <circle key={k} cx={p.x} cy={p.y} r={ri === 0 ? 9 : 12} fill={colorOf(r.drug)} stroke="#fff" strokeWidth="2.5" />;
                    })}
                  </g>
                );
              })}
              <text x={C} y={C - 4} textAnchor="middle" fontSize="15" fontWeight="700" fill={T.text} fontFamily="Lora, Georgia, serif">1 year</text>
              <text x={C} y={C + 15} textAnchor="middle" fontSize="12" fill={T.textMuted} fontFamily="Inter, sans-serif">{block.rings.map((r) => r.doses).join(" doses or ")} doses</text>
            </svg>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 14, flex: "1 1 220px" }}>
              {block.rings.map((r, ri) => (
                <div key={ri}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{r.name}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: colorOf(r.drug), fontFamily: T.serif, lineHeight: 1.2 }}>{r.doses} a year</div>
                  <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, marginTop: 2, lineHeight: 1.45 }}>{r.detail}</div>
                </div>
              ))}
            </div>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "catch-grid": {
      // One square per case. The reader counts filled squares the way the
      // pathologist counted fusions, and "1 of 10" needs no axis.
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="list">
            {block.rows.map((r, j) => (
              <div key={j} role="listitem" className="blog-catch-row">
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{r.label}</div>
                  {r.sub && <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.sans, marginTop: 2 }}>{r.sub}</div>}
                  <div style={{ fontSize: 22, fontWeight: 800, color: r.caught / r.total < 0.5 ? VIZ_HOT : T.primary, fontFamily: T.serif, marginTop: 6, lineHeight: 1 }}>{r.caught} of {r.total}</div>
                </div>
                <div className="blog-catch-grid" role="img" aria-label={`${r.label}: ${r.caught} of ${r.total} found by ${block.testLabel}`}>
                  {Array.from({ length: r.total }, (_, c) => (
                    <i key={c} style={{ background: c < r.caught ? T.primary : "transparent", border: `2px solid ${c < r.caught ? T.primary : VIZ_HOT}` }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 11, height: 11, borderRadius: 2, background: T.primary, display: "inline-block" }} />Found by {block.testLabel}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 11, height: 11, borderRadius: 2, border: `2px solid ${VIZ_HOT}`, boxSizing: "border-box" as const, display: "inline-block" }} />Missed, found only by RNA sequencing</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "verdict-board": {
      // Each panel: two gauges on one 0-100 scale, drug hot and placebo grey,
      // with the gap written between them. The stamp states the trial's own
      // verdict so the reader does not have to infer it from a p-value.
      const unit = block.unit ?? "%";
      const H = 150;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-verdict-grid">
            {block.trials.map((t, j) => {
              const met = t.verdict === "met";
              return (
                <div key={j} className="blog-verdict-panel" role="group" aria-label={`${t.name}: ${t.drugLabel} ${t.drug}${unit} of ${t.drugN}, placebo ${t.placebo}${unit} of ${t.placeboN}, ${met ? "met" : "missed"} its primary endpoint, p ${t.p}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{t.name}</div>
                      {t.sub && <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.sans, marginTop: 2 }}>{t.sub}</div>}
                    </div>
                    <span className="blog-verdict-stamp" style={{ color: met ? T.primaryDark : VIZ_HOT, borderColor: met ? T.primary : VIZ_HOT }}>{met ? "MET" : "MISSED"}</span>
                  </div>
                  <div className="blog-verdict-gauges" aria-hidden="true">
                    {[{ v: t.drug, n: t.drugN, l: t.drugLabel, c: VIZ_HOT }, { v: t.placebo, n: t.placeboN, l: "Placebo", c: "#A8A29E" }].map((g, k) => (
                      <div key={k} className="blog-verdict-gauge">
                        <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: T.serif, lineHeight: 1 }}>{g.v}{unit}</div>
                        <div style={{ height: H, width: 34, background: T.bg, border: `1px solid ${T.borderLight}`, borderRadius: 8, position: "relative", overflow: "hidden", margin: "6px 0" }}>
                          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: `${Math.min(100, g.v)}%`, background: g.c, borderRadius: "0 0 7px 7px" }} />
                        </div>
                        <div style={{ fontSize: 11.5, color: T.textSecondary, fontFamily: T.sans, textAlign: "center" as const, lineHeight: 1.3 }}>{g.l}<br /><span style={{ color: T.textMuted }}>n={g.n}</span></div>
                      </div>
                    ))}
                    <div className="blog-verdict-gap" style={{ height: H, marginTop: 21 }}>
                      <div style={{ position: "absolute", left: "50%", bottom: `${Math.min(100, t.placebo)}%`, height: `${Math.max(0, Math.min(100, t.drug) - Math.min(100, t.placebo))}%`, width: 2, marginLeft: -1, background: met ? T.primary : VIZ_HOT }} />
                      <div style={{ position: "absolute", left: 0, right: 0, bottom: `calc(${Math.min(100, (t.drug + t.placebo) / 2)}% - 10px)`, textAlign: "center" as const, fontSize: 12, fontWeight: 700, color: T.text, fontFamily: T.sans, background: T.surface, lineHeight: 1.2 }}>{t.gapLabel ?? `+${(t.drug - t.placebo).toFixed(1).replace(/\.0$/, "")} pts`}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.5, marginTop: 8 }}><strong style={{ color: T.text }}>p = {t.p}.</strong> {t.detail}</div>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "outcome-fork": {
      // A date node forks into outcome cards. Tone colors the card's edge:
      // teal for approval, amber for a narrower result, hot for a rejection.
      const edge = (t: string) => t === "good" ? T.primary : t === "mixed" ? "#D97706" : VIZ_HOT;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-fork">
            <div className="blog-fork-node" aria-hidden="true">
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" as const, color: T.textMuted, fontFamily: T.sans }}>{block.node.kicker ?? "Decision"}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.text, fontFamily: T.serif, lineHeight: 1.15, marginTop: 4 }}>{block.node.label}</div>
              {block.node.sub && <div style={{ fontSize: 12, color: T.textSecondary, fontFamily: T.sans, marginTop: 4, lineHeight: 1.4 }}>{block.node.sub}</div>}
            </div>
            <ol className="blog-fork-branches">
              {block.branches.map((b, j) => (
                <li key={j} className="blog-fork-card" style={PLAIN_CALLOUTS ? undefined : { borderLeftColor: edge(b.tone) }}>
                  <div style={{ fontSize: PLAIN_CALLOUTS ? 15 : 14.5, fontWeight: 700, color: PLAIN_CALLOUTS ? edge(b.tone) : T.text, fontFamily: T.sans, marginBottom: 6 }}>{b.label}</div>
                  <div style={{ fontSize: 13, color: T.text, fontFamily: T.sans, lineHeight: 1.55 }}><span style={PLAIN_CALLOUTS ? { fontSize: 13, fontWeight: 700, color: T.text, marginRight: 6 } : { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: edge(b.tone), marginRight: 6 }}>{block.meansLabel ?? "For you"}</span>{b.means}</div>
                  <div style={{ fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.5, marginTop: 6 }}><span style={PLAIN_CALLOUTS ? { fontSize: 12.5, fontWeight: 700, color: T.textSecondary, marginRight: 6 } : { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: T.textMuted, marginRight: 6 }}>{block.signsLabel ?? "Signs"}</span>{b.signs}</div>
                </li>
              ))}
            </ol>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "pedigree": {
      // Clinical pedigree symbols drawn by hand-set position, so the family
      // reads like the chart a genetic counselor sketches. Everything a reader
      // needs beyond the shape lives in the key below the tree.
      const W = block.width, RH = block.rowHeight, R = 15;
      const H = block.generations.length * RH + 4;
      const cy = (g: number) => g * RH + 56;
      const ext = (p: { sex: string }) => (p.sex === "u" ? R * 1.25 : R);
      const byId = new Map(block.people.map((p) => [p.id, p]));
      const isCouple = (a: string, b: string) => (block.couples || []).some(([x, y]) => (x === a && y === b) || (x === b && y === a));
      const hatch = `ped${i}-hatch`, split = `ped${i}-split`;
      const GREY = "#A8A29E", LINE = "#57534E";
      const STATUS: Record<string, string> = {
        affected: "Had the disease",
        carrier: "Carried the variant, inferred from her children",
        presumed: "Presumed by the family, never tested",
        split: "Some tested positive, some negative",
        negative: "Tested negative",
        unknown: "Unknown, never tested",
        none: "No known signs, not tested",
      };
      type Ped = (typeof block.people)[number];
      const shapeEl = (p: Pick<Ped, "sex">, x: number, y: number, r: number, fill: string, stroke: string, sw: number, key?: string) =>
        p.sex === "m" ? <rect key={key} x={x - r} y={y - r} width={r * 2} height={r * 2} rx={r * 0.18} fill={fill} stroke={stroke} strokeWidth={sw} />
        : p.sex === "f" ? <circle key={key} cx={x} cy={y} r={r} fill={fill} stroke={stroke} strokeWidth={sw} />
        : <polygon key={key} points={`${x},${y - r * 1.25} ${x + r * 1.25},${y} ${x},${y + r * 1.25} ${x - r * 1.25},${y}`} fill={fill} stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />;
      const node = (p: Pick<Ped, "sex" | "status" | "deceased" | "group" | "highlight">, x: number, y: number, r: number) => {
        const st = p.status;
        const fill = st === "affected" ? VIZ_HOT : st === "presumed" ? `url(#${hatch})` : st === "split" ? `url(#${split})` : T.surface;
        const stroke = st === "affected" || st === "presumed" ? VIZ_HOT : st === "negative" ? T.primary : st === "unknown" || st === "none" ? GREY : T.text;
        const sw = r < 10 ? 1.5 : 2;
        return (
          <g>
            {p.group && [8, 4].map((o) => shapeEl(p, x + o * (r / R), y - o * (r / R), r, T.bg, GREY, 1.2, `g${o}`))}
            {p.highlight && <circle cx={x} cy={y} r={r + 8} fill="none" stroke={T.primary} strokeWidth={1.6} strokeDasharray="3 3" />}
            {shapeEl(p, x, y, r, fill, stroke, sw)}
            {st === "carrier" && <circle cx={x} cy={y} r={r * 0.36} fill={VIZ_HOT} />}
            {st === "negative" && <line x1={x - r * 0.45} y1={y} x2={x + r * 0.45} y2={y} stroke={T.primary} strokeWidth={r < 10 ? 1.6 : 2.6} strokeLinecap="round" />}
            {st === "unknown" && <text x={x} y={y + r * 0.36} textAnchor="middle" fontSize={r * 1.05} fontWeight={700} fill={GREY} fontFamily={T.sans}>?</text>}
            {p.deceased && <line x1={x - r - 5 * (r / R)} y1={y + r + 5 * (r / R)} x2={x + r + 5 * (r / R)} y2={y - r - 5 * (r / R)} stroke={T.text} strokeWidth={r < 10 ? 1.4 : 2} strokeLinecap="round" />}
          </g>
        );
      };
      const mini = (p: Pick<Ped, "sex" | "status" | "deceased" | "group" | "highlight">, k: string | number) => (
        <svg key={k} width={22} height={22} viewBox="-11 -11 22 22" aria-hidden="true" style={{ flexShrink: 0, overflow: "visible" }}>{node({ ...p, highlight: false, group: false }, 0, 0, 7.5)}</svg>
      );
      const lines: ReactNode[] = [];
      (block.couples || []).forEach(([a, b], k) => {
        const pa = byId.get(a), pb = byId.get(b);
        if (!pa || !pb) return;
        const [l, r] = pa.x < pb.x ? [pa, pb] : [pb, pa];
        lines.push(<line key={`c${k}`} x1={l.x + ext(l)} y1={cy(l.gen)} x2={r.x - ext(r)} y2={cy(r.gen)} stroke={LINE} strokeWidth={1.6} />);
      });
      block.descents.forEach((d, k) => {
        const parents = d.from.map((id) => byId.get(id)).filter(Boolean) as Ped[];
        const kids = d.to.map((id) => byId.get(id)).filter(Boolean) as Ped[];
        if (!parents.length || !kids.length) return;
        const py = cy(parents[0].gen), ky = cy(kids[0].gen), barY = ky - 40;
        const xs: number[] = [];
        if (parents.length === 2 && isCouple(parents[0].id, parents[1].id)) {
          const ox = (parents[0].x + parents[1].x) / 2;
          xs.push(ox);
          lines.push(<line key={`d${k}o`} x1={ox} y1={py} x2={ox} y2={barY} stroke={LINE} strokeWidth={1.6} />);
        } else {
          parents.forEach((p, j) => { xs.push(p.x); lines.push(<line key={`d${k}p${j}`} x1={p.x} y1={py + ext(p) + (p.sub ? 42 : 26)} x2={p.x} y2={barY} stroke={LINE} strokeWidth={1.6} />); });
        }
        kids.forEach((c) => xs.push(c.x));
        const lo = Math.min(...xs), hi = Math.max(...xs);
        if (hi > lo) lines.push(<line key={`d${k}b`} x1={lo} y1={barY} x2={hi} y2={barY} stroke={LINE} strokeWidth={1.6} />);
        kids.forEach((c, j) => lines.push(<line key={`d${k}k${j}`} x1={c.x} y1={barY} x2={c.x} y2={ky - ext(c) - (c.group ? 0 : 0)} stroke={LINE} strokeWidth={1.6} />));
      });
      const used = Array.from(new Set(block.people.map((p) => p.status)));
      const order = ["affected", "carrier", "presumed", "split", "negative", "unknown", "none"];
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 12, lineHeight: 1.5 }}>{block.note}</div>}
          <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${block.title || "Family tree"}. ${block.people.map((p) => `${p.name}: ${STATUS[p.status].toLowerCase()}${p.deceased ? ", died" : ""}`).join("; ")}.`} style={{ width: "100%", maxWidth: 560, display: "block", margin: "0 auto", overflow: "visible" }}>
            <defs>
              <pattern id={hatch} width={5} height={5} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width={5} height={5} fill={T.surface} />
                <rect width={2.4} height={5} fill={VIZ_HOT} />
              </pattern>
              <linearGradient id={split} x1="0" x2="1" y1="0" y2="0">
                <stop offset="50%" stopColor={VIZ_HOT} />
                <stop offset="50%" stopColor={T.primaryLight} />
              </linearGradient>
            </defs>
            {block.generations.map((g, k) => (
              <g key={k}>
                {k % 2 === 1 && <rect x={0} y={k * RH} width={W} height={RH} fill={T.bg} rx={8} />}
                <text x={10} y={cy(k) + 6} fontSize={16} fontWeight={700} fill={T.textMuted} fontFamily={T.serif}>{g.label}</text>
                {g.era && <text x={W - 8} y={k * RH + 16} textAnchor="end" fontSize={9.5} fontWeight={700} letterSpacing="0.08em" fill={T.textMuted} fontFamily={T.sans}>{g.era.toUpperCase()}</text>}
              </g>
            ))}
            {lines}
            {block.people.map((p) => (
              <g key={p.id}>
                {node(p, p.x, cy(p.gen), R)}
                <text x={p.x} y={cy(p.gen) + ext(p) + (p.highlight ? 26 : 18)} textAnchor="middle" fontSize={16} fontWeight={700} fill={T.text} fontFamily={T.sans}>{p.name}</text>
                {p.sub && <text x={p.x} y={cy(p.gen) + ext(p) + (p.highlight ? 43 : 35)} textAnchor="middle" fontSize={13} fill={T.textSecondary} fontFamily={T.sans}>{p.sub}</text>}
              </g>
            ))}
          </svg>
          <div style={{ display: "flex", flexWrap: "wrap" as const, gap: "6px 16px", marginTop: 12, fontSize: 12, color: T.textMuted, fontFamily: T.sans }} aria-hidden="true">
            {order.filter((s) => used.includes(s as Ped["status"])).map((s) => (
              <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{mini({ sex: "f", status: s as Ped["status"] }, s)}{STATUS[s]}</span>
            ))}
            {block.people.some((p) => p.deceased) && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{mini({ sex: "m", status: "none", deceased: true }, "dec")}Died</span>}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{mini({ sex: "m", status: "none" }, "m")}Man</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{mini({ sex: "f", status: "none" }, "f")}Woman</span>
            {block.people.some((p) => p.sex === "u") && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{mini({ sex: "u", status: "none" }, "u")}Not specified or a group</span>}
          </div>
          <ul className="blog-ped-key">
            {block.people.filter((p) => p.detail).map((p) => (
              <li key={p.id}>{mini(p, p.id)}<span><b>{p.name}.</b> {p.detail}</span></li>
            ))}
          </ul>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "rewind": {
      // The diagnosis is a wall on the right; everything else is measured in
      // years before it. Bands are 1 spread either side of the average, cut
      // off at the wall, so a reader sees how early the body started talking.
      const pos = (yrs: number) => 100 - (Math.min(block.max, Math.max(0, yrs)) / block.max) * 100;
      const ticks: number[] = [];
      for (let t = block.max; t >= 0; t -= block.step) ticks.push(t);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-rewind-axis" aria-hidden="true">
            <div />
            <div style={{ position: "relative", height: 18 }}>
              {ticks.map((t) => (
                <span key={t} style={{ position: "absolute", left: `${pos(t)}%`, transform: t === block.max ? "none" : t === 0 ? "translateX(-100%)" : "translateX(-50%)", fontSize: 11, fontWeight: t === 0 ? 800 : 500, color: t === 0 ? T.text : T.textMuted, fontFamily: T.sans, whiteSpace: "nowrap" as const, letterSpacing: t === 0 ? "0.04em" : undefined, textTransform: t === 0 ? ("uppercase" as const) : undefined }}>
                  {t === 0 ? block.anchorLabel : t === block.max ? `${t} yrs` : `${t}`}
                </span>
              ))}
            </div>
          </div>
          <div>
            {block.rows.map((r, j) => (
              <div key={j} className="blog-rewind-row" role="img" aria-label={`${r.label}: on average ${r.mean} years before diagnosis${r.share ? `, in ${r.share}` : ""}`}>
                <div className="blog-rewind-label">
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontFamily: T.sans, lineHeight: 1.3 }}>{r.label}</span>
                  {r.share && <span style={{ fontSize: 11.5, color: T.textMuted, fontFamily: T.sans, lineHeight: 1.3 }}>{r.share}</span>}
                </div>
                <div className="blog-rewind-track">
                  {ticks.map((t) => <i key={t} aria-hidden="true" style={{ position: "absolute", left: `${pos(t)}%`, top: 4, bottom: 4, width: 1, background: T.borderLight }} />)}
                  <i aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, top: "50%", height: 1, background: T.border }} />
                  {r.low != null && r.high != null && (
                    <span aria-hidden="true" style={{ position: "absolute", left: `${pos(r.high)}%`, width: `${Math.max(0, pos(r.low) - pos(r.high))}%`, top: "50%", height: 12, marginTop: -6, borderRadius: 6, background: `linear-gradient(90deg, rgba(194,65,12,0.06), rgba(194,65,12,0.32))` }} />
                  )}
                  <span aria-hidden="true" style={{ position: "absolute", left: `${pos(r.mean)}%`, top: "50%", width: 14, height: 14, marginLeft: -7, marginTop: -7, borderRadius: "50%", background: VIZ_HOT, boxShadow: `0 0 0 3px ${T.surface}, 0 1px 4px rgba(0,0,0,0.25)` }} />
                  <b aria-hidden="true" style={{ position: "absolute", left: `${pos(r.mean)}%`, top: -3, transform: "translateX(-50%)", fontSize: 11.5, fontWeight: 800, color: VIZ_HOT, fontFamily: T.sans, whiteSpace: "nowrap" as const, background: T.surface, padding: "0 3px", lineHeight: 1.2 }}>{r.mean} yrs</b>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "gene-strip": {
      // Protein positions drawn to scale inside each exon; the stretches
      // between exons are not to scale. A pin's head area is its share of
      // positive tests, so the one common variant dominates the way it does
      // in a testing program's results.
      const W = 460, X0 = 26, X1 = 444, GAP = 16, EY = 128, EH = 28;
      const len = (e: { from: number; to: number }) => e.to - e.from + 1;
      const total = block.exons.reduce((a, e) => a + len(e), 0);
      const k = (X1 - X0 - GAP * (block.exons.length - 1)) / total;
      const starts: number[] = [];
      block.exons.reduce((x, e) => { starts.push(x); return x + len(e) * k + GAP; }, X0);
      const xOf = (aa: number) => {
        const j = block.exons.findIndex((e) => aa >= e.from && aa <= e.to);
        if (j < 0) return X0;
        return starts[j] + (aa - block.exons[j].from + 0.5) * k;
      };
      const maxShare = Math.max(...block.variants.map((v) => v.share || 0), 1);
      const rOf = (v: (typeof block.variants)[number]) => (v.benign || !v.share ? 5 : Math.max(4.5, 24 * Math.sqrt(v.share / maxShare)));
      const range = (e: { from: number; to: number }) => (e.from < 1 ? `signal, 1 to ${e.to}` : `${e.from} to ${e.to}`);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 10, lineHeight: 1.5 }}>{block.note}</div>}
          <svg viewBox={`0 0 ${W} 186`} role="img" aria-label={`${block.gene} gene with ${block.exons.length} exons. ${block.variants.map((v) => `${v.name} (${v.alias}) at position ${v.at}, ${v.shareLabel}`).join("; ")}.`} style={{ width: "100%", maxWidth: 620, display: "block", margin: "0 auto", overflow: "visible" }}>
            {block.exons.slice(1).map((e, j) => {
              const a = starts[j] + len(block.exons[j]) * k, b = starts[j + 1];
              const m = (a + b) / 2;
              return <polyline key={`in${j}`} points={`${a},${EY + EH / 2} ${m - 4},${EY + EH / 2} ${m - 1},${EY + EH / 2 - 5} ${m + 2},${EY + EH / 2 + 5} ${m + 5},${EY + EH / 2} ${b},${EY + EH / 2}`} fill="none" stroke="#A8A29E" strokeWidth={1.5} strokeLinejoin="round" />;
            })}
            {block.exons.map((e, j) => <rect key={`exr${j}`} x={starts[j]} y={EY} width={len(e) * k} height={EH} rx={5} fill={T.primaryDark} />)}
            {block.lead && (() => {
              const a = xOf(block.lead.from) - 0.5 * k, b = xOf(block.lead.to) + 0.5 * k;
              return <rect x={a} y={EY} width={b - a} height={EH} rx={5} fill={T.primary} />;
            })()}
            {block.exons.map((e, j) => (
              <g key={`ex${j}`}>
                <text x={starts[j] + (len(e) * k) / 2} y={EY + 19} textAnchor="middle" fontSize={13} fontWeight={700} fill="#fff" fontFamily={T.sans}>{e.label}</text>
                <text x={starts[j] + (len(e) * k) / 2} y={EY + EH + 17} textAnchor="middle" fontSize={12} fill={T.textMuted} fontFamily={T.sans}>{range(e)}</text>
              </g>
            ))}
            {block.variants.map((v, j) => {
              const x = xOf(v.at), r = rOf(v), top = EY - (24 + (v.lift ?? 0.4) * 70);
              const left = v.side === "left";
              const col = v.benign ? "#A8A29E" : VIZ_HOT;
              const big = r >= 16;
              return (
                <g key={`v${j}`}>
                  <line x1={x} y1={EY} x2={x} y2={top + r} stroke={col} strokeWidth={1.6} />
                  <circle cx={x} cy={top} r={r} fill={col} stroke={T.surface} strokeWidth={2} />
                  {big && v.share != null && <text x={x} y={top + 5} textAnchor="middle" fontSize={14} fontWeight={800} fill="#fff" fontFamily={T.sans}>{Math.round(v.share)}%</text>}
                  <text x={left ? x - r - 6 : x + r + 6} y={top - 1} textAnchor={left ? "end" : "start"} fontSize={15} fontWeight={800} fill={T.text} fontFamily={T.sans}>{v.name}</text>
                  <text x={left ? x - r - 6 : x + r + 6} y={top + 17} textAnchor={left ? "end" : "start"} fontSize={12.5} fill={T.textSecondary} fontFamily={T.sans}>{big ? "most common" : v.benign ? "harmless" : `${v.share}%`}</text>
                </g>
              );
            })}
          </svg>
          <div className="blog-gene-cards">
            {block.variants.map((v, j) => (
              <div key={j} className="blog-gene-card" style={{ borderTopColor: v.benign ? "#A8A29E" : VIZ_HOT }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" as const }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: T.serif }}>{v.name}</span>
                  <span style={{ fontSize: 11.5, color: T.textMuted, fontFamily: T.sans }}>also {v.alias}</span>
                </div>
                <div className="blog-gene-line"><span>Found in</span>{v.origin}</div>
                <div className="blog-gene-line"><span>Mostly affects</span>{v.affects}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: v.benign ? T.textMuted : VIZ_HOT, fontFamily: T.sans, marginTop: 8 }}>{v.shareLabel}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "time-lanes": {
      // Position is proportional to calendar time. Dots carry a number and the
      // words live in a key under each lane, so nothing overlaps or clips no
      // matter how crowded the right edge gets.
      const ym = (d: string) => { const [y, m, day] = d.split("-").map(Number); return y + ((m || 1) - 1) / 12 + ((day || 1) - 1) / 365; };
      const t0 = ym(block.start), t1 = ym(block.end);
      const x = (d: string) => Math.max(0, Math.min(100, ((ym(d) - t0) / (t1 - t0)) * 100));
      const years: number[] = []; for (let y = Math.ceil(t0); y <= Math.floor(t1); y++) years.push(y);
      // Spans of about a year or less get month ticks (the title carries the
      // year), and a label near either edge is anchored inward so it never clips.
      const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const ticks: { at: string; label: string }[] = [];
      if (t1 - t0 <= 1.5) {
        let [ty, tm] = block.start.split("-").map(Number);
        const [ey, em] = block.end.split("-").map(Number);
        while (ty < ey || (ty === ey && tm <= em)) {
          ticks.push({ at: `${ty}-${String(tm).padStart(2, "0")}`, label: MON[tm - 1] });
          tm++; if (tm > 12) { tm = 1; ty++; }
        }
      } else {
        for (const y of years) ticks.push({ at: `${y}-01`, label: String(y) });
      }
      const tickShift = (pos: number) => (pos < 6 ? "translateX(0)" : pos > 94 ? "translateX(-100%)" : "translateX(-50%)");
      const kindStyle = (k: string) => k === "approval" ? { bg: T.primary, ring: T.primary, fg: "#fff" } : k === "decision" ? { bg: T.surface, ring: VIZ_HOT, fg: VIZ_HOT } : k === "setback" ? { bg: VIZ_HOT, ring: VIZ_HOT, fg: "#fff" } : { bg: "#78716C", ring: "#78716C", fg: "#fff" };
      const fmt = (d: string) => new Date(d.length === 7 ? d + "-01T12:00:00Z" : d + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
      let n = 0;
      const numbered = block.lanes.map((ln) => ({ ...ln, points: [...ln.points].sort((a, b) => ym(a.date) - ym(b.date)).map((pt) => ({ ...pt, n: ++n })) }));
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-scroll-hint" aria-hidden="true">Swipe sideways to see the whole chart</div>
          <div style={{ overflowX: "auto" as const, WebkitOverflowScrolling: "touch" as const }}>
            <div style={{ minWidth: 560, position: "relative", paddingTop: 18 }}>
              <div className="blog-lanes-axis" aria-hidden="true" style={{ marginTop: 10 }}>
                <div />
                <div style={{ position: "relative", height: 16 }}>
                  {ticks.map((tk) => <span key={tk.at} style={{ position: "absolute", left: `${x(tk.at)}%`, transform: tickShift(x(tk.at)), fontSize: 11, color: T.textMuted, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>{tk.label}</span>)}
                  {block.today && <span style={{ position: "absolute", left: `${x(block.today)}%`, top: -17, transform: "translateX(-50%)", fontSize: 10.5, fontWeight: 700, color: VIZ_HOT, fontFamily: T.sans, letterSpacing: "0.06em", textTransform: "uppercase" as const, whiteSpace: "nowrap" as const }}>Today</span>}
                </div>
              </div>
              <div style={{ position: "relative" }}>
                {numbered.map((ln, j) => (
                  <div key={j} className="blog-lane" role="group" aria-label={`${ln.label}: ${[...(ln.spans || []).map((sp) => `${sp.label}, ${fmt(sp.from)} to ${fmt(sp.to)}`), ...ln.points.map((pt) => `${pt.label}, ${fmt(pt.date)}`)].join("; ")}`}>
                    <div className="blog-lane-label">
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{ln.label}</div>
                      {ln.sub && <div style={{ fontSize: 11, color: T.textMuted, fontFamily: T.sans }}>{ln.sub}</div>}
                    </div>
                    <div className="blog-lane-track" style={{ height: 30 + (ln.spans?.length || 0) * 16 + (ln.points.some((pt, k) => k > 0 && x(pt.date) - x(ln.points[k - 1].date) < 4.5) ? 18 : 0) }}>
                      {ticks.map((tk) => <span key={tk.at} aria-hidden="true" style={{ position: "absolute", left: `${x(tk.at)}%`, top: 0, bottom: 0, width: 1, background: T.borderLight }} />)}
                      {block.today && <span aria-hidden="true" style={{ position: "absolute", left: `${x(block.today)}%`, top: -6, bottom: -6, borderLeft: `2px dashed ${VIZ_HOT}`, marginLeft: -1 }} />}
                      {(ln.spans || []).map((sp, k) => {
                        const w = Math.max(1.5, x(sp.to) - x(sp.from));
                        return (
                          <div key={k} className="blog-lane-span" style={{ left: `${x(sp.from)}%`, width: `${w}%`, top: 4 + k * 16 }} title={`${sp.label}: ${fmt(sp.from)} to ${fmt(sp.to)}`}>
                            {w > 24 && <span>{sp.label}</span>}
                          </div>
                        );
                      })}
                      {ln.points.map((pt, k) => {
                        const st = kindStyle(pt.kind);
                        const prev = k > 0 ? ln.points[k - 1] : null;
                        const crowded = prev ? x(pt.date) - x(prev.date) < 4.5 : false;
                        return (
                          <div key={k} className="blog-lane-point" style={{ left: `${x(pt.date)}%`, top: 8 + (ln.spans?.length || 0) * 16 + (crowded ? 18 : 0) }} title={`${pt.label}, ${fmt(pt.date)}`}>
                            <i style={{ background: st.bg, borderColor: st.ring, color: st.fg, borderWidth: pt.kind === "decision" ? 3 : 2 }}>{pt.n}</i>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="blog-lanes-keys">
            {numbered.map((ln, j) => (
              <div key={j}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: T.textMuted, fontFamily: T.sans, margin: "10px 0 4px" }}>{ln.label}</div>
                <ol className="blog-lane-key">
                  {(ln.spans || []).filter((sp) => (x(sp.to) - x(sp.from)) <= 24).map((sp, k) => <li key={`s${k}`}><i aria-hidden="true" className="blog-lane-key-span" /><span><b>{fmt(sp.from)} to {fmt(sp.to)}</b> {sp.label}</span></li>)}
                  {ln.points.map((pt, k) => { const st = kindStyle(pt.kind); return <li key={k}><i aria-hidden="true" style={{ background: st.bg, borderColor: st.ring, color: st.fg }}>{pt.n}</i><span><b>{fmt(pt.date)}</b> {pt.label}</span></li>; })}
                </ol>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            {([["approval", "FDA approval"], ["decision", "FDA decision due"], ["setback", "Setback or mixed result"], ["event", "Filing or label change"]] as const).map(([k, l]) => { const st = kindStyle(k); return <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 11, height: 11, borderRadius: "50%", background: st.bg, border: `2px solid ${st.ring}`, boxSizing: "border-box" as const, display: "inline-block" }} />{l}</span>; })}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 18, height: 8, borderRadius: 3, background: T.primaryLight, border: `1px solid ${T.primary}`, display: "inline-block" }} />Trial, first patient to results</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "zone-climb": {
      // Dystrophin "how much protein" ladder. The track is logarithmic so
      // 0.4% and 40% both fit without one being a sliver, and the shaded
      // zones are the clinical bands from the literature (Duchenne, severe
      // Becker, milder Becker, no weakness). Each row is a drug: a hollow
      // dot where patients started, a solid dot where they ended up, and a
      // thin rail between. Rows without a published start show only the
      // solid dot.
      const lo = Math.log10(block.min); const hi = Math.log10(block.max);
      const pos = (v: number) => Math.max(0, Math.min(100, ((Math.log10(Math.max(v, block.min)) - lo) / (hi - lo)) * 100));
      const rungs = [0.1, 1, 10, 100].filter((r) => r >= block.min && r <= block.max);
      const tone = (t?: string) => t === "pending" ? VIZ_HOT : t === "ghost" ? "#B8B2A7" : T.primary;
      const zoneFill = ["rgba(194,65,12,0.07)", "rgba(217,119,6,0.08)", "rgba(15,118,110,0.07)", "rgba(15,118,110,0.14)"];
      const fmt = (v: number) => v >= 10 ? `${Math.round(v)}%` : `${parseFloat(v.toFixed(2))}%`;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-dot-row blog-zone-head" aria-hidden="true" style={{ borderTop: "none", padding: 0 }}>
            <div className="blog-dot-label" />
            <div className="blog-zone-track" style={{ position: "relative", height: 34 }}>
              {block.zones.map((z, k) => {
                const l = pos(z.from); const r = pos(z.to);
                return (
                  <div key={k} style={{ position: "absolute", left: `${l}%`, width: `${r - l}%`, top: 0, bottom: 0, borderLeft: `1px dashed ${T.border}`, padding: "0 3px", display: "flex", alignItems: "flex-end" }}>
                    <div className="blog-zone-head-label" style={{ fontSize: 10.5, fontWeight: 700, color: T.text, fontFamily: T.sans, lineHeight: 1.15, paddingBottom: 4 }}>{z.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div role="list">
            {block.rows.map((r, j) => {
              const ax = pos(r.after);
              const bx = r.before !== undefined ? pos(r.before) : null;
              const c = tone(r.tone);
              const label = r.href ? <Link href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</Link> : r.label;
              const valueText = `${r.afterLabel ?? ""}${fmt(r.after)}`;
              const aria = `${r.label}: ${bx !== null ? `from ${fmt(r.before as number)} to ` : ""}${valueText}${r.n ? `, ${r.n} patients` : ""}`;
              const flip = ax > 70;
              return (
                <div key={j} role="listitem" className="blog-dot-row">
                  <div className="blog-dot-label">{label}{r.sub && <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>{r.sub}</div>}</div>
                  <div className="blog-zone-track" style={{ position: "relative", height: 38 }} aria-label={aria}>
                    {block.zones.map((z, k) => <span key={k} aria-hidden="true" style={{ position: "absolute", left: `${pos(z.from)}%`, width: `${pos(z.to) - pos(z.from)}%`, top: 0, bottom: 0, background: zoneFill[k % zoneFill.length], borderLeft: `1px dashed ${T.border}` }} />)}
                    {rungs.map((t) => <span key={t} aria-hidden="true" style={{ position: "absolute", left: `${pos(t)}%`, top: 0, bottom: 0, width: 1, background: T.borderLight }} />)}
                    {bx !== null && <span aria-hidden="true" style={{ position: "absolute", left: `${Math.min(bx, ax)}%`, width: `${Math.abs(ax - bx)}%`, top: "50%", height: 3, marginTop: -1.5, background: c, opacity: 0.45, borderRadius: 2 }} />}
                    {bx !== null && <span aria-hidden="true" style={{ position: "absolute", left: `${bx}%`, top: "50%", width: 12, height: 12, marginLeft: -6, marginTop: -6, borderRadius: "50%", background: T.surface, border: `2px solid ${c}`, opacity: 0.8 }} />}
                    <span aria-hidden="true" style={{ position: "absolute", left: `${ax}%`, top: "50%", width: 17, height: 17, marginLeft: -8.5, marginTop: -8.5, borderRadius: "50%", background: c, boxShadow: `0 0 0 2.5px ${T.surface}` }} />
                    <span style={{ position: "absolute", left: flip ? "auto" : `calc(${ax}% + 14px)`, right: flip ? `calc(${100 - ax}% + 14px)` : "auto", top: "50%", transform: "translateY(-50%)", fontSize: 12.5, fontWeight: 700, color: T.text, fontFamily: T.sans, whiteSpace: "nowrap" as const, textAlign: flip ? "right" as const : "left" as const }}>
                      {valueText}{r.n ? <span style={{ fontWeight: 500, color: T.textMuted }}> ({r.n})</span> : null}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="blog-dot-row" aria-hidden="true" style={{ borderTop: "none", paddingTop: 2 }}>
            <div className="blog-dot-label" />
            <div className="blog-zone-track" style={{ position: "relative", height: 16 }}>
              {rungs.map((t) => <span key={t} style={{ position: "absolute", left: `${pos(t)}%`, transform: "translateX(-50%)", fontSize: 11, color: T.textMuted, fontFamily: T.sans }}>{t >= 1 ? `${t}%` : `${t}%`}</span>)}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "6px 12px", marginTop: 12 }}>
            {block.zones.map((z, k) => (
              <div key={k} style={{ display: "flex", gap: 7, alignItems: "flex-start", fontSize: 11.5, fontFamily: T.sans, lineHeight: 1.3 }}>
                <i aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 3, background: zoneFill[k % zoneFill.length], border: `1px solid ${T.border}`, display: "inline-block", marginTop: 2, flexShrink: 0 }} />
                <span><strong style={{ color: T.text }}>{z.label}</strong>{z.sub && <span style={{ color: T.textMuted, display: "block" }}>{z.sub}</span>}</span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 10, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 11, height: 11, borderRadius: "50%", border: `2px solid ${T.textMuted}`, display: "inline-block", boxSizing: "border-box" as const }} />Before treatment</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 12, height: 12, borderRadius: "50%", background: T.primary, display: "inline-block" }} />Approved drug</span>
            {block.rows.some((r) => r.tone === "pending") && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 12, height: 12, borderRadius: "50%", background: VIZ_HOT, display: "inline-block" }} />Under FDA review</span>}
            <span>Scale is logarithmic: each rung is 10 times the last.</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "exon-map": {
      // The dystrophin gene drawn as a ribbon of numbered blocks, one per
      // exon. Shaded runs are where deletions cluster; filled blocks are the
      // exons a drug skips, labeled above the ribbon. Under it, a single bar
      // divides all Duchenne mutations by which skip would help, so the
      // reader sees the gene and the patient population in one glance.
      const n = block.exons;
      const inHot = (e: number) => block.hotspots.some((h) => e >= h.from && e <= h.to);
      const targetFor = (e: number) => block.targets.find((t) => t.exon === e);
      const tone = (t?: string) => t === "pending" ? VIZ_HOT : t === "both" ? "#7C3AED" : T.primary;
      const sorted = [...block.targets].sort((a, b) => a.exon - b.exon);
      const levels: Record<number, number> = {};
      sorted.forEach((t, k) => { levels[t.exon] = k > 0 && t.exon - sorted[k - 1].exon <= 3 ? (levels[sorted[k - 1].exon] + 1) % 2 : 0; });
      const covered = block.targets.reduce((s, t) => s + t.shareValue, 0);
      const x = (e: number) => ((e - 0.5) / n) * 100;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 18, lineHeight: 1.5 }}>{block.note}</div>}
          <div style={{ position: "relative", height: 46 }} aria-hidden="true">
            {sorted.map((t) => (
              <div key={t.exon} style={{ position: "absolute", left: `${x(t.exon)}%`, bottom: levels[t.exon] === 0 ? 0 : 24, transform: "translateX(-50%)", textAlign: "center" as const }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: tone(t.tone), fontFamily: T.sans, whiteSpace: "nowrap" as const, lineHeight: 1.1 }}><span className="blog-exon-word">Exon </span>{t.exon}</div>
                <div style={{ width: 1, height: levels[t.exon] === 0 ? 8 : 32, background: tone(t.tone), margin: "2px auto 0", opacity: 0.7 }} />
              </div>
            ))}
          </div>
          <div role="img" aria-label={`The dystrophin gene's ${n} exons in order. Deletions cluster in ${block.hotspots.map((h) => `${h.label} (exons ${h.from} to ${h.to})`).join(" and ")}. Skipped exons: ${sorted.map((t) => `exon ${t.exon} (${t.drugs})`).join(", ")}.`} style={{ display: "grid", gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, gap: 1.5, alignItems: "end" }}>
            {Array.from({ length: n }, (_, k) => k + 1).map((e) => {
              const t = targetFor(e);
              return <span key={e} style={{ display: "block", height: t ? 30 : 22, borderRadius: 2, background: t ? tone(t.tone) : inHot(e) ? "rgba(217,119,6,0.32)" : VIZ_COOL, boxShadow: t ? `0 0 0 1.5px ${T.surface}` : undefined }} />;
            })}
          </div>
          <div style={{ position: "relative", height: 16, marginTop: 4 }} aria-hidden="true">
            {[1, 10, 20, 30, 40, 50, 60, 70, n].map((e) => <span key={e} style={{ position: "absolute", left: `${x(e)}%`, transform: "translateX(-50%)", fontSize: 10.5, color: T.textMuted, fontFamily: T.sans }}>{e}</span>)}
          </div>
          <div style={{ position: "relative", height: 18, marginTop: 2 }} aria-hidden="true">
            {block.hotspots.map((h, k) => (
              <div key={k} style={{ position: "absolute", left: `${((h.from - 1) / n) * 100}%`, width: `${((h.to - h.from + 1) / n) * 100}%`, top: 0, borderTop: `2px solid rgba(217,119,6,0.7)`, textAlign: "center" as const, fontSize: 10.5, color: "#92400E", fontFamily: T.sans, paddingTop: 2, whiteSpace: "nowrap" as const, overflow: "visible" }}>{h.label}</div>
            ))}
          </div>
          <div style={{ marginTop: 26, fontSize: 12.5, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{block.shareTitle}</div>
          <div role="img" aria-label={`${block.shareTitle}: ${sorted.map((t) => `exon ${t.exon} ${t.share}`).join(", ")}; all other mutations ${(100 - covered).toFixed(0)}%.`} style={{ display: "flex", height: 28, marginTop: 8, borderRadius: 6, overflow: "hidden", border: `1px solid ${T.border}` }}>
            {sorted.map((t) => <span key={t.exon} style={{ width: `${t.shareValue}%`, background: tone(t.tone), boxShadow: `inset -1.5px 0 0 ${T.surface}`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: T.sans, whiteSpace: "nowrap" as const, overflow: "hidden" }}>{t.exon}</span>)}
            <span style={{ flex: 1, background: VIZ_COOL, display: "flex", alignItems: "center", paddingLeft: 10, color: T.textMuted, fontSize: 11.5, fontFamily: T.sans, whiteSpace: "nowrap" as const, overflow: "hidden" }}>{block.restLabel}</span>
          </div>
          <div className="blog-exon-legend" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "8px 14px", marginTop: 12 }}>
            {sorted.map((t) => (
              <div key={t.exon} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, fontFamily: T.sans, lineHeight: 1.35 }}>
                <i aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: tone(t.tone), display: "inline-block", marginTop: 3, flexShrink: 0 }} />
                <span><strong style={{ color: T.text }}>Exon {t.exon}, {t.share}</strong><span style={{ color: T.textMuted, display: "block" }}>{t.drugs}</span></span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "forest-plot": {
      // Journal-style forest plot. The x axis is the difference from the
      // comparator in percentage points; 0 is no difference. A whisker that
      // crosses 0 means the trial could not rule out no benefit.
      const span = block.max - block.min;
      const X = (v: number) => Math.max(0, Math.min(100, ((v - block.min) / span) * 100));
      const unit = block.unit ?? "";
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "22px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="list">
            {block.rows.map((r, j) => {
              const label = r.href ? <Link href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</Link> : r.label;
              const size = Math.max(9, Math.min(18, r.weight ?? 12));
              const col = r.muted ? "#8A8478" : VIZ_HOT;
              return (
                <div key={j} role="listitem" className="blog-dot-row blog-forest-row">
                  <div className="blog-dot-label">{label}{r.sub && <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>{r.sub}</div>}</div>
                  <div className="blog-forest-cell">
                    <div className="blog-dot-track" style={{ height: 34 }} aria-label={`${r.label}: ${r.text}`}>
                      {block.ticks.map((t) => <span key={t} aria-hidden="true" style={{ position: "absolute", left: `${X(t)}%`, top: 0, bottom: 0, width: 1, background: T.borderLight }} />)}
                      <span aria-hidden="true" style={{ position: "absolute", left: `${X(0)}%`, top: -4, bottom: -4, width: 0, borderLeft: `1.5px dashed ${T.textMuted}` }} />
                      <span aria-hidden="true" style={{ position: "absolute", left: `${X(r.lo)}%`, width: `${X(r.hi) - X(r.lo)}%`, top: "50%", height: 2, marginTop: -1, background: col }} />
                      <span aria-hidden="true" style={{ position: "absolute", left: `${X(r.lo)}%`, top: "50%", height: 12, marginTop: -6, width: 2, marginLeft: -1, background: col }} />
                      <span aria-hidden="true" style={{ position: "absolute", left: `${X(r.hi)}%`, top: "50%", height: 12, marginTop: -6, width: 2, marginLeft: -1, background: col }} />
                      <span aria-hidden="true" style={{ position: "absolute", left: `${X(r.est)}%`, top: "50%", width: size, height: size, marginLeft: -size / 2, marginTop: -size / 2, background: col, boxShadow: `0 0 0 2px ${T.surface}` }} />
                    </div>
                    <div className="blog-forest-text">{r.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="blog-dot-row" aria-hidden="true" style={{ borderTop: "none", paddingTop: 2 }}>
            <div className="blog-dot-label" />
            <div className="blog-forest-cell">
              <div>
              <div style={{ position: "relative", height: 16 }}>
                {block.ticks.map((t) => <span key={t} style={{ position: "absolute", left: `${X(t)}%`, transform: "translateX(-50%)", fontSize: 11, color: T.textMuted, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>{t > 0 ? "+" : ""}{t}{unit}</span>)}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" as const, gap: "2px 12px", marginTop: 6, fontSize: 11.5, fontFamily: T.sans, color: T.textSecondary }}>
                <span>&larr; {block.leftLabel}</span>
                <span style={{ fontWeight: 600 }}>{block.rightLabel} &rarr;</span>
              </div>
              </div>
              <div className="blog-forest-text" />
            </div>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "life-timeline": {
      // One proportional column (October 24, 2026). Time runs down a ruler:
      // each gap between events is drawn with a height proportional to the
      // years that passed (faster after the optional break year), so empty
      // decades read as empty space. The spine is solid across the person's
      // lifetime and dashed before birth and after today. Year and age sit
      // on the left; each event is a sentence beside its own marker, so
      // there is no separate key to cross-reference.
      const ym = (d: string) => { const [y, m] = d.split("-").map(Number); return y + (m ? (m - 1) / 12 : 0.5); };
      const [bornY, bornM] = block.born.split("-").map(Number);
      const born = ym(block.born);
      const nowT = ym(block.now);
      const brk = block.breakYear ?? null;
      const PX_OLD = 6, PX_NEW = 18, GAP_MIN = 6, GAP_MAX = 120;
      const gapPx = (t0: number, t1: number) => {
        let px = 0;
        if (brk == null) px = (t1 - t0) * PX_NEW;
        else px = Math.max(0, Math.min(t1, brk) - t0) * PX_OLD + Math.max(0, t1 - Math.max(t0, brk)) * PX_NEW;
        return Math.round(Math.max(GAP_MIN, Math.min(GAP_MAX, px)));
      };
      const ageAt = (d: string) => { const [y, m] = d.split("-").map(Number); return y - bornY - (m && m < (bornM || 1) ? 1 : 0); };
      const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      type Item = { t: number; date: string; today?: boolean; e?: (typeof block.events)[number] };
      const items: Item[] = [...block.events].map((e) => ({ t: ym(e.date), date: e.date, e }));
      items.push({ t: nowT, date: block.now, today: true });
      items.sort((x, y) => x.t - y.t);
      const ink = T.text, faint = "#B8B2AA";
      const lineFor = (t0: number, t1: number) => (t1 <= born || t0 >= nowT) ? `1.5px dashed ${faint}` : `2px solid ${ink}`;
      const COLS = "78px 22px minmax(0, 1fr)";
      return (
        <figure key={i} style={{ margin: "34px 0", padding: "24px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 17, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {items.map((it, j) => {
              const prev = j > 0 ? items[j - 1] : null;
              const gap = prev ? gapPx(prev.t, it.t) : 0;
              const ticks: number[] = [];
              if (prev) for (let y = Math.floor(prev.t / 5) * 5 + 5; y < it.t; y += 5) if (y > prev.t + 1.5 && y < it.t - 1.5) ticks.push(y);
              const [yy, mm] = it.date.split("-").map(Number);
              const age = ageAt(it.date);
              const rowLine = lineFor(it.t - 0.01, it.t + 0.01);
              const isFuture = it.t > nowT;
              return (
                <li key={j} style={{ listStyle: "none" }}>
                  {prev && (
                    <div aria-hidden="true" style={{ display: "grid", gridTemplateColumns: COLS, height: gap }}>
                      <div style={{ position: "relative" }}>
                        {gap >= 30 && ticks.map((y) => (
                          <span key={y} style={{ position: "absolute", right: 8, top: `${((y - prev.t) / (it.t - prev.t)) * 100}%`, transform: "translateY(-50%)", fontSize: 10, color: T.textMuted, fontFamily: T.sans }}>{y}</span>
                        ))}
                      </div>
                      <div style={{ position: "relative" }}>
                        <span style={{ position: "absolute", left: 10, top: 0, bottom: 0, borderLeft: lineFor(prev.t, it.t) }} />
                        {gap >= 30 && ticks.map((y) => (
                          <span key={y} style={{ position: "absolute", left: 6, width: 9, top: `${((y - prev.t) / (it.t - prev.t)) * 100}%`, borderTop: `1px solid ${T.textMuted}` }} />
                        ))}
                      </div>
                      <div />
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: COLS, alignItems: "start" }}>
                    <div style={{ textAlign: "right" as const, paddingRight: 8, fontFamily: T.sans, lineHeight: 1.25, paddingTop: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: it.today ? T.textMuted : T.text, whiteSpace: "nowrap" as const }}>{mm ? `${MONTHS[mm - 1]} ${yy}` : yy}</div>
                      <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic" as const, whiteSpace: "nowrap" as const }}>
                        {it.e?.estimated ? "estimate" : age < 0 ? "before birth" : `age ${age}`}
                      </div>
                    </div>
                    <div style={{ position: "relative", alignSelf: "stretch", minHeight: 22 }}>
                      <span aria-hidden="true" style={{ position: "absolute", left: 10, top: 0, bottom: 0, borderLeft: it.today ? `2px solid ${ink}` : rowLine }} />
                      {it.today ? (
                        <span aria-hidden="true" style={{ position: "absolute", left: 3, width: 16, top: 8, borderTop: `2px solid ${ink}` }} />
                      ) : (
                        <span aria-hidden="true" style={{ position: "absolute", left: 5, top: 4, width: 12, height: 12, borderRadius: 999, boxSizing: "border-box" as const, background: it.e?.estimated ? T.surface : it.e?.kind === "approval" ? T.primary : ink, border: `2px ${it.e?.estimated ? "dashed" : "solid"} ${it.e?.kind === "approval" ? T.primary : ink}` }} />
                      )}
                    </div>
                    <div style={{ fontFamily: T.sans, fontSize: 14, lineHeight: 1.55, color: it.today ? T.textMuted : isFuture ? T.textSecondary : T.text, fontStyle: it.today ? ("italic" as const) : undefined, paddingTop: 0, paddingBottom: 2 }}>
                      {it.today ? `Today, ${block.person} is ${age}.` : parseInlineLinks(it.e?.detail || it.e?.label || "")}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "16px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "log-dots": {
      // Cleveland dot plot on a log10 axis (October 24, 2026). Ink only:
      // decade gridlines, a thin leader from the left edge, one dot, the
      // value beside it. No fills or badges, so it reads like a journal
      // figure rather than a dashboard.
      const lo = Math.log10(block.min), hi = Math.log10(block.max);
      const pos = (v: number) => Math.max(0, Math.min(100, ((Math.log10(Math.max(v, block.min)) - lo) / (hi - lo)) * 100));
      const decades: number[] = [];
      for (let d = Math.ceil(lo); d <= Math.floor(hi); d++) decades.push(10 ** d);
      const ink = T.text;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "22px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="list">
            {block.rows.map((r, j) => {
              const x = pos(r.value);
              const right = x > 72;
              return (
                <div key={j} role="listitem" className="blog-ld-row" style={{ borderTop: j === 0 ? `1px solid ${ink}` : `1px solid ${T.borderLight}` }}>
                  <div className="blog-ld-label" style={{ fontFamily: T.sans }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.3 }}>{r.href ? <a href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</a> : r.label}</div>
                    {r.sub && <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.35, marginTop: 1 }}>{r.sub}</div>}
                  </div>
                  <div className="blog-ld-track" aria-label={`${r.label}: ${r.valueLabel}`}>
                    {decades.map((d) => <span key={d} aria-hidden="true" style={{ position: "absolute", left: `${pos(d)}%`, top: 0, bottom: 0, borderLeft: `1px solid ${T.borderLight}` }} />)}
                    <span aria-hidden="true" style={{ position: "absolute", left: 0, width: `${x}%`, top: "50%", borderTop: `1px solid ${ink}` }} />
                    <span aria-hidden="true" style={{ position: "absolute", left: `${x}%`, top: "50%", width: 9, height: 9, marginLeft: -4.5, marginTop: -4.5, borderRadius: "50%", background: ink }} />
                    <span style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", ...(right ? { right: `calc(${100 - x}% + 10px)` } : { left: `calc(${x}% + 10px)` }), fontSize: 12.5, fontWeight: 600, color: T.text, fontFamily: T.sans, whiteSpace: "nowrap" as const, background: T.surface, padding: "0 3px" }}>{r.valueLabel}</span>
                  </div>
                </div>
              );
            })}
            <div className="blog-ld-row blog-ld-axis" aria-hidden="true" style={{ borderTop: `1px solid ${ink}`, paddingTop: 4 }}>
              <div className="blog-ld-label" />
              <div style={{ position: "relative", height: 34 }}>
                {decades.map((d) => (
                  <span key={d}>
                    <span style={{ position: "absolute", left: `${pos(d)}%`, top: -4, height: 5, borderLeft: `1px solid ${ink}` }} />
                    <span style={{ position: "absolute", left: `${pos(d)}%`, top: 3, transform: "translateX(-50%)", fontSize: 11, color: T.textSecondary, fontFamily: T.sans }}>{d.toLocaleString("en-US")}</span>
                  </span>
                ))}
                <span style={{ position: "absolute", left: "50%", top: 19, transform: "translateX(-50%)", fontSize: 11, color: T.textMuted, fontFamily: T.sans, fontStyle: "italic" as const, whiteSpace: "nowrap" as const }}>{block.axisLabel}</span>
              </div>
            </div>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "trial-gantt": {
      // Bars from first patient to estimated primary completion on a shared
      // year axis. Dashed markers pin dated events (an FDA decision, today).
      const t0 = block.startYear;
      const t1 = block.endYear + 1;
      const toYears = (d: string) => { const [y, m] = d.split("-").map(Number); return y + ((m || 1) - 1) / 12; };
      const X = (d: string) => Math.max(0, Math.min(100, ((toYears(d) - t0) / (t1 - t0)) * 100));
      const years: number[] = [];
      for (let y = t0; y <= block.endYear; y++) years.push(y);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "22px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-dot-row" aria-hidden="true" style={{ borderTop: "none", paddingBottom: 0 }}>
            <div className="blog-dot-label" />
            <div className="blog-gantt-track" style={{ position: "relative", minHeight: 30 }}>
              {block.markers.map((m, k) => <span key={k} style={{ position: "absolute", left: `${X(m.date)}%`, bottom: 2, transform: k % 2 ? "translateX(-100%)" : "none", paddingLeft: k % 2 ? 0 : 6, paddingRight: k % 2 ? 6 : 0, fontSize: 11, fontWeight: 700, color: k === 0 ? VIZ_HOT : T.textSecondary, fontFamily: T.sans, lineHeight: 1.2, maxWidth: 170 }}>{m.label}</span>)}
            </div>
          </div>
          <div role="list">
            {block.rows.map((r, j) => {
              const label = r.href ? <Link href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</Link> : r.label;
              const a = X(r.start);
              const b = X(r.end);
              return (
                <div key={j} role="listitem" className="blog-dot-row">
                  <div className="blog-dot-label">{label}{r.sub && <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>{r.sub}</div>}</div>
                  <div className="blog-dot-track blog-gantt-track" style={{ height: 40 }} aria-label={`${r.label}: from ${r.start} to an estimated ${r.end}${r.tag ? `, ${r.tag}` : ""}`}>
                    {years.map((y) => <span key={y} aria-hidden="true" style={{ position: "absolute", left: `${X(`${y}-01`)}%`, top: 0, bottom: 0, width: 1, background: T.borderLight }} />)}
                    {block.markers.map((m, k) => <span key={k} aria-hidden="true" style={{ position: "absolute", left: `${X(m.date)}%`, top: -6, bottom: -6, width: 0, borderLeft: `1.5px dashed ${k === 0 ? VIZ_HOT : T.textMuted}` }} />)}
                    <span aria-hidden="true" style={{ position: "absolute", left: `${a}%`, width: `${Math.max(1, b - a)}%`, top: 8, height: 12, borderRadius: 2, background: r.hot ? VIZ_HOT : "#5B6B7A", opacity: r.hot ? 1 : 0.85 }} />
                    <span aria-hidden="true" style={{ position: "absolute", left: `${b}%`, top: 4, width: 2, height: 20, marginLeft: -1, background: r.hot ? VIZ_HOT : "#5B6B7A" }} />
                    {r.tag && <span style={{ position: "absolute", left: `${a}%`, top: 23, fontSize: 11, color: T.textSecondary, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>{r.tag}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="blog-dot-row" aria-hidden="true" style={{ borderTop: "none", paddingTop: 2 }}>
            <div className="blog-dot-label" />
            <div className="blog-gantt-track" style={{ position: "relative", height: 16 }}>
              {years.map((y) => <span key={y} style={{ position: "absolute", left: `${X(`${y}-07`)}%`, transform: "translateX(-50%)", fontSize: 11, color: T.textMuted, fontFamily: T.sans }}>{y}</span>)}
            </div>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "band-rules": {
      // One blot strip, several rulebooks. The strip at the top draws each
      // band position like a real nitrocellulose strip; every rule row below
      // repeats the positions and fills in only the bands that rule counts,
      // with the "how many" requirement at the end of the row.
      const n = block.bands.length;
      const xOf = (k: number) => ((k + 0.5) / n) * 100;
      const widthOf = (id: string) => (id === "lsa" ? 14 : id === "41" || id === "93" ? 9 : 6);
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 16, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-br-row blog-br-strip-row" aria-hidden="true">
            <div className="blog-br-label" style={{ color: T.textMuted, fontWeight: 600 }}>Band on the strip</div>
            <div className="blog-br-strip">
              {block.bands.map((b, k) => (
                <span key={b.id} style={{ position: "absolute", left: `${xOf(k)}%`, top: 8, bottom: 8, width: widthOf(b.id), marginLeft: -widthOf(b.id) / 2, borderRadius: 2, background: b.id === "lsa" ? T.primaryDark : "#3B3733", opacity: b.id === "lsa" ? 0.85 : 0.75 }} />
              ))}
            </div>
            <div className="blog-br-need" />
          </div>
          <div className="blog-br-row" aria-hidden="true" style={{ borderTop: "none", paddingTop: 0 }}>
            <div className="blog-br-label" />
            <div className="blog-br-axis">
              {block.bands.map((b, k) => (
                <span key={b.id} style={{ position: "absolute", left: `${xOf(k)}%`, transform: "translateX(-50%)", textAlign: "center", lineHeight: 1.15 }}>
                  <span style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: T.text, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{b.label}</span>
                  {b.name && <span className="blog-br-name" style={{ display: "block", fontSize: 10, color: T.textMuted, fontFamily: T.sans, marginTop: k % 2 ? 13 : 1, whiteSpace: "nowrap" }}>{b.name}</span>}
                </span>
              ))}
            </div>
            <div className="blog-br-need" />
          </div>
          {block.groups.map((g, gi) => (
            <div key={gi} role="list" aria-label={g.label}>
              <div className="blog-br-group">{g.label}</div>
              {g.rules.map((r, j) => {
                const names = block.bands.filter((b) => r.counted.includes(b.id)).map((b) => b.label).join(", ");
                return (
                  <div key={j} role="listitem" className="blog-br-row">
                    <div className="blog-br-label">
                      {r.href ? <a href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noopener" style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</a> : r.label}
                      {r.sub && <span style={{ display: "block", fontSize: 11.5, color: T.textMuted, fontWeight: 400, marginTop: 2, lineHeight: 1.35 }}>{r.sub}</span>}
                    </div>
                    <div className="blog-br-track" role="img" aria-label={`${r.label} counts bands ${names}; ${r.need}`}>
                      {block.bands.map((b, k) => {
                        const on = r.counted.includes(b.id);
                        return <span key={b.id} aria-hidden="true" style={{ position: "absolute", left: `${xOf(k)}%`, top: "50%", width: on ? widthOf(b.id) + 2 : 4, height: on ? 26 : 4, marginLeft: on ? -(widthOf(b.id) + 2) / 2 : -2, marginTop: on ? -13 : -2, borderRadius: on ? 2 : "50%", background: on ? (b.id === "lsa" ? T.primaryDark : T.primary) : "#D6D3CD" }} />;
                      })}
                    </div>
                    <div className="blog-br-need">{r.need}</div>
                  </div>
                );
              })}
            </div>
          ))}
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "dumbbell": {
      // Two dots per row: the drug's rate (hot) and the comparator's (grey),
      // joined by a bar. The bar's length is the absolute gap, and the text
      // after it is the relative reduction, so neither can hide the other.
      const unit = block.unit ?? "";
      const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => +(f * block.max).toFixed(2));
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="list">
            {block.rows.map((r, j) => {
              const ax = Math.min(100, (r.a / block.max) * 100);
              const bx = Math.min(100, (r.b / block.max) * 100);
              const lo = Math.min(ax, bx);
              const hi = Math.max(ax, bx);
              const label = r.href ? <Link href={r.href} style={{ color: T.text, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{r.label}</Link> : r.label;
              const fmt = (v: number) => r.decimals !== undefined ? v.toFixed(r.decimals) : String(v);
              return (
                <div key={j} role="listitem" className="blog-dot-row">
                  <div className="blog-dot-label">{label}{r.sub && <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>{r.sub}</div>}</div>
                  <div className="blog-dot-track blog-dumbbell-track" aria-label={`${r.label}: ${block.aLabel} ${fmt(r.a)}${unit}, ${block.bLabel} ${fmt(r.b)}${unit}, ${r.delta}`}>
                    {ticks.map((t) => <span key={t} aria-hidden="true" style={{ position: "absolute", left: `${(t / block.max) * 100}%`, top: 0, bottom: 0, width: 1, background: T.borderLight }} />)}
                    <span aria-hidden="true" style={{ position: "absolute", left: `${lo}%`, width: `${hi - lo}%`, top: "50%", height: 6, marginTop: -3, borderRadius: 3, background: `linear-gradient(90deg, ${VIZ_HOT}, #B8B2A7)` }} />
                    <span aria-hidden="true" style={{ position: "absolute", left: `${bx}%`, top: "50%", width: 16, height: 16, marginLeft: -8, marginTop: -8, borderRadius: "50%", background: "#B8B2A7", boxShadow: `0 0 0 2.5px ${T.surface}` }} />
                    <span aria-hidden="true" style={{ position: "absolute", left: `${ax}%`, top: "50%", width: 18, height: 18, marginLeft: -9, marginTop: -9, borderRadius: "50%", background: VIZ_HOT, boxShadow: `0 0 0 2.5px ${T.surface}, 0 2px 6px rgba(194,65,12,0.35)` }} />
                    <span className="blog-dumbbell-a" style={{ position: "absolute", left: `${ax}%`, top: 0, transform: "translateX(-50%)", fontSize: 11, fontWeight: 700, color: VIZ_HOT, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>{fmt(r.a)}{unit}</span>
                    <span className="blog-dumbbell-b" style={{ position: "absolute", left: `${bx}%`, bottom: 0, transform: "translateX(-50%)", fontSize: 11, fontWeight: 600, color: T.textMuted, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>{fmt(r.b)}{unit}</span>
                    <span style={{ position: "absolute", left: `calc(${hi}% + 16px)`, top: "50%", transform: "translateY(-50%)", fontSize: 12.5, lineHeight: 1.25, fontWeight: 700, color: T.text, fontFamily: T.sans, width: "max-content", maxWidth: `calc(${100 - hi}% + 54px)` }}>{r.delta}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="blog-dot-row" aria-hidden="true" style={{ borderTop: "none", paddingTop: 2 }}>
            <div className="blog-dot-label" />
            <div style={{ position: "relative", height: 16, marginRight: 76 }}>
              {ticks.map((t) => <span key={t} style={{ position: "absolute", left: `${(t / block.max) * 100}%`, transform: "translateX(-50%)", fontSize: 11, color: T.textMuted, fontFamily: T.sans }}>{t}{unit}</span>)}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 10, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 12, height: 12, borderRadius: "50%", background: VIZ_HOT, display: "inline-block" }} />{block.aLabel}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 12, height: 12, borderRadius: "50%", background: "#B8B2A7", display: "inline-block" }} />{block.bLabel}</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "threshold-gauge": {
      // A lab value on a log axis, because the thresholds that matter for
      // liver tests (1.5x, 3x, 5x, 8x the upper limit of normal) are ratios
      // and a linear axis would crush the low end where most people sit.
      // Pins carry numbers and the words live in the key underneath, so
      // nothing overlaps on a phone.
      const lo = Math.log10(block.min); const hi = Math.log10(block.max);
      const pos = (v: number) => Math.round(Math.max(0, Math.min(100, ((Math.log10(v) - lo) / (hi - lo)) * 100)) * 1000) / 1000;
      const fill = (t: string) => t === "stop" ? "#FDE2DC" : t === "watch" ? "#FEF0C7" : "#E6F4F1";
      const ink = (t: string) => t === "stop" ? VIZ_HOT : t === "watch" ? "#B45309" : T.primary;
      const marks = [...block.marks].sort((a, b) => a.value - b.value);
      const fmtV = (v: number) => `${v % 1 === 0 ? v : v.toFixed(1)}x`;
      const shift = (p: number) => (p < 4 ? "translateX(0)" : p > 96 ? "translateX(-100%)" : "translateX(-50%)");
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-scroll-hint" aria-hidden="true">Swipe sideways to see the whole scale</div>
          <div style={{ overflowX: "auto" as const, WebkitOverflowScrolling: "touch" as const }}>
            <div style={{ minWidth: 520, position: "relative", padding: "34px 6px 0" }} aria-hidden="true">
              {/* numbered pins above the track; neighbours closer than 7% step up a row */}
              <div style={{ position: "absolute", left: 6, right: 6, top: 0, height: 34 }}>
              {marks.map((m, k) => {
                const p = pos(m.value);
                const prev = k > 0 ? pos(marks[k - 1].value) : -100;
                const raised = p - prev < 7 && k % 2 === 1;
                return (
                  <div key={k} style={{ position: "absolute", left: `${p.toFixed(3)}%`, top: raised ? 0 : 14, transform: "translateX(-50%)", display: "flex", flexDirection: "column" as const, alignItems: "center" }}>
                    <span style={{ width: 22, height: 22, borderRadius: "50%", background: ink(m.tone), color: "#fff", fontSize: 11.5, fontWeight: 800, fontFamily: T.sans, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 0 2px ${T.surface}` }}>{k + 1}</span>
                    <span style={{ width: 2, height: raised ? 32 : 18, background: ink(m.tone) }} />
                  </div>
                );
              })}
              </div>
              <div style={{ position: "relative", height: 46, borderRadius: 10, overflow: "hidden", border: `1px solid ${T.border}`, marginTop: 20 }}>
                {block.zones.map((z, k) => {
                  const a = pos(z.from), b = pos(z.to);
                  return (
                    <div key={k} style={{ position: "absolute", left: `${a}%`, width: `${Math.round((b - a) * 1000) / 1000}%`, top: 0, bottom: 0, background: fill(z.tone), borderLeft: k > 0 ? `1px solid ${T.surface}` : "none", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", boxSizing: "border-box" as const }}>
                      <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase" as const, color: ink(z.tone), fontFamily: T.sans, textAlign: "center" as const, lineHeight: 1.15 }}>{z.label}</span>
                    </div>
                  );
                })}
                {marks.map((m, k) => <span key={k} style={{ position: "absolute", left: `${pos(m.value)}%`, top: 0, bottom: 0, width: 2, marginLeft: -1, background: ink(m.tone), opacity: 0.85 }} />)}
              </div>
              <div style={{ position: "relative", height: 20, marginTop: 6 }}>
                {block.ticks.filter((t) => t >= block.min && t <= block.max).map((t) => (
                  <span key={t} style={{ position: "absolute", left: `${pos(t)}%`, transform: shift(pos(t)), fontSize: 11.5, fontWeight: 600, color: T.textMuted, fontFamily: T.sans, whiteSpace: "nowrap" as const }}>{fmtV(t)}</span>
                ))}
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: T.sans, textAlign: "right" as const }}>{block.unit}</div>
            </div>
          </div>
          <ol style={{ listStyle: "none", margin: "16px 0 0", padding: 0, display: "grid", gap: 10 }}>
            {marks.map((m, k) => (
              <li key={k} style={{ display: "grid", gridTemplateColumns: "26px 56px 1fr", gap: 10, alignItems: "baseline", fontFamily: T.sans }}>
                <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: "50%", background: ink(m.tone), color: "#fff", fontSize: 11.5, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", alignSelf: "start" }}>{k + 1}</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: ink(m.tone) }}>{fmtV(m.value)}</span>
                <span style={{ fontSize: 13.5, color: T.text, lineHeight: 1.5 }}><b style={{ fontWeight: 700 }}>{m.label}.</b> <span style={{ color: T.textSecondary }}>{parseInlineLinks(m.detail)}</span></span>
              </li>
            ))}
          </ol>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "14px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "dose-calendar": {
      // A year on one line per treatment. Pens are small diamonds; infusions
      // are circles whose area scales with hours in the chair. The eye counts
      // the marks and weighs them before a single number is read.
      const W = 720, LEFT = 26, RIGHT = 26, ROW_H = 64, TOP = 26;
      const span = W - LEFT - RIGHT;
      const x = (day: number) => LEFT + (Math.min(block.days, Math.max(0, day)) / block.days) * span;
      const maxHours = Math.max(1, ...block.rows.flatMap((r) => r.events.map((e) => e.hours ?? 0)));
      const rad = (h: number) => 6 + 10 * Math.sqrt(h / maxHours);
      const monthStarts = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334, 365];
      const H = TOP + block.rows.length * ROW_H + 22;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-scroll-hint" aria-hidden="true">Swipe sideways to see the whole chart</div>
          <div style={{ overflowX: "auto" as const, WebkitOverflowScrolling: "touch" as const }}>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", minWidth: 560, fontFamily: T.sans }} role="img" aria-label={block.rows.map((r) => `${r.label}: ${r.summary}`).join(". ")}>
              {monthStarts.map((d, k) => (
                <g key={k}>
                  <line x1={x(d)} x2={x(d)} y1={TOP - 6} y2={H - 20} stroke={T.borderLight} strokeWidth={1} />
                  {k < 12 && <text x={(x(d) + x(monthStarts[k + 1])) / 2} y={H - 6} textAnchor="middle" fontSize={10.5} fill={T.textMuted}>{["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][k]}</text>}
                </g>
              ))}
              {block.rows.map((r, j) => {
                const cy = TOP + j * ROW_H + 34;
                return (
                  <g key={j}>
                    <text x={LEFT} y={TOP + j * ROW_H + 8} fontSize={12.5} fontWeight={700} fill={T.text}>{r.label}{r.sub ? <tspan fontWeight={500} fill={T.textMuted}> {r.sub}</tspan> : null}</text>
                    <line x1={LEFT} x2={W - RIGHT} y1={cy} y2={cy} stroke={T.border} strokeWidth={1.5} />
                    {r.events.map((e, k) => e.kind === "pen" ? (
                      <g key={k} transform={`translate(${x(e.day)} ${cy})`}><title>{e.label}</title><rect x={-5} y={-5} width={10} height={10} rx={1.5} transform="rotate(45)" fill={T.primary} /></g>
                    ) : (
                      <g key={k}><title>{e.label}</title>
                        <circle cx={x(e.day)} cy={cy} r={rad(e.hours ?? 1)} fill={e.kind === "infusion" ? VIZ_HOT : "#B45309"} fillOpacity={0.9} stroke={T.surface} strokeWidth={2} />
                        {(e.hours ?? 0) >= 1 && <text x={x(e.day)} y={cy + 3.5} textAnchor="middle" fontSize={10} fontWeight={700} fill="#FFFFFF">{e.hours}h</text>}
                      </g>
                    ))}
                    <text x={W - RIGHT} y={TOP + j * ROW_H + 8} textAnchor="end" fontSize={11} fill={T.textSecondary}>{r.summary}</text>
                  </g>
                );
              })}
            </svg>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 10, fontSize: 12, color: T.textMuted, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 9, height: 9, background: T.primary, transform: "rotate(45deg)", display: "inline-block" }} />Self-injection at home</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 14, height: 14, borderRadius: "50%", background: VIZ_HOT, display: "inline-block" }} />Infusion, sized by hours</span>
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "spread-map": {
      // Diseases down, years across, one color per drug. Dots from the same
      // drug are joined, so the path of a molecule across diseases is the
      // shape the reader sees before reading a single year.
      const PAL = [VIZ_HOT, T.primary, "#1D4ED8", "#6D28D9", "#BE185D", "#B45309"];
      const W = 720, LEFT = 168, RIGHT = 22, TOP = 30, ROW_H = 46;
      const years = block.endYear - block.startYear;
      const x = (y: number) => LEFT + ((y - block.startYear) / years) * (W - LEFT - RIGHT);
      const cy = (row: number) => TOP + row * ROW_H + ROW_H / 2;
      const H = TOP + block.rows.length * ROW_H + 26;
      const yearTicks: number[] = [];
      for (let y = block.startYear; y <= block.endYear; y += 5) yearTicks.push(y);
      // Dots at the same (row, year) from different drugs fan out vertically.
      const taken = new Map<string, number>();
      const offset = (row: number, year: number) => { const k = `${row}:${year}`; const n = taken.get(k) ?? 0; taken.set(k, n + 1); return n; };
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-scroll-hint" aria-hidden="true">Swipe sideways to see the whole chart</div>
          <div style={{ overflowX: "auto" as const, WebkitOverflowScrolling: "touch" as const }}>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", minWidth: 600, fontFamily: T.sans }} role="img" aria-label={block.caption}>
              {yearTicks.map((y) => (
                <g key={y}>
                  <line x1={x(y)} x2={x(y)} y1={TOP - 8} y2={H - 22} stroke={T.borderLight} strokeWidth={1} />
                  <text x={x(y)} y={H - 7} textAnchor="middle" fontSize={11} fill={T.textMuted}>{y}</text>
                </g>
              ))}
              {block.rows.map((r, j) => (
                <g key={j}>
                  <line x1={LEFT} x2={W - RIGHT} y1={cy(j)} y2={cy(j)} stroke={T.border} strokeWidth={1} strokeDasharray="2 4" />
                  <text x={LEFT - 10} y={cy(j) + 4} textAnchor="end" fontSize={12.5} fontWeight={700} fill={T.text}>{r.label}</text>
                  {r.sub && <text x={LEFT - 10} y={cy(j) + 17} textAnchor="end" fontSize={10.5} fill={T.textMuted}>{r.sub}</text>}
                </g>
              ))}
              {block.series.map((s, k) => {
                const pts = [...s.points].sort((a, b) => a.year - b.year || a.row - b.row).map((p) => ({ ...p, X: x(p.year), Y: cy(p.row) + offset(p.row, p.year) * 12 }));
                const color = PAL[k % PAL.length];
                let d = "";
                pts.forEach((p, n) => {
                  if (n === 0) { d += `M${p.X} ${p.Y}`; return; }
                  const q = pts[n - 1];
                  const mx = (q.X + p.X) / 2;
                  d += ` C${mx} ${q.Y}, ${mx} ${p.Y}, ${p.X} ${p.Y}`;
                });
                return (
                  <g key={k}>
                    {pts.length > 1 && <path d={d} fill="none" stroke={color} strokeWidth={2.5} strokeOpacity={0.45} strokeLinecap="round" />}
                    {pts.map((p, n) => (
                      <g key={n}><title>{`${s.label}, ${block.rows[p.row]?.label}, ${p.year}: ${p.detail}`}</title>
                        <circle cx={p.X} cy={p.Y} r={9} fill={color} stroke={T.surface} strokeWidth={2.5} />
                        <text x={p.X} y={p.Y - 13} textAnchor="middle" fontSize={10.5} fontWeight={700} fill={color}>{p.year}</text>
                      </g>
                    ))}
                  </g>
                );
              })}
            </svg>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 10, fontSize: 12, color: T.textSecondary, fontFamily: T.sans, flexWrap: "wrap" as const }}>
            {block.series.map((s, k) => (
              <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <i aria-hidden="true" style={{ width: 11, height: 11, borderRadius: "50%", background: PAL[k % PAL.length], display: "inline-block" }} />
                {s.href ? <Link href={s.href} style={{ color: T.textSecondary, textDecoration: "none", borderBottom: `1px dotted ${T.textMuted}` }}>{s.label}</Link> : s.label}
              </span>
            ))}
          </div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "12px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "block-strip": {
      // 20 blocks per row, each worth 5%. The lock marks an exclusion that
      // a washout cannot fix, because the treatment cannot be undone.
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && (
            <figcaption style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>
              {block.locked && (
                <svg aria-hidden="true" width="16" height="18" viewBox="0 0 16 18" style={{ flexShrink: 0 }}>
                  <rect x="1.5" y="8" width="13" height="9" rx="2" fill="none" stroke={VIZ_HOT} strokeWidth="1.8" />
                  <path d="M4.5 8V5.5a3.5 3.5 0 0 1 7 0V8" fill="none" stroke={VIZ_HOT} strokeWidth="1.8" />
                </svg>
              )}
              {block.title}
            </figcaption>
          )}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 14, lineHeight: 1.5 }}>{block.note}</div>}
          <div role="list">
            {block.rows.map((r, j) => {
              const on = Math.round(r.value / 5);
              return (
                <div key={j} role="listitem" className="blog-strip-row">
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.serif }}>
                      {r.href ? <Link href={r.href} style={{ color: T.text, textDecoration: "none" }}>{r.label}</Link> : r.label}
                    </div>
                    {r.sub && <div style={{ fontSize: 11.5, color: T.textMuted, fontFamily: T.sans, marginTop: 2 }}>{r.sub}</div>}
                  </div>
                  <div className="blog-strip-blocks" role="img" aria-label={block.scaleNote ? `${r.label}: ${r.value}%` : `${r.value}% of ${r.label} trials`}>
                    {Array.from({ length: 20 }, (_, b) => <i key={b} style={{ background: b < on ? VIZ_HOT : VIZ_COOL }} />)}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: VIZ_HOT, fontFamily: T.sans, textAlign: "right" as const }}>{r.value}%</div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.sans, marginTop: 10 }}>{block.scaleNote ?? "Each block is 5% of that disease's open treatment trials."}</div>
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "10px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "time-ruler": {
      // Ordered time windows on one axis; bubble area = how many exclusion
      // rules ask for that window. Long windows switch to the warning color.
      const max = Math.max(1, ...block.points.map((p) => p.count));
      const total = block.points.reduce((a, p) => a + p.count, 0);
      const n = block.points.length;
      const hl = block.highlightFrom ?? n;
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "24px 22px 16px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>{block.title}</figcaption>}
          {block.note && <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 8, lineHeight: 1.5 }}>{block.note}</div>}
          <div className="blog-scroll-hint" aria-hidden="true">Swipe sideways to see the whole chart</div>
          <div style={{ overflowX: "auto" as const, WebkitOverflowScrolling: "touch" as const }}>
            <div role="list" style={{ position: "relative", minWidth: 560, height: 170, margin: "0 6px" }}>
              <div aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, bottom: 44, height: 2, background: T.border }} />
              {block.points.map((p, j) => {
                const d = 10 + 58 * Math.sqrt(p.count / max);
                const x = ((j + 0.5) / n) * 100;
                const hot = j >= hl;
                const rawShare = total ? (p.count / total) * 100 : 0;
                const share = p.count > 0 && Math.round(rawShare) === 0 ? "<1%" : `${Math.round(rawShare)}%`;
                return (
                  <div key={j} role="listitem" aria-label={`${p.label}: ${p.count} of ${total} (${share})`} style={{ position: "absolute", left: `${x}%`, bottom: 0, transform: "translateX(-50%)", width: `${100 / n}%`, textAlign: "center" as const }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: hot ? VIZ_HOT : T.primary, fontFamily: T.sans, marginBottom: 4 }}>{share}</div>
                    <div aria-hidden="true" style={{ width: d, height: d, borderRadius: "50%", margin: "0 auto", background: hot ? VIZ_HOT : T.primary, opacity: 0.85 }} />
                    <div aria-hidden="true" style={{ width: 2, height: 8, background: T.border, margin: "0 auto" }} />
                    <div style={{ fontSize: 11, color: T.textSecondary, fontFamily: T.sans, lineHeight: 1.25, height: 34, padding: "2px 2px 0" }}>{p.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {block.highlightLabel && (
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" as const, marginTop: 8, fontSize: 12, color: T.textMuted, fontFamily: T.sans }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 10, height: 10, borderRadius: "50%", background: T.primary, display: "inline-block" }} />Shorter windows</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}><i aria-hidden="true" style={{ width: 10, height: 10, borderRadius: "50%", background: VIZ_HOT, display: "inline-block" }} />{block.highlightLabel}</span>
            </div>
          )}
          <p style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.sans, margin: "10px 0 0", lineHeight: 1.55 }}>{parseInlineLinks(block.caption)}</p>
        </figure>
      );
    }

    case "journey-band": {
      // Overlapping day-range phases on a shared scale. Percentage offsets
      // keep it responsive; the point is seeing what runs in parallel.
      return (
        <figure key={i} style={{ margin: "32px 0", padding: "26px 22px 18px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
          {block.title && (
            <figcaption style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>
              {block.title}
            </figcaption>
          )}
          {block.note && (
            <div style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginBottom: 18, lineHeight: 1.5 }}>
              {block.note}
            </div>
          )}
          <div role="img" aria-label={block.title || "Timeline of overlapping phases"}>
            <div style={{ position: "relative", height: 18, marginBottom: 8 }} aria-hidden="true">
              {block.ticks.map((t, j) => (
                <span key={j} style={{ position: "absolute", left: `${(t.day / block.maxDays) * 100}%`, fontSize: 11, color: T.textMuted, fontFamily: T.sans, whiteSpace: "nowrap" as const, letterSpacing: "0.02em", transform: j === 0 ? "none" : j === block.ticks.length - 1 ? "translateX(-100%)" : "translateX(-50%)" }}>
                  {t.label}
                </span>
              ))}
            </div>
            {block.phases.map((p, j) => {
              const left = (p.start / block.maxDays) * 100;
              const width = Math.max(((p.end - p.start) / block.maxDays) * 100, 3);
              return (
                <div key={j} style={{ padding: "9px 0 11px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" as const, marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.serif }}>{p.label}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.primary, fontFamily: T.sans }}>{p.range}</span>
                  </div>
                  <div style={{ position: "relative", height: 16 }}>
                    {block.ticks.map((t, k) => (
                      <div key={k} aria-hidden="true" style={{ position: "absolute", top: 0, bottom: 0, left: `${(t.day / block.maxDays) * 100}%`, width: 1, background: k === 0 ? T.border : T.borderLight }} />
                    ))}
                    <div style={{ position: "absolute", left: `${left}%`, width: `${width}%`, top: 2, height: 12, background: T.primaryMedium, borderRadius: 2, borderLeft: `3px solid ${T.primary}` }} />
                  </div>
                  <div style={{ fontSize: 12.5, color: T.textSecondary, fontFamily: T.sans, marginTop: 6, lineHeight: 1.5 }}>{p.detail}</div>
                </div>
              );
            })}
          </div>
        </figure>
      );
    }

    case "table": {
      // A real <table> rather than a styled grid: the caption, column
      // headers and row headers give every cell its context for screen
      // readers, search engines and AI answer engines. Wraps on phones
      // and scrolls sideways only if a cell cannot wrap.
      return (
        <figure key={i} style={{ margin: "32px 0", padding: 0 }}>
          <div className="blog-table-wrap" style={{ overflowX: "auto", border: `1px solid ${T.border}`, borderRadius: 14, background: T.surface }}>
            <table className="blog-table" style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.sans }}>
              <caption style={{ captionSide: "top", textAlign: "left", padding: "18px 18px 10px", fontFamily: T.serif, fontSize: 16, fontWeight: 700, color: T.text }}>
                {block.caption}
              </caption>
              <thead>
                <tr>
                  {block.columns.map((c, k) => (
                    <th key={k} scope="col" style={{
                      textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700,
                      letterSpacing: "0.08em", textTransform: "uppercase" as const, color: T.textMuted,
                      borderBottom: `1px solid ${T.border}`, background: T.bg, verticalAlign: "bottom",
                    }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((r, j) => (
                  <tr key={j} style={{ borderBottom: j === block.rows.length - 1 ? "none" : `1px solid ${T.borderLight}` }}>
                    <th scope="row" style={{
                      textAlign: "left", padding: "12px 14px", fontSize: 13, fontWeight: 700,
                      color: T.primaryDark, verticalAlign: "top", width: "26%", lineHeight: 1.45,
                    }}>{parseInlineLinks(r.label)}</th>
                    {r.cells.map((cell, k) => (
                      <td key={k} style={{ padding: "12px 14px", fontSize: 14, lineHeight: 1.55, color: T.textSecondary, verticalAlign: "top" }}>
                        {parseInlineLinks(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.note && <figcaption style={{ fontSize: 13, color: T.textMuted, fontFamily: T.sans, marginTop: 10, lineHeight: 1.5 }}>{parseInlineLinks(block.note)}</figcaption>}
        </figure>
      );
    }

    case "term-grid": {
      const cols = block.columns || 2;
      return (
        <div key={i} style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: 10,
          margin: "28px 0",
        }}
        className="blog-term-grid"
        >
          {block.terms.map((t_item, j) => (
            <div key={j} style={{
              padding: "14px 16px",
              background: T.surface,
              border: `1px solid ${T.borderLight}`,
              borderRadius: T.r2,
              transition: "border-color 0.15s",
            }}>
              <div style={{
                fontSize: 13,
                fontWeight: 700,
                color: T.primaryDark,
                fontFamily: T.sans,
                marginBottom: 4,
                letterSpacing: "0.005em",
              }}>
                {t_item.term}
              </div>
              <div style={{
                fontSize: 13,
                lineHeight: 1.55,
                color: T.textMuted,
                fontFamily: T.sans,
              }}>
                {t_item.meaning}
              </div>
            </div>
          ))}
        </div>
      );
    }

    case "series-card": {
      const gradients = [
        "linear-gradient(135deg, #093F39 0%, #0E5F56 50%, #2E9C8E 100%)",
        "linear-gradient(135deg, #064E3B 0%, #047857 50%, #10B981 100%)",
        "linear-gradient(135deg, #1E3A5F 0%, #2563EB 50%, #60A5FA 100%)",
        "linear-gradient(135deg, #312E81 0%, #6D28D9 50%, #A78BFA 100%)",
      ];
      return (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 14, margin: "28px 0 12px" }}
             className="series-card-stack">
          {block.items.map((item, j) => (
            <Link
              key={j}
              href={item.href}
              style={{
                display: "flex", alignItems: "center", gap: 0,
                background: gradients[j % gradients.length],
                borderRadius: 14, overflow: "hidden",
                textDecoration: "none",
                boxShadow: "0 2px 12px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.1)",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                position: "relative",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.2), 0 2px 6px rgba(0,0,0,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.1)";
              }}
            >
              {/* Part number badge */}
              <div style={{
                width: 80, minHeight: 100,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                borderRight: "1px solid rgba(255,255,255,0.15)",
              }}
              className="series-card-badge"
              >
                <div style={{
                  fontSize: 11, fontWeight: 600, fontFamily: T.sans,
                  color: "rgba(255,255,255,0.7)", textTransform: "uppercase",
                  letterSpacing: "0.1em", lineHeight: 1,
                }}>Part</div>
                <div style={{
                  fontSize: 36, fontWeight: 800, fontFamily: T.serif,
                  color: "#FFFFFF", lineHeight: 1, marginTop: 2,
                }}>{item.part}</div>
              </div>
              {/* Content area */}
              <div style={{
                flex: 1, padding: "18px 24px 18px 20px",
                minWidth: 0,
              }}
              className="series-card-content"
              >
                <div style={{
                  fontSize: 17, fontWeight: 700, color: "#FFFFFF",
                  fontFamily: T.serif, lineHeight: 1.3,
                  letterSpacing: "-0.01em",
                }}
                className="series-card-title"
                >
                  {item.title}
                </div>
                <div style={{
                  fontSize: 13, color: "rgba(255,255,255,0.8)",
                  fontFamily: T.sans, lineHeight: 1.55,
                  marginTop: 6, fontWeight: 400,
                }}
                className="series-card-desc"
                >
                  {item.description}
                </div>
              </div>
              {/* Arrow */}
              <div style={{
                padding: "0 20px 0 0", flexShrink: 0,
                display: "flex", alignItems: "center",
              }}
              className="series-card-arrow"
              >
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none"
                  stroke="rgba(255,255,255,0.6)" strokeWidth={2}
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      );
    }
  }
}

type RelatedPost = { slug: string; title: string; tag: string };
export type { NextStep } from "@/components/BlogNextSteps";

export default function BlogPostClient({
  post,
  relatedPosts = [],
  nextSteps = [],
  conditionName,
  conditionLabel,
  decisionAsk,
}: {
  post: BlogPost;
  /** Set when the post names pending FDA decisions (post.alertDecisions). */
  decisionAsk?: { heading: string; sub: string; successText: string; choices: { slug: string; name: string; hint?: string }[] } | null;
  relatedPosts?: RelatedPost[];
  nextSteps?: NextStep[];
  /** Formal disease name; what AlertSignup matches subscriptions on */
  conditionName?: string;
  /** How the post itself names the condition; used in reader-facing headings */
  conditionLabel?: string;
}) {
  /* Placement of the two reader-continuation elements, computed once from
     the block list so it is deterministic for every post, old or new.

     - Next steps go immediately before the SECOND heading: the reader has
       finished the opening section and the first argument, and is at the
       first natural pause. Posts with fewer than two headings get it after
       the body instead.
     - The alert signup moves from the very bottom (after ~3,000 words and
       the sources list, where GA4 recorded one form start a week) to just
       before the FAQ heading, which every recent post has and which is the
       point where a reader who is still here has decided the post is for
       them. Posts without an FAQ heading keep the old bottom placement. */
  const headingIdx = post.content
    .map((b, i) => (b.type === "heading" ? i : -1))
    .filter((i) => i >= 0);
  const nextStepsBefore = headingIdx.length >= 2 ? headingIdx[1] : -1;
  const faqHeadingIdx = post.content.findIndex(
    (b) => b.type === "heading" && /\b(questions?|faq|asking)\b/i.test(b.text),
  );
  // Some posts end with a sentence that points to "the signup below". If that
  // pointer sits after the FAQ heading, moving the form above the FAQ would
  // leave the reader looking at nothing, so the form follows the pointer.
  const SIGNUP_POINTER = /\b(sign ?up|signup|alert (?:form|below)|subscribe)\b[^.]{0,60}\b(below|beneath|at the bottom)\b|\b(below|at the bottom)\b[^.]{0,40}\b(sign ?up|signup|alert)\b/i;
  let signupPointerIdx = -1;
  post.content.forEach((b, i) => {
    const t = (b as { text?: string; answer?: string }).text || (b as { answer?: string }).answer || "";
    if (SIGNUP_POINTER.test(t)) signupPointerIdx = i;
  });
  const signupAfter = faqHeadingIdx >= 0 && signupPointerIdx > faqHeadingIdx ? signupPointerIdx : -1;
  // Where the article text ends: just above the FAQ (and its divider), or
  // after the last block when there is no FAQ.
  const articleEndIdx = faqHeadingIdx >= 0
    ? (faqHeadingIdx > 0 && post.content[faqHeadingIdx - 1].type === "divider" ? faqHeadingIdx - 1 : faqHeadingIdx)
    : post.content.length;
  // The signup closes the article, ahead of the divider that opens the FAQ,
  // so the FAQ heading never sits flush against the signup box.
  // A post can pin the signup with an { type: "alert-signup" } block.
  const hasInlineSignup = post.content.some((b) => b.type === "alert-signup");
  const signupBefore = !hasInlineSignup && signupAfter < 0 && faqHeadingIdx >= 0 ? articleEndIdx : -1;

  const signupEl = (
    <div style={{ margin: "40px 0 8px" }}>
      {decisionAsk ? (
        <AlertSignup
          condition={decisionAsk.choices[0].slug}
          choices={decisionAsk.choices}
          source={`blog-${post.slug}-decision`}
          heading={decisionAsk.heading}
          sub={decisionAsk.sub}
          successText={decisionAsk.successText}
        />
      ) : (
        <AlertSignup
          condition={conditionName}
          source={`blog-${post.slug}`}
          heading={conditionLabel ? `Get the next ${conditionLabel} update by email` : "Get the next update by email"}
          sub={conditionLabel
            ? `One email when ${conditionLabel} trials change or an FDA decision lands. No newsletter, no spam.`
            : "One email when trials change or decisions land for the conditions you care about. No newsletter, no spam."}
        />
      )}
    </div>
  );
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.sans }}>
      <SiteNav activePage="blog" />

      {/* a11y: explicit <main> landmark so screen readers can jump straight
          to article content. Lighthouse audit "document-has-main" requires
          this even though <article> exists below it. */}
      <main>

      <style dangerouslySetInnerHTML={{ __html: `
        .blog-dose-grid { display: flex; flex-wrap: wrap; gap: 2px; margin-bottom: 8px; }
        .blog-stamp { display: inline-block; font-family: 'Inter', sans-serif; font-weight: 900; text-transform: uppercase; letter-spacing: 0.12em; line-height: 1.15; padding: 4px 9px; border: 2.5px solid currentColor; border-radius: 5px; box-shadow: inset 0 0 0 1.5px #fff, inset 0 0 0 3px currentColor; transform: rotate(-4deg); opacity: 0.9; -webkit-mask-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='80'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -2.2 1.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); mask-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='80'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -2.2 1.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); }
        .blog-stamp-red { color: #B42318; }
        .blog-stamp-teal { color: #0E5F56; }
        .blog-stamp-amber { color: #B45309; }
        .blog-stamp-big { position: absolute; right: 4%; bottom: 46px; font-size: 19px; padding: 8px 16px; border-width: 3.5px; transform: rotate(-9deg); pointer-events: none; }
        .blog-dymo { display: inline-block; background: #1C1917; color: #fff; font-family: 'Inter', sans-serif; font-weight: 800; font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; padding: 4px 10px 5px; border-radius: 3px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.2); text-shadow: 0 -1px 0 rgba(0,0,0,0.6), 0 1px 0 rgba(255,255,255,0.15); }
        .blog-dymo-next { background: #fff; color: #B45309; border: 1.5px dashed #B45309; box-shadow: none; text-shadow: none; }
        .blog-tape-m { display: none; }
        @media (max-width: 560px) {
          .blog-tape { height: var(--hm) !important; }
          .blog-tape-d, .blog-tape-gap { display: none !important; }
          .blog-tape-m { display: block; }
          .blog-tape-item { top: var(--lym) !important; }
        }
        .blog-qcard { position: relative; padding-top: 14px; }
        .blog-qcard-holes { position: absolute; top: 0; left: 24px; right: 24px; display: flex; justify-content: space-between; z-index: 2; }
        .blog-qcard-holes i { width: 14px; height: 26px; border-radius: 7px; border: 3px solid #A8A29E; background: transparent; }
        .blog-qcard-paper { background-color: #FFFEF7; background-image: linear-gradient(90deg, transparent 52px, #F4A3A3 52px, #F4A3A3 54px, transparent 54px), repeating-linear-gradient(transparent 0 31px, #CFE0F0 31px 32px); border: 1px solid #E7E2D3; border-radius: 4px 4px 10px 10px; padding: 30px 22px 18px 66px; box-shadow: 0 1px 0 #E7E2D3, 0 3px 0 -1px #FFFEF7, 0 4px 0 -1px #E7E2D3, 0 8px 18px rgba(28,25,23,0.08); }
        .blog-qcard-print { margin-top: 8px; background: none; border: 1.5px solid #1E3A5F; color: #1E3A5F; font: 700 12.5px 'Inter', sans-serif; padding: 6px 12px; border-radius: 999px; cursor: pointer; }
        .blog-qcard-print:hover { background: #1E3A5F; color: #fff; }
        @media (max-width: 560px) { .blog-qcard-paper { padding: 28px 14px 16px 44px; background-image: linear-gradient(90deg, transparent 34px, #F4A3A3 34px, #F4A3A3 36px, transparent 36px), repeating-linear-gradient(transparent 0 31px, #CFE0F0 31px 32px); } .blog-qcard-holes { left: 12px; right: 12px; } }
        @media print { html[data-print] body * { visibility: hidden !important; } html[data-print] .blog-qcard, html[data-print] .blog-qcard * { visibility: visible !important; } html[data-print] .blog-qcard { position: absolute; left: 0; top: 0; width: 100%; } html[data-print] .blog-qcard-print { display: none !important; } }
        .blog-ruler-m, .blog-ruler-stamp-m { display: none; }
        @media (max-width: 560px) {
          .blog-ruler { height: var(--hm) !important; }
          .blog-ruler-d, .blog-stamp-big { display: none !important; }
          .blog-ruler-m { display: block; }
          .blog-ruler-label { top: var(--lym) !important; }
          .blog-ruler-stamp-m { display: block; text-align: center; margin: 14px 0 4px; }
        }
        .blog-pin { display: inline-block; width: 9px; height: 9px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 1.5px solid #A8A29E; background: transparent; box-sizing: border-box; }
        .blog-pin-us { background: #0E5F56; border-color: #0E5F56; }
        .blog-dose-sq { display: block; width: 6px; height: 6px; background: var(--dose); border-radius: 1.5px; opacity: 0.9; }
        .bc-head { display: grid; margin-left: 220px; text-align: center; font-family: Georgia, serif; font-size: 15px; font-weight: 700; margin-bottom: 4px; }
        .bc-counts { font-family: system-ui, sans-serif; margin-top: 8px; }
        .bc-counts strong { display: block; font-family: Georgia, serif; font-size: 22px; font-weight: 800; line-height: 1.1; }
        .bc-counts small { display: block; font-size: 12px; color: #7A746B; font-weight: 400; margin-top: 2px; }
        .bc-plot { position: relative; height: 330px; font-family: system-ui, sans-serif; }
        .bc-tick { position: absolute; left: 0; width: 30px; text-align: right; transform: translateY(-50%); font-size: 11.5px; color: #8A8378; }
        .bc-gauge { position: absolute; left: 38px; width: 26px; top: 0; bottom: 0; border-radius: 13px; background: #E7E3DC; }
        .bc-gauge-fill { position: absolute; left: 38px; width: 26px; bottom: 0; border-radius: 0 0 13px 13px; background: rgba(194, 65, 12, 0.32); }
        .bc-gauge-climb { position: absolute; left: 41px; width: 20px; border: 2px dashed #C2410C; border-radius: 4px; box-sizing: border-box; display: flex; align-items: center; justify-content: center; }
        .bc-gauge-climb b { font-size: 11px; font-weight: 800; color: #C2410C; background: #FFFDF9; padding: 1px 2px; border-radius: 3px; }
        .bc-line { position: absolute; left: 70px; right: 0; height: 0; }
        .bc-line em { position: absolute; left: 6px; bottom: 3px; font-style: normal; font-size: 12px; white-space: nowrap; background: rgba(255,253,249,0.9); padding: 0 3px; border-radius: 3px; }
        .bc-crowds { position: absolute; left: 220px; right: 0; top: 0; bottom: 0; display: grid; }
        .bc-crowd { position: relative; }
        .bc-up, .bc-down { position: absolute; left: 0; right: 0; margin: 0 auto; max-width: 128px; display: flex; flex-wrap: wrap; gap: 3px; justify-content: center; }
        .bc-up { flex-wrap: wrap-reverse; }
        .bc-up i, .bc-down i { width: 13px; height: 13px; border-radius: 50%; display: block; }
        .bc-down i { box-shadow: inset 0 0 0 1.5px #A39C90; }
        .vl-strip { display: grid; grid-template-columns: repeat(24, 1fr); gap: 4px; margin-bottom: 18px; }
        .vl-sq { display: block; aspect-ratio: 1; border-radius: 3px; }
        .vl-none { box-shadow: inset 0 0 0 1.5px #A39C90; }
        .vl-group { border-top: 1px solid #F0EEEA; padding: 14px 0 6px; }
        .vl-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; font-family: system-ui, sans-serif; }
        .vl-key { width: 14px; height: 14px; flex: none; }
        .vl-count { font-size: 26px; font-weight: 800; line-height: 1; min-width: 34px; font-family: Georgia, serif; }
        .vl-label { font-size: 14px; font-weight: 700; color: #1C1917; display: flex; flex-direction: column; gap: 2px; }
        .vl-label small { font-size: 12.5px; font-weight: 400; color: #78716C; }
        .vl-chips { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px; }
        .vl-chip { display: inline-flex; align-items: baseline; gap: 6px; padding: 4px 10px; border-radius: 999px; font-size: 13px; font-family: system-ui, sans-serif; border: 1px solid #E8E6E1; background: #FAFAF8; }
        .vl-chip a { color: #1C1917; text-decoration: none; }
        .vl-chip a:hover { text-decoration: underline; }
        .vl-chip em { font-style: normal; font-size: 11px; color: #78716C; }
        .vl-chip-heard { background: #EEF8F5; border-color: #C6E7E1; }
        .vl-chip-umbrella { background: #FBF3E2; border-color: #EBD6A8; }
        .cs-sheet { background: #FFFDF7; border: 1px solid #E8E1CF; border-radius: 6px; box-shadow: 0 1px 0 #E8E1CF, 0 6px 14px rgba(28,25,23,0.06); background-image: repeating-linear-gradient(to bottom, transparent 0, transparent 27px, #EFE8D6 27px, #EFE8D6 28px); }
        .cs-top { display: flex; justify-content: space-between; gap: 10px; flex-wrap: wrap; padding: 10px 16px; background: #F4EFE2; border-bottom: 1px solid #E8E1CF; border-radius: 6px 6px 0 0; font-family: system-ui, sans-serif; font-size: 12.5px; color: #57534E; }
        .cs-top b { color: #0E5F56; }
        .cs-parts { list-style: none; margin: 0; padding: 6px 0; counter-reset: cs; }
        .cs-part { display: grid; grid-template-columns: 170px minmax(0, 1fr); gap: 14px; padding: 10px 16px; align-items: start; }
        .cs-margin { display: flex; flex-direction: column; gap: 4px; border-right: 2px solid #E9B9A8; padding-right: 12px; font-family: system-ui, sans-serif; }
        .cs-tag { align-self: flex-start; font-size: 10.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #FFFFFF; background: #0E5F56; border-radius: 4px; padding: 2px 6px; }
        .cs-what { font-size: 12.5px; color: #57534E; line-height: 1.4; }
        .cs-body { margin: 0; font-family: Georgia, 'Times New Roman', serif; font-style: italic; font-size: 15px; line-height: 1.6; color: #292524; }
        .cs-foot { padding: 10px 16px; border-top: 1px dashed #E8E1CF; font-family: system-ui, sans-serif; font-size: 12.5px; color: #57534E; }
        .blog-mirror-row { display: grid; grid-template-columns: minmax(0, 1fr) 132px minmax(0, 1fr); gap: 10px; align-items: center; padding: 6px 0; font-family: system-ui, sans-serif; }
        .blog-mirror-head { font-size: 11.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #7A746B; padding-bottom: 8px; }
        .blog-mirror-side { display: flex; align-items: center; gap: 8px; height: 22px; }
        .blog-mirror-side span { display: block; height: 18px; min-width: 3px; }
        .blog-mirror-side b { font-size: 12.5px; font-weight: 700; white-space: nowrap; }
        .blog-mirror-label { text-align: center; font-size: 13px; font-weight: 600; line-height: 1.25; }
        .blog-waffle-wrap { display: grid; grid-template-columns: minmax(150px, 1fr) minmax(0, 260px); gap: 24px; align-items: center; }
        .blog-waffle { display: grid; grid-template-columns: repeat(10, 1fr); gap: 4px; width: 100%; max-width: 260px; justify-self: end; }
        .blog-waffle i { display: block; aspect-ratio: 1 / 1; border-radius: 3px; }
        .blog-rg-strip { list-style: none; margin: 0 0 6px; padding: 18px 0 0; display: grid; grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); gap: 8px; position: relative; font-family: ${T.sans}; }
        .blog-rg-strip::before { content: ""; position: absolute; left: 4px; right: 4px; top: 23px; height: 2px; background: ${T.border}; }
        .blog-rg-ev { position: relative; padding-top: 14px; }
        .blog-rg-ev .blog-rg-dot { position: absolute; top: 0; left: 0; }
        .blog-rg-dot { display: inline-block; width: 12px; height: 12px; border-radius: 50%; border: 2px solid #FFFFFF; box-shadow: 0 0 0 1px ${T.border}; }
        .blog-rg-drug .blog-rg-dot, .blog-rg-key-drug { background: ${T.primary}; }
        .blog-rg-customs .blog-rg-dot, .blog-rg-key-customs { background: #B45309; }
        .blog-rg-date { display: block; font-size: 11.5px; font-weight: 700; color: ${T.text}; margin-top: 4px; }
        .blog-rg-lbl { display: block; font-size: 11.5px; color: ${T.textSecondary}; line-height: 1.35; margin-top: 2px; }
        .blog-rg-row { display: grid; grid-template-columns: minmax(200px, 1.25fr) 1fr 1fr; gap: 16px; padding: 14px 0 14px 12px; border-top: 1px solid ${T.borderLight}; font-family: ${T.sans}; border-left: 3px solid transparent; }
        .blog-rg-st-closed { border-left-color: #9F1239; }
        .blog-rg-st-closing { border-left-color: #B45309; }
        .blog-rg-st-open { border-left-color: ${T.primary}; }
        .blog-rg-st-narrow { border-left-color: ${T.primaryMedium}; }
        ${PLAIN_CALLOUTS ? `.blog-rg-row { border-left: none; padding-left: 0; }` : ""}
        .blog-rg-route { display: flex; gap: 10px; align-items: flex-start; }
        .blog-rg-col { font-size: 13px; color: ${T.textSecondary}; line-height: 1.5; }
        .blog-rg-h { display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: ${T.textMuted}; margin-bottom: 3px; }
        .blog-rg-badge { display: inline-block; font-size: 11.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px; margin-top: 5px; }
        .blog-rg-badge-closed { background: #FFF1F2; color: #9F1239; }
        .blog-rg-badge-closing { background: #FFFBEB; color: #92400E; }
        .blog-rg-badge-open { background: ${T.primaryLight}; color: ${T.primaryDark}; }
        .blog-rg-badge-narrow { background: #FFFFFF; color: ${T.primaryDark}; border: 1px solid ${T.primaryMedium}; }
        @media (max-width: 680px) {
          .blog-rg-strip { grid-auto-flow: row; grid-auto-columns: auto; padding: 0 0 0 4px; gap: 10px; }
          .blog-rg-strip::before { left: 9px; right: auto; top: 6px; bottom: 6px; width: 2px; height: auto; }
          .blog-rg-ev { padding: 0 0 0 24px; }
          .blog-rg-ev .blog-rg-dot { top: 2px; left: 0; }
          .blog-rg-date { display: inline; margin: 0 6px 0 0; }
          .blog-rg-lbl { display: inline; }
          .blog-rg-row { grid-template-columns: 1fr; gap: 8px; }
        }
        .blog-ap { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; align-items: start; }
        .blog-ap-body { display: flex; justify-content: center; align-items: flex-start; gap: 10px; }
        .blog-ap-tags { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 8px; max-width: 120px; }
        .blog-ap-tag { font-family: ${T.sans}; font-size: 12px; line-height: 1.3; color: ${T.text}; background: ${T.primaryLight}; border: 1px solid ${T.primaryMedium}; border-radius: 6px; padding: 6px 8px; position: relative; }
        .blog-ap-tag::before { content: ""; position: absolute; left: -10px; top: 12px; width: 10px; height: 2px; background: ${T.primaryMedium}; }
        .blog-ap-year { display: block; font-weight: 700; color: ${T.primary}; font-size: 11px; letter-spacing: 0.04em; }
        .blog-ap-none { background: #FFF1F2; border-color: #FBCFE8; color: #9F1239; font-weight: 600; }
        .blog-ap-none::before { background: #FBCFE8; }
        .blog-ap-key { display: inline-block; width: 12px; height: 12px; border-radius: 50%; }
        @media (max-width: 560px) { .blog-ap { grid-template-columns: 1fr; gap: 24px; } }
        .blog-tt-key { display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 12px; color: ${T.textMuted}; font-family: ${T.sans}; margin-bottom: 14px; }
        .blog-tt-key > span { display: inline-flex; align-items: center; gap: 6px; }
        .blog-tt-row { display: grid; grid-template-columns: 200px minmax(0, 1fr); gap: 12px; align-items: start; padding: 9px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-tt-label { line-height: 1.3; min-width: 0; }
        .blog-tt-sub { display: block; font-size: 11.5px; color: ${T.textMuted}; margin-top: 2px; font-family: ${T.sans}; line-height: 1.35; }
        .blog-tt-flag { display: block; font-size: 11px; color: #9F1239; font-weight: 600; margin-top: 2px; font-family: ${T.sans}; }
        .blog-tt-tiles { display: flex; flex-wrap: wrap; gap: 3px; padding-top: 3px; }
        .blog-tt-tile { position: relative; display: inline-block; width: 11px; height: 11px; border-radius: 2px; box-sizing: border-box; flex: 0 0 auto; }
        .blog-tt-i { background: ${T.primary}; }
        .blog-tt-t { background: #DCEEE9; border: 1.5px solid ${T.primary}; }
        .blog-tt-o { background: repeating-linear-gradient(135deg, #CFCAC0 0 2px, #F3F1EC 2px 4px); border: 1px solid #CFCAC0; }
        .blog-tt-us::after { content: ""; position: absolute; right: -2px; bottom: -2px; width: 5px; height: 5px; border-radius: 50%; background: #B45309; border: 1px solid #fff; }
        @media (max-width: 640px) { .blog-tt-row { grid-template-columns: minmax(0, 1fr); gap: 6px; } }
        .blog-rr-wrap { overflow-x: auto; }
        .blog-rr { width: 100%; min-width: 560px; height: auto; display: block; }
        .blog-rr-list { display: none; }
        @media (max-width: 640px) { .blog-rr-wrap { display: none; } .blog-rr-list { display: block; margin: 0 0 10px; padding-left: 18px; font-size: 13.5px; line-height: 1.5; color: ${T.textSecondary}; font-family: ${T.sans}; } .blog-rr-list li { margin-bottom: 6px; } }
        .blog-ps { position: relative; padding-left: 0; }
        .blog-ps-stage { position: relative; display: grid; grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); gap: 14px; align-items: center; padding: 14px 0 12px; }
        .blog-ps-mesh { position: absolute; left: 0; right: 0; bottom: 0; height: 9px; border-radius: 5px; background-image: radial-gradient(circle, ${T.surface} 2.2px, transparent 2.6px), linear-gradient(90deg, #C9C3B8, #A9A295); background-size: 9px 9px, 100% 100%; }
        .blog-ps-gate { display: flex; gap: 10px; align-items: flex-start; font-family: ${T.sans}; font-size: 14px; color: ${T.text}; line-height: 1.35; }
        .blog-ps-gate strong { display: block; font-weight: 700; }
        .blog-ps-gate em { display: block; font-style: normal; font-size: 12px; color: ${T.textMuted}; margin-top: 2px; }
        .blog-ps-num { flex: none; width: 24px; height: 24px; border-radius: 50%; background: ${T.text}; color: #fff; font: 700 12px/24px ${T.sans}; text-align: center; }
        .blog-ps-held, .blog-ps-tray ul { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 8px; }
        .blog-ps-pill, .blog-ps-pending { position: relative; display: inline-flex; flex-direction: column; padding: 6px 12px 6px 30px; border-radius: 999px; font: 600 13px/1.25 ${T.sans}; background: ${T.primaryLight}; border: 1.5px solid ${T.primary}; color: ${T.text}; max-width: 100%; }
        .blog-ps-pill a, .blog-ps-pending a { color: inherit; text-decoration: none; border-bottom: 1px dotted ${T.textMuted}; align-self: flex-start; }
        .blog-ps-caught { background: #F4F1EC; border-color: #B9B1A4; color: ${T.textSecondary}; }
        .blog-ps-cap { position: absolute; left: 7px; top: 50%; width: 15px; height: 9px; margin-top: -4.5px; border-radius: 5px; background: linear-gradient(90deg, ${T.primary} 50%, #fff 50%); border: 1px solid ${T.primary}; }
        .blog-ps-caught .blog-ps-cap { background: linear-gradient(90deg, #A9A295 50%, #fff 50%); border-color: #A9A295; }
        .blog-ps-why { font-weight: 400; font-size: 11.5px; color: ${T.textMuted}; margin-top: 1px; }
        .blog-ps-empty { font: 400 12.5px/1.4 ${T.sans}; color: ${T.textMuted}; font-style: italic; padding: 6px 0; }
        .blog-ps-tray { margin-top: 14px; padding: 14px; border-radius: 12px; background: repeating-linear-gradient(135deg, #FAF8F4 0 8px, #F3EFE8 8px 16px); border: 1.5px solid #D9D2C6; }
        .blog-ps-tray-label { font: 700 11px/1 ${T.sans}; letter-spacing: 0.1em; text-transform: uppercase; color: ${T.textMuted}; margin-bottom: 10px; }
        .blog-ps-pending { background: #fff; border-style: dashed; border-color: #B45309; }
        .blog-ps-pending .blog-ps-cap { background: repeating-linear-gradient(90deg, #B45309 0 3px, #fff 3px 5px); border-color: #B45309; }
        @media (max-width: 600px) { .blog-ps-stage { grid-template-columns: minmax(0, 1fr); gap: 8px; } }
        .blog-dp { display: grid; grid-template-columns: 1fr 1fr; gap: 26px; }
        .blog-dp-group { min-width: 0; }
        .blog-dp-label { font: 700 15px/1.3 ${T.serif}; color: ${T.text}; margin-bottom: 10px; }
        .blog-dp-label span { display: block; font: 400 12px/1.4 ${T.sans}; color: ${T.textMuted}; margin-top: 2px; }
        .blog-dp-plot { position: relative; display: flex; gap: 18px; justify-content: center; padding-top: 22px; }
        .blog-dp-zero { position: absolute; left: 0; right: 0; top: 44px; border-top: 2px solid ${T.text}; }
        .blog-dp-zero span { position: absolute; left: 0; top: -18px; font: 600 11px/1 ${T.sans}; color: ${T.textMuted}; }
        .blog-dp-col { display: flex; flex-direction: column; align-items: center; width: 92px; }
        .blog-dp-period { font: 700 11px/1 ${T.sans}; letter-spacing: 0.08em; text-transform: uppercase; color: ${T.textMuted}; height: 22px; }
        .blog-dp-well { position: relative; width: 46px; }
        .blog-dp-bar { position: relative; width: 46px; border-radius: 0 0 23px 23px; }
        .blog-dp-drug { background: linear-gradient(180deg, ${T.primary}, #2C8F82); }
        .blog-dp-placebo { background: linear-gradient(180deg, #9A948A, #BDB7AD); }
        .blog-dp-drip { position: absolute; left: 50%; bottom: -7px; width: 8px; height: 8px; margin-left: -4px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); background: inherit; opacity: 0.7; }
        .blog-dp-val { position: absolute; left: 50%; transform: translateX(-50%); margin-top: 6px; font: 700 15px/1 ${T.sans}; color: ${T.text}; white-space: nowrap; }
        .blog-dp-on { margin-top: 8px; text-align: center; font: 600 12px/1.3 ${T.sans}; color: ${T.textSecondary}; }
        .blog-dp-on span { display: block; font-weight: 400; color: ${T.textMuted}; }
        .blog-dp-verdict { margin-top: 10px; font: 400 13px/1.5 ${T.sans}; color: ${T.textSecondary}; text-align: center; }
        .blog-dp-flag { margin-top: 16px; padding: 9px 12px; border-left: 3px solid #B45309; background: #FFF7ED; font: 400 12.5px/1.5 ${T.sans}; color: #7C2D12; border-radius: 0 8px 8px 0; }
        ${PLAIN_CALLOUTS ? `.blog-dp-flag { margin-top: 18px; padding: 12px 0 0; border-left: none; border-top: 1px solid ${T.border}; background: none; border-radius: 0; font: 500 13px/1.55 ${T.sans}; color: #92400E; }` : ""}
        @media (max-width: 600px) { .blog-dp { grid-template-columns: minmax(0, 1fr); gap: 30px; } }
        .blog-td-key { display: flex; flex-wrap: wrap; gap: 8px 18px; font: 400 12.5px/1.4 ${T.sans}; color: ${T.textSecondary}; margin-bottom: 14px; }
        .blog-td-key span { display: inline-flex; align-items: center; gap: 7px; }
        .blog-td-key b { color: ${T.text}; }
        .blog-td-key .blog-td-door { width: 14px; height: 20px; }
        .blog-bm-grid { display: grid; grid-template-columns: minmax(0, 1fr) 190px minmax(0, 1fr); gap: 16px; align-items: center; }
        .blog-bm-col { display: flex; flex-direction: column; gap: 12px; }
        .blog-bm-fig { width: 100%; max-height: 360px; }
        .blog-bm-card { border: 1px solid ${T.border}; border-radius: 12px; padding: 12px 14px; background: ${T.bg}; font-family: ${T.sans}; }
        .blog-bm-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
        .blog-bm-num { width: 22px; height: 22px; border-radius: 50%; background: ${T.primary}; color: #fff; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex: none; }
        .blog-bm-label { font-size: 14.5px; font-weight: 700; color: ${T.text}; flex: 0 1 auto; min-width: 0; line-height: 1.3; }
        .blog-bm-tag { flex: none; font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #92400E; background: #FEF3C7; border-radius: 999px; padding: 2px 8px; }
        .blog-bm-measure { font-size: 12.5px; color: ${T.textMuted}; margin-bottom: 8px; line-height: 1.4; }
        .blog-bm-row { font-size: 13.5px; color: ${T.text}; line-height: 1.45; margin-bottom: 5px; }
        .blog-bm-who { display: inline-block; min-width: 58px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-right: 6px; }
        .blog-bm-drug { color: ${T.primaryDark}; }
        .blog-bm-pbo { color: ${T.textMuted}; }
        .blog-bm-detail { font-size: 12.5px; color: ${T.textSecondary}; line-height: 1.5; margin-top: 6px; }
        @media (max-width: 720px) {
          .blog-bm-grid { grid-template-columns: minmax(0, 1fr); }
          .blog-bm-fig { max-height: 250px; order: -1; }
        }
        .blog-ff-prompt { font: 700 13px ${T.sans}; color: ${T.text}; margin-bottom: 8px; }
        .blog-ff-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 18px; }
        .blog-ff-chips button { display: inline-flex; align-items: center; gap: 7px; border: 1px solid ${T.border}; background: ${T.surface}; border-radius: 999px; padding: 7px 13px 7px 9px; font: 600 12.5px ${T.sans}; color: ${T.textSecondary}; cursor: pointer; text-align: left; line-height: 1.35; }
        .blog-ff-chips button.on { border-color: ${T.primary}; background: ${T.primaryLight}; color: ${T.primaryDark}; }
        .blog-ff-box { width: 16px; height: 16px; border-radius: 4px; border: 1.5px solid ${T.textMuted}; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; line-height: 1; color: #fff; }
        .blog-ff-chips button.on .blog-ff-box { background: ${T.primary}; border-color: ${T.primary}; }
        .blog-ff-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 12px; }
        .blog-ff-card { border: 1px solid ${T.border}; border-radius: 12px; padding: 14px 14px 12px; background: ${T.bg}; display: flex; flex-direction: column; }
        .blog-ff-head { display: flex; flex-direction: column; gap: 2px; font-family: ${T.sans}; margin-bottom: 10px; }
        .blog-ff-head a, .blog-ff-head b { font-size: 16px; font-weight: 700; color: ${T.text}; text-decoration: none; }
        .blog-ff-head a { border-bottom: 1px dotted ${T.textMuted}; align-self: flex-start; }
        .blog-ff-head span { font-size: 12px; color: ${T.textMuted}; }
        .blog-ff-head em { font-style: normal; align-self: flex-start; margin-top: 4px; font-size: 11.5px; line-height: 1.3; font-weight: 700; color: #B45309; background: #FFF7ED; border: 1px solid #FED7AA; border-radius: 999px; padding: 3px 9px; white-space: nowrap; }
        .blog-ff-resp { margin-bottom: 10px; }
        .blog-ff-resp-label { font: 600 12px/1.4 ${T.sans}; color: ${T.textSecondary}; margin-bottom: 8px; }
        .blog-ff-row { display: grid; grid-template-columns: 1fr auto; grid-template-areas: "who val" "bar bar"; align-items: baseline; column-gap: 8px; row-gap: 4px; margin-bottom: 9px; font-family: ${T.sans}; }
        .blog-ff-who { grid-area: who; font-size: 12px; font-weight: 600; color: ${T.text}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .blog-ff-row.pl .blog-ff-who { color: ${T.textMuted}; font-weight: 500; }
        .blog-ff-track { grid-area: bar; position: relative; height: 10px; background: ${T.borderLight}; border-radius: 999px; overflow: hidden; }
        .blog-ff-track i { position: absolute; left: 0; top: 0; bottom: 0; background: ${T.primary}; border-radius: 999px; }
        .blog-ff-row.pl .blog-ff-track i { background: #A8A29E; }
        .blog-ff-row b { grid-area: val; font-size: 13px; font-weight: 700; color: ${T.text}; text-align: right; font-variant-numeric: tabular-nums; }
        .blog-ff-row.pl b { color: ${T.textMuted}; font-weight: 600; }
        .blog-ff-gap { font: 700 12.5px/1.4 ${T.sans}; color: ${T.primaryDark}; margin-top: 4px; }
        .blog-ff-gap small { display: block; font-weight: 400; font-size: 11px; color: ${T.textMuted}; }
        .blog-ff-facts { list-style: none; margin: 0 0 10px; padding: 0; font: 400 12.5px/1.45 ${T.sans}; color: ${T.textSecondary}; }
        .blog-ff-facts li { padding: 4px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-ff-for { margin-top: auto; }
        .blog-ff-empty { font: 400 12px/1.45 ${T.sans}; color: ${T.textMuted}; margin: 0; font-style: italic; }
        .blog-ff-ans { display: flex; gap: 9px; padding: 8px 9px; border-radius: 8px; margin-top: 6px; font-family: ${T.sans}; }
        .blog-ff-ans b { font-size: 11.5px; color: ${T.textMuted}; font-weight: 600; }
        .blog-ff-ans p { margin: 1px 0 0; font-size: 12.5px; line-height: 1.45; color: ${T.text}; }
        .blog-ff-ans.plus { background: #EEF8F5; }
        .blog-ff-ans.caution { background: #FFF7ED; }
        .blog-ff-ans.neutral { background: ${T.borderLight}; }
        .blog-ff-mark { flex: none; width: 18px; height: 18px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font: 800 12px ${T.sans}; color: #fff; margin-top: 1px; }
        .blog-ff-ans.plus .blog-ff-mark { background: ${T.primary}; }
        .blog-ff-ans.caution .blog-ff-mark { background: #B45309; }
        .blog-ff-ans.neutral .blog-ff-mark { background: #A8A29E; }
        .blog-wd-top { display: flex; justify-content: space-between; align-items: flex-end; gap: 14px; flex-wrap: wrap; margin-bottom: 8px; }
        .blog-wd-read { display: flex; flex-direction: column; font-family: ${T.sans}; }
        .blog-wd-kg { font-family: ${T.serif}; font-size: 40px; font-weight: 700; color: ${T.primaryDark}; line-height: 1; font-variant-numeric: tabular-nums; }
        .blog-wd-kg small { font-size: 18px; font-weight: 600; color: ${T.textSecondary}; }
        .blog-wd-lb { font-size: 12.5px; color: ${T.textMuted}; margin-top: 4px; }
        .blog-wd-doses { display: inline-flex; border: 1px solid ${T.border}; border-radius: 999px; padding: 3px; background: ${T.bg}; }
        .blog-wd-doses button { border: none; background: transparent; border-radius: 999px; padding: 6px 12px; font: 600 12.5px ${T.sans}; color: ${T.textSecondary}; cursor: pointer; }
        .blog-wd-doses button.on { background: ${T.primary}; color: #fff; }
        .blog-wd-ruler { position: relative; padding: 6px 0 22px; }
        .blog-wd-ruler input { width: 100%; margin: 0; accent-color: ${T.primary}; height: 28px; cursor: pointer; background: repeating-linear-gradient(90deg, ${T.border} 0 1px, transparent 1px 2.2%); background-size: 100% 10px; background-repeat: no-repeat; background-position: 0 50%; }
        .blog-wd-ticks { position: absolute; left: 8px; right: 8px; bottom: 0; height: 16px; font: 600 10.5px ${T.sans}; color: ${T.textMuted}; }
        .blog-wd-ticks span { position: absolute; transform: translateX(-50%); }
        .blog-wd-presets { display: flex; flex-wrap: wrap; gap: 6px; margin: 4px 0 16px; }
        .blog-wd-presets button { border: 1px solid ${T.border}; background: ${T.surface}; border-radius: 999px; padding: 5px 11px; font: 600 12px ${T.sans}; color: ${T.textSecondary}; cursor: pointer; }
        .blog-wd-presets button.on { border-color: ${T.primary}; color: ${T.primary}; background: ${T.primaryLight}; }
        .blog-wd-rows { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
        .blog-wd-rows li { display: grid; grid-template-columns: 170px minmax(0, 1fr) 128px; grid-template-areas: "name bar val" "name vials vials"; column-gap: 12px; row-gap: 4px; align-items: center; padding: 10px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-wd-rows li.flat { grid-template-areas: "name bar val"; }
        .blog-wd-name { grid-area: name; display: flex; flex-direction: column; font-family: ${T.sans}; align-self: start; }
        .blog-wd-name a, .blog-wd-name b { font-size: 14px; font-weight: 700; color: ${T.text}; text-decoration: none; }
        .blog-wd-name a { border-bottom: 1px dotted ${T.textMuted}; align-self: flex-start; }
        .blog-wd-name span { font-size: 11.5px; color: ${T.textMuted}; line-height: 1.35; margin-top: 2px; }
        .blog-wd-bar { grid-area: bar; height: 16px; background: ${T.borderLight}; border-radius: 4px; overflow: hidden; }
        .blog-wd-bar i { display: block; height: 100%; border-radius: 4px; transition: width 0.25s ease; }
        .blog-wd-bar i.inf { background: linear-gradient(90deg, ${T.primaryMedium}, ${T.primary}); }
        .blog-wd-bar i.pill { background: repeating-linear-gradient(135deg, #F2C48D 0 6px, #E8A85E 6px 12px); }
        .blog-wd-val { grid-area: val; display: flex; flex-direction: column; text-align: right; font-family: ${T.sans}; }
        .blog-wd-val b { font-size: 15px; color: ${T.text}; font-variant-numeric: tabular-nums; }
        .blog-wd-val span { font-size: 11px; color: ${T.textMuted}; }
        .blog-wd-vials { grid-area: vials; display: flex; flex-wrap: wrap; align-items: center; gap: 3px; }
        .blog-wd-vials i { width: 7px; height: 14px; border-radius: 2px 2px 3px 3px; background: ${T.primary}; opacity: 0.85; position: relative; }
        .blog-wd-vials i::before { content: ""; position: absolute; top: -3px; left: 1px; right: 1px; height: 3px; background: #9CA3AF; border-radius: 1px; }
        .blog-wd-vials i.part { background: linear-gradient(0deg, ${T.primary} var(--fill), ${T.borderLight} var(--fill)); outline: 1px solid ${T.primaryMedium}; }
        .blog-wd-vials span { font: 11.5px ${T.sans}; color: ${T.textMuted}; margin-left: 6px; }
        @media (max-width: 640px) {
          .blog-wd-rows li, .blog-wd-rows li.flat { grid-template-columns: minmax(0, 1fr) auto; grid-template-areas: "name val" "bar bar" "vials vials"; }
          .blog-wd-rows li.flat { grid-template-areas: "name val" "bar bar"; }
          .blog-wd-kg { font-size: 34px; }
        }
        .blog-td-wall { list-style: none; margin: 0; padding: 14px 10px 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(66px, 1fr)); gap: 16px 8px; background: linear-gradient(180deg, #F7F4EE 0, #F7F4EE calc(100% - 6px), #CFC7B9 calc(100% - 6px)); border-radius: 10px 10px 4px 4px; }
        .blog-td-wall li { display: flex; flex-direction: column; align-items: center; gap: 5px; min-width: 0; }
        .blog-td-wall a { display: block; }
        i.blog-td-door, .blog-td-wall .blog-td-leaf { display: inline-block; position: relative; width: 30px; height: 44px; border-radius: 15px 15px 2px 2px; }
        .blog-td-shut .blog-td-leaf, i.blog-td-shut { background: linear-gradient(90deg, #3F3A33, #57504A); }
        .blog-td-unmarked .blog-td-leaf, i.blog-td-unmarked { background: #fff; border: 1.6px solid #8A8378; }
        .blog-td-open .blog-td-leaf, i.blog-td-open { background: #0E5F56; box-shadow: inset -12px 0 0 #DFF2EE; border: 1.6px solid #0E5F56; transform: perspective(60px) rotateY(-24deg); transform-origin: left center; }
        .blog-td-knob { position: absolute; right: 5px; top: 52%; width: 4px; height: 4px; border-radius: 50%; background: #E3B341; }
        .blog-td-open .blog-td-knob { right: 15px; }
        .blog-td-us .blog-td-leaf::after, i.blog-td-us::after { content: ""; position: absolute; left: -4px; top: -4px; width: 9px; height: 9px; border-radius: 50%; background: #B42318; border: 1.5px solid #fff; }
        .blog-td-name { font: 600 10.5px/1.2 ${T.sans}; color: ${T.textSecondary}; text-align: center; max-width: 80px; overflow-wrap: break-word; hyphens: auto; }
        .blog-td-list { list-style: none; margin: 16px 0 0; padding: 12px 0 0; border-top: 1px solid ${T.borderLight}; display: grid; gap: 6px; font: 400 13px/1.45 ${T.sans}; color: ${T.textSecondary}; }
        .blog-td-list a { color: ${T.primary}; font-weight: 600; }

        .blog-ag { position: relative; font-family: ${T.sans}; --ag-lab: 190px; }
        .blog-ag-zones { position: relative; height: 26px; margin-left: var(--ag-lab); }
        .blog-ag-zone { position: absolute; top: 0; bottom: 0; display: flex; align-items: center; justify-content: center; border-radius: 6px 6px 0 0; font-size: 11.5px; font-weight: 700; letter-spacing: 0.02em; color: ${T.primaryDark}; }
        .blog-ag-zone span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 0 6px; }
        .blog-ag-zone-0 { background: #F3E8D7; color: #7C4A03; }
        .blog-ag-zone-1 { background: ${T.primaryLight}; }
        .blog-ag-body { position: relative; }
        .blog-ag-gap { position: absolute; top: 0; bottom: 0; left: var(--ag-lab); background: repeating-linear-gradient(135deg, rgba(159,18,57,0.06) 0 8px, rgba(159,18,57,0.11) 8px 16px); border-left: 2px solid #9F1239; border-right: 2px dashed #9F1239; pointer-events: none; }
        .blog-ag-gapkey { background: repeating-linear-gradient(135deg, rgba(159,18,57,0.12) 0 4px, rgba(159,18,57,0.22) 4px 8px); border: 1px solid #9F1239; }
        .blog-ag-row { display: grid; grid-template-columns: var(--ag-lab) 1fr; align-items: center; padding: 10px 0; border-bottom: 1px dashed ${T.border}; }
        .blog-ag-label { padding-right: 12px; }
        .blog-ag-track { position: relative; height: 30px; }
        .blog-ag-bar { position: absolute; top: 3px; bottom: 3px; box-sizing: border-box; border-radius: 6px; display: flex; align-items: center; padding: 0 10px; overflow: hidden; }
        .blog-ag-bar span { font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .blog-ag-approved { background: #0E5F56; color: #FFFFFF; }
        .blog-ag-weight { background: repeating-linear-gradient(90deg, #0E5F56 0 6px, #2E8A7E 6px 12px); color: #FFFFFF; opacity: 0.85; }
        .blog-ag-trial { background: #475569; color: #FFFFFF; }
        .blog-ag-pending { background: #FFFFFF; border: 2px dashed #B45309; color: #7C4A03; }
        .blog-ag-gate { position: absolute; top: -4px; bottom: -4px; width: 3px; margin-left: -1px; background: #111827; border-radius: 2px; }
        .blog-ag-axis { position: relative; height: 22px; margin-left: var(--ag-lab); border-top: 2px solid ${T.text}; }
        .blog-ag-axis span { position: absolute; top: 4px; transform: translateX(-50%); font-size: 11.5px; color: ${T.textMuted}; }
        .blog-ag-axis span:first-child { transform: none; }
        .blog-ag-axis span:last-child { transform: translateX(-100%); }
        .blog-ag-key { display: inline-block; width: 22px; height: 12px; border-radius: 3px; position: static; }
        @media (max-width: 680px) {
          .blog-ag { --ag-lab: 0px; }
          .blog-ag-row { grid-template-columns: 1fr; gap: 6px; }
          .blog-ag-label { position: relative; z-index: 2; }
          .blog-ag-label > span:first-child { background: ${T.surface}; padding-right: 6px; }
          .blog-ag-label > span:last-child { display: inline-block; background: ${T.surface}; padding-right: 6px; }
          .blog-ag-zone { font-size: 10.5px; }
        }
        .blog-fp-row { display: grid; grid-template-columns: 26px 150px 1fr; gap: 12px; padding: 12px 0; border-top: 1px solid ${T.borderLight}; align-items: start; }
        .blog-fp-row:first-child { border-top: none; }
        .blog-fp-num { width: 22px; height: 22px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; font-family: ${T.sans}; box-sizing: border-box; margin-top: 1px; }
        .blog-fp-when { font-size: 12.5px; font-weight: 700; color: ${T.textSecondary}; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; line-height: 1.4; padding-top: 2px; }
        @media (max-width: 640px) {
          .blog-fp-wide { display: none !important; }
          .blog-fp-tall { display: block !important; }
          .blog-fp-row { grid-template-columns: 26px 1fr; }
          .blog-fp-row > div:last-child { grid-column: 2; }
        }
        .blog-br-row { display: grid; grid-template-columns: 170px minmax(0, 1fr) 140px; gap: 14px; align-items: center; padding: 10px 0; border-top: 1px solid ${T.borderLight}; font-family: ${T.sans}; }
        .blog-br-strip-row { border-top: none; padding-top: 0; }
        .blog-br-label { font-size: 13px; font-weight: 600; color: ${T.text}; line-height: 1.3; }
        .blog-br-strip { position: relative; height: 44px; border-radius: 6px; background: linear-gradient(180deg, #F6F1E7, #EFE8DA); border: 1px solid #E3DBC9; box-shadow: inset 0 1px 2px rgba(0,0,0,0.06); }
        .blog-br-axis { position: relative; height: 42px; }
        .blog-br-track { position: relative; height: 30px; }
        .blog-br-track::before { content: ""; position: absolute; left: 0; right: 0; top: 50%; height: 1px; background: ${T.borderLight}; }
        .blog-br-need { font-size: 12.5px; font-weight: 700; color: ${T.primaryDark}; line-height: 1.3; }
        .blog-br-group { font-size: 12px; font-weight: 700; color: ${T.textMuted}; padding: 16px 0 2px; font-family: ${T.sans}; }
        @media (max-width: 640px) {
          .blog-br-row { grid-template-columns: 1fr; gap: 6px; }
          .blog-br-strip-row .blog-br-label { display: none; }
          .blog-br-name { display: none !important; }
          .blog-br-need { font-size: 12px; }
          .blog-br-need:empty { display: none; }
        }
        .blog-cg-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .blog-cg-mobile { display: none; }
        .blog-cg-mrow { padding: 12px 0; border-top: 1px solid ${T.borderLight}; font-family: ${T.sans}; }
        .blog-cg-mrow:first-child { border-top: none; padding-top: 0; }
        .blog-cg-chip { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; padding: 3px 8px 3px 5px; border-radius: 999px; line-height: 1.3; }
        .blog-cg-chip .blog-cg-mark { width: 12px; height: 12px; }
        .blog-cg-chip-yes { background: ${T.primaryLight}; color: ${T.primaryDark}; font-weight: 600; }
        .blog-cg-chip-partly { background: #FFFFFF; color: ${T.text}; border: 1px solid ${T.primaryMedium}; }
        .blog-cg-chip-no { background: #FFFFFF; color: ${T.textMuted}; border: 1px solid ${T.borderLight}; }
        @media (max-width: 640px) {
          .blog-cg-mobile { display: block; }
          .blog-cg-scroll { display: none; }
        }
        .blog-cg { display: grid; min-width: 620px; font-family: ${T.sans}; }
        .blog-cg-head { font-size: 11.5px; font-weight: 700; color: ${T.textMuted}; padding: 0 6px 10px; line-height: 1.3; border-bottom: 2px solid ${T.border}; }
        .blog-cg-colhead { text-align: center; }
        .blog-cg-rowhead { font-size: 13px; font-weight: 600; color: ${T.text}; padding: 12px 10px 12px 0; border-bottom: 1px solid ${T.borderLight}; line-height: 1.3; }
        .blog-cg-cell { display: flex; align-items: center; justify-content: center; border-bottom: 1px solid ${T.borderLight}; padding: 12px 4px; }
        .blog-cg-mark { display: inline-block; width: 16px; height: 16px; border-radius: 50%; box-sizing: border-box; flex: none; }
        .blog-cg-yes { background: ${T.primary}; border: 2px solid ${T.primary}; }
        .blog-cg-partly { background: linear-gradient(90deg, ${T.primary} 50%, #FFFFFF 50%); border: 2px solid ${T.primary}; }
        .blog-cg-no { background: #FFFFFF; border: 2px solid #D6D3CD; }
        .blog-dt-row { display: grid; grid-template-columns: 170px minmax(0, 1fr) 210px; gap: 14px; align-items: start; padding: 14px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-dt-row:first-child { border-top: none; padding-top: 4px; }
        .blog-dt-flow { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .blog-dt-chips { display: flex; flex-wrap: wrap; gap: 5px; }
        .blog-dt-chip { font-size: 12px; font-family: ${T.sans}; padding: 3px 8px; border-radius: 6px; line-height: 1.35; }
        .blog-dt-data { background: #FEF3C7; color: #78350F; border: 1px solid #F3D99B; }
        .blog-dt-to { background: #EEF2F6; color: #1E293B; border: 1px solid #D5DCE4; font-weight: 600; }
        .blog-dt-arrow { font-size: 18px; color: ${T.textMuted}; font-family: ${T.sans}; }
        .blog-dt-outcome { font-size: 12.5px; color: ${T.textSecondary}; font-family: ${T.sans}; line-height: 1.45; }
        @media (max-width: 720px) {
          .blog-dt-row { grid-template-columns: 1fr !important; gap: 8px !important; }
        }
        .blog-cu-row { display: grid; grid-template-columns: 170px minmax(0, 1fr); gap: 14px; align-items: start; padding: 8px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-cu-axis { border-top: none; padding: 0 0 2px; }
        .blog-cu-label { font-size: 13px; font-weight: 600; color: ${T.text}; font-family: ${T.sans}; line-height: 1.3; padding-top: 8px; }
        .blog-cu-track { position: relative; min-height: 36px; }
        @media (max-width: 640px) {
          .blog-cu-row { grid-template-columns: 1fr !important; gap: 2px !important; }
          .blog-cu-axis .blog-cu-label { display: none; }
        }
        .blog-ld-row { display: grid; grid-template-columns: 190px minmax(0, 1fr); gap: 14px; align-items: center; padding: 10px 0; }
        .blog-ld-track { position: relative; height: 26px; margin-right: 8px; }
        @media (max-width: 560px) { .blog-ld-row { grid-template-columns: minmax(0, 1fr); gap: 4px; padding: 9px 0; } .blog-ld-axis .blog-ld-label { display: none; } }
        .blog-dl-row { display: grid; grid-template-columns: 210px minmax(0, 1fr); gap: 28px; padding: 20px 0 22px; }
        .blog-dl-term { font-size: 17px; line-height: 1.35; }
        .blog-dl-def { font-size: 16px; line-height: 1.7; }
        .blog-dl-compact .blog-dl-row { grid-template-columns: 170px minmax(0, 1fr); gap: 20px; padding: 13px 0 14px; }
        .blog-dl-compact .blog-dl-term { font-size: 15px; }
        .blog-dl-compact .blog-dl-def { font-size: 14.5px; line-height: 1.6; }
        @media (max-width: 640px) { .blog-dl-row, .blog-dl-compact .blog-dl-row { grid-template-columns: minmax(0, 1fr); gap: 6px; padding: 16px 0 18px; } .blog-dl-compact .blog-dl-row { padding: 12px 0 13px; } }
        .blog-dot-row { display: grid; grid-template-columns: 190px minmax(0, 1fr); gap: 12px; align-items: center; padding: 5px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-dot-label { font-size: 13px; color: ${T.text}; font-family: ${T.sans}; line-height: 1.3; }
        .blog-dot-track { position: relative; height: 30px; margin-right: 76px; }
        .blog-forest-cell { display: grid; grid-template-columns: minmax(0, 1fr) 150px; gap: 14px; align-items: center; }
        .blog-forest-cell .blog-dot-track { margin-right: 0 !important; }
        .blog-forest-text { font-size: 12.5px; font-weight: 700; color: ${T.text}; font-family: ${T.sans}; line-height: 1.3; }
        .blog-gantt-track { margin-right: 12px !important; }
        @media (max-width: 640px) { .blog-forest-cell { grid-template-columns: minmax(0, 1fr); gap: 2px; } .blog-forest-text { font-size: 11.5px; } }
        .blog-zone-track { margin-right: 96px; }
        .blog-strip-row { display: grid; grid-template-columns: 170px minmax(0, 1fr) 52px; gap: 14px; align-items: center; padding: 10px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-strip-blocks { display: grid; grid-template-columns: repeat(20, 1fr); gap: 3px; }
        .blog-strip-blocks i { display: block; height: 18px; border-radius: 2px; }
        .blog-dumbbell-track { height: 46px !important; }
        .blog-lanes-axis { display: grid; grid-template-columns: 150px minmax(0, 1fr); }
        .blog-lane { display: grid; grid-template-columns: 150px minmax(0, 1fr); border-top: 1px solid ${T.borderLight}; padding: 12px 0 10px; align-items: start; }
        .blog-lanes-keys { border-top: 1px solid ${T.borderLight}; margin-top: 4px; }
        .blog-lane-label { padding: 2px 10px 0 0; }
        .blog-lane-track { position: relative; }
        .blog-lane-span { position: absolute; height: 12px; border-radius: 6px; background: ${T.primaryLight}; border: 1px solid ${T.primary}; overflow: hidden; }
        .blog-lane-span > span { position: absolute; left: 6px; top: -1px; font-size: 10.5px; color: ${T.primaryDark}; font-family: ${T.sans}; white-space: nowrap; line-height: 12px; }
        .blog-lane-point { position: absolute; transform: translateX(-50%); }
        .blog-lane-point > i { display: flex; align-items: center; justify-content: center; width: 20px; height: 20px; border-radius: 50%; border-style: solid; box-sizing: border-box; box-shadow: 0 0 0 2px ${T.surface}; font-style: normal; font-size: 10.5px; font-weight: 800; font-family: ${T.sans}; }
        .blog-lane-key { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 4px 16px; }
        .blog-lane-key li { display: flex; gap: 7px; align-items: flex-start; font-size: 12px; color: ${T.textSecondary}; font-family: ${T.sans}; line-height: 1.4; }
        .blog-lane-key li > i { flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; width: 17px; height: 17px; border-radius: 50%; border: 2px solid; box-sizing: border-box; font-style: normal; font-size: 9.5px; font-weight: 800; margin-top: 1px; }
        .blog-lane-key li > i.blog-lane-key-span { width: 17px; height: 9px; border-radius: 3px; background: ${T.primaryLight}; border: 1px solid ${T.primary}; margin-top: 5px; }
        .blog-lane-key b { color: ${T.text}; font-weight: 700; }
        .blog-verdict-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 16px; }
        .blog-verdict-panel { border: 1px solid ${T.borderLight}; border-radius: 12px; padding: 16px 16px 12px; background: ${T.bg}; }
        .blog-verdict-stamp { font-size: 11px; font-weight: 800; letter-spacing: 0.12em; border: 2px solid; border-radius: 6px; padding: 3px 8px; transform: rotate(-4deg); font-family: ${T.sans}; flex-shrink: 0; }
        .blog-verdict-gauges { display: grid; grid-template-columns: 1fr 56px 1fr; align-items: end; }
        .blog-verdict-gauge { display: flex; flex-direction: column; align-items: center; }
        .blog-verdict-gap { position: relative; }
        .blog-fork { display: grid; grid-template-columns: 150px 1fr; gap: 18px; align-items: center; }
        .blog-fork-node { border: 2px solid ${T.text}; border-radius: 12px; padding: 14px; text-align: center; background: ${T.surface}; position: relative; }
        .blog-fork-node::after { content: ""; position: absolute; right: -18px; top: 50%; width: 18px; height: 2px; background: ${T.border}; }
        .blog-fork-branches { list-style: none; margin: 0; padding: 0 0 0 0; display: grid; gap: 10px; border-left: 2px solid ${T.border}; padding-left: 18px; }
        .blog-fork-card { border: 1px solid ${T.borderLight}; border-left: 4px solid; border-radius: 10px; padding: 12px 14px; background: ${T.bg}; position: relative; }
        .blog-fork-card::before { content: ""; position: absolute; left: -20px; top: 50%; width: 18px; height: 2px; background: ${T.border}; }
        ${PLAIN_CALLOUTS ? `.blog-fork-card { border: 1px solid ${T.border}; background: ${T.surface}; }` : ""}
        .blog-ladder-axis { display: grid; grid-template-columns: 190px minmax(0, 1fr); gap: 12px; margin-bottom: 2px; }
        .blog-jug-grid { display: grid; grid-template-columns: repeat(10, 1fr); gap: 8px 6px; max-width: 420px; }
        .blog-jug { display: block; aspect-ratio: 3 / 4; border: 2px solid; border-radius: 3px 3px 6px 6px; position: relative; box-sizing: border-box; }
        .blog-jug::before { content: ""; position: absolute; top: -6px; left: 30%; width: 40%; height: 5px; border: 2px solid; border-color: inherit; border-bottom: none; border-radius: 2px 2px 0 0; box-sizing: border-box; }
        .blog-jug-track { position: relative; height: 8px; background: ${VIZ_COOL}; border-radius: 4px; margin-top: 16px; max-width: 420px; }
        .blog-jug-track span { position: absolute; top: 0; bottom: 0; background: #9CC5BE; border-radius: 4px; }
        .blog-jug-track b { position: absolute; top: -5px; width: 3px; height: 18px; background: ${T.primaryDark}; border-radius: 2px; transform: translateX(-1px); }
        .blog-jug-facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px 16px; margin: 16px 0 0; padding: 12px 0 0; border-top: 1px solid ${T.borderLight}; }
        .blog-jug-facts dt { font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em; color: ${T.textMuted}; font-family: ${T.sans}; }
        .blog-jug-facts dd { margin: 2px 0 0; font-size: 13.5px; font-weight: 600; color: ${T.text}; font-family: ${T.sans}; line-height: 1.4; }
        .blog-vp-head { display: grid; grid-template-columns: 1fr 1.25fr; gap: 18px; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em; color: ${T.textMuted}; font-family: ${T.sans}; padding-bottom: 8px; border-bottom: 1px solid ${T.border}; }
        .blog-vp-row { display: grid; grid-template-columns: 1fr 1.25fr; gap: 18px; padding: 14px 0; border-bottom: 1px solid ${T.borderLight}; }
        .blog-vp-bar { position: relative; height: 12px; background: ${VIZ_COOL}; border-radius: 6px; margin-top: 8px; margin-right: 44px; }
        .blog-vp-bar span { position: absolute; left: 0; top: 0; bottom: 0; background: ${T.primary}; border-radius: 6px; }
        .blog-vp-bar b { position: absolute; left: calc(100% + 8px); top: -3px; font-size: 13px; color: ${T.text}; font-family: ${T.sans}; }
        .blog-vp-chip { display: inline-block; margin-top: 6px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; font-family: ${T.sans}; }
        @media (max-width: 640px) { .blog-vp-head { display: none; } .blog-vp-row { grid-template-columns: 1fr; gap: 8px; } }
        .blog-thermo-row { display: flex; justify-content: space-around; gap: 12px; padding-top: 18px; }
        .blog-thermo-group { flex: 1 1 0; min-width: 0; max-width: 220px; }
        .blog-thermo { position: relative; display: flex; flex-direction: column; align-items: center; width: 52px; }
        .blog-thermo em { font-style: normal; font-weight: 800; font-size: 16px; font-family: ${T.sans}; margin-bottom: 6px; }
        .blog-thermo-tube { position: relative; width: 22px; border: 2px solid #D6D0C6; border-bottom: none; border-radius: 11px 11px 0 0; background: #FBFAF8; overflow: hidden; box-sizing: border-box; }
        .blog-thermo-tube span { position: absolute; left: 0; right: 0; bottom: 0; }
        .blog-thermo i { display: block; width: 34px; height: 34px; border-radius: 50%; margin-top: -4px; border: 2px solid #D6D0C6; box-sizing: border-box; }
        .blog-cliff-row { display: flex; align-items: flex-start; gap: 10px; padding-top: 8px; }
        .blog-cliff-top { width: 100%; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; border-bottom: 2px solid #D6D0C6; }
        .blog-cliff-col { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; text-align: center; }
        .blog-cliff-col b { font-size: 15px; font-weight: 800; font-family: ${T.sans}; margin-bottom: 6px; white-space: nowrap; }
        .blog-cliff-track { position: relative; width: 100%; max-width: 64px; }
        .blog-cliff-track span { position: absolute; left: 0; right: 0; bottom: 0; border-radius: 6px 6px 0 0; }
        .blog-cliff-col strong { margin-top: 8px; font-size: 13px; color: ${T.text}; font-family: ${T.sans}; }
        .blog-cliff-col small { font-size: 12px; color: ${T.textMuted}; font-family: ${T.sans}; }
        .blog-cliff-col em { font-style: normal; margin-top: 4px; font-size: 11.5px; color: ${T.textSecondary}; font-family: ${T.sans}; line-height: 1.35; }
        .blog-cliff-dose { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 18px; }
        .blog-cliff-dose > div { padding: 8px 12px; background: ${T.bg}; border-radius: 8px; }
        @media (max-width: 640px) { .blog-cliff-col b { font-size: 12.5px; } .blog-cliff-dose { grid-template-columns: 1fr; } }
        @media (max-width: 640px) {
          .blog-faceoff thead { display: none; }
          .blog-faceoff tr { display: block; padding: 10px 4px; }
          .blog-faceoff th[scope="row"] { display: block; padding: 4px 14px 6px !important; }
          .blog-faceoff td { display: block; padding: 4px 14px 8px !important; }
          .blog-faceoff td::before { content: attr(data-label); display: block; font-size: 11.5px; font-weight: 800; letter-spacing: 0.02em; }
          .blog-faceoff td.fo-a::before { color: ${T.primary}; }
          .blog-faceoff td.fo-b::before { color: ${PRICE_B}; }
        }
        .blog-rt-track { position: relative; height: 30px; margin: 10px 12px 6px; }
        .blog-rt-track::before { content: ""; position: absolute; left: 0; right: 0; top: 14px; height: 3px; background: ${VIZ_COOL}; border-radius: 2px; }
        .blog-rt-zero { position: absolute; top: 2px; bottom: 2px; width: 2px; background: ${T.text}; opacity: 0.25; }
        .blog-rt-track i { position: absolute; top: 4px; width: 22px; height: 22px; margin-left: -11px; border-radius: 50%; border: 2px solid #fff; box-sizing: border-box; font-style: normal; font-size: 11px; font-weight: 800; font-family: ${T.sans}; display: flex; align-items: center; justify-content: center; }
        .blog-rt-list { list-style: none; margin: 4px 0 0; padding: 0; }
        .blog-rt-list li { display: flex; gap: 8px; align-items: flex-start; padding: 3px 0; font-size: 13px; line-height: 1.45; color: ${T.textSecondary}; font-family: ${T.sans}; }
        .blog-rt-list li b { flex-shrink: 0; width: 18px; height: 18px; border-radius: 50%; font-size: 10.5px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; box-sizing: border-box; }
        .blog-rt-list li strong { color: ${T.text}; font-weight: 700; }
        .blog-gates-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .blog-gates-card { border: 1px solid ${T.border}; border-radius: 12px; padding: 14px 14px 10px; background: ${T.bg}; }
        .blog-gates-list { list-style: none; margin: 0; padding: 0; }
        .blog-gates-list li { display: flex; gap: 10px; align-items: flex-start; padding: 7px 8px; border-radius: 8px; font-size: 13.5px; line-height: 1.45; color: ${T.textSecondary}; font-family: ${T.sans}; }
        .blog-gates-list li i { flex-shrink: 0; width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 1.5px solid #D6D0C6; font-style: normal; font-size: 11px; font-weight: 700; color: ${T.textMuted}; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
        .blog-gates-list li.is-gene { background: #FEF3C7; color: ${T.text}; }
        .blog-gates-list li.is-gene i { border-color: ${PRICE_B}; color: ${PRICE_B}; }
        .blog-gates-badge { display: inline-block; margin-left: 8px; padding: 1px 7px; border-radius: 999px; font-size: 11px; font-weight: 800; color: #fff; background: ${PRICE_B}; vertical-align: 1px; }
        @media (max-width: 640px) { .blog-gates-grid { grid-template-columns: 1fr; } }
        .blog-ring-wrap { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; justify-content: center; }
        .blog-thermo small { margin-top: 6px; font-size: 11.5px; color: ${T.textMuted}; font-family: ${T.sans}; text-align: center; line-height: 1.3; }
        .blog-leak-step { padding: 12px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-leak-step:first-child { border-top: none; padding-top: 0; }
        .blog-leak-head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
        .blog-leak-n { width: 22px; height: 22px; border-radius: 50%; background: ${T.primaryLight}; color: ${T.primaryDark}; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .blog-leak-bar { position: relative; display: flex; height: 30px; border-radius: 6px; overflow: hidden; margin-bottom: 8px; }
        .blog-leak-bar > span { display: block; height: 100%; }
        .blog-leak-bar > b { position: absolute; top: 50%; transform: translateY(-50%); font-size: 12.5px; font-weight: 700; color: ${T.text}; font-family: ${T.sans}; white-space: nowrap; }
        .blog-catch-row { display: grid; grid-template-columns: 190px minmax(0, 1fr); gap: 16px; align-items: center; padding: 12px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-catch-row:first-child { border-top: none; padding-top: 0; }
        .blog-catch-grid { display: flex; flex-wrap: wrap; gap: 5px; }
        .blog-catch-grid i { width: 18px; height: 18px; border-radius: 4px; box-sizing: border-box; display: inline-block; }
        .blog-ped-key { list-style: none; margin: 14px 0 0; padding: 12px 0 0; border-top: 1px solid ${T.borderLight}; display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 8px 18px; }
        .blog-ped-key li { display: flex; gap: 8px; align-items: flex-start; font-size: 12.5px; color: ${T.textSecondary}; font-family: ${T.sans}; line-height: 1.45; }
        .blog-ped-key b { color: ${T.text}; }
        .blog-rewind-axis { display: grid; grid-template-columns: 190px minmax(0, 1fr); gap: 14px; margin-bottom: 4px; }
        .blog-rewind-row { display: grid; grid-template-columns: 190px minmax(0, 1fr); gap: 14px; align-items: center; padding: 8px 0; border-top: 1px solid ${T.borderLight}; }
        .blog-rewind-label { display: flex; flex-direction: column; gap: 2px; }
        .blog-rewind-track { position: relative; height: 40px; border-right: 3px solid ${T.text}; }
        .blog-gene-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 10px; margin-top: 14px; }
        .blog-gene-card { border: 1px solid ${T.borderLight}; border-top: 3px solid; border-radius: 10px; padding: 12px 13px; background: ${T.bg}; }
        .blog-gene-line { font-size: 12.5px; color: ${T.textSecondary}; font-family: ${T.sans}; line-height: 1.45; margin-top: 6px; }
        .blog-gene-line > span { display: block; font-size: 10px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: ${T.textMuted}; }
        .blog-scroll-hint { display: none; font-size: 11.5px; color: ${T.textMuted}; font-family: ${T.sans}; margin: 0 0 8px; }
        /* Mobile */
        @media (max-width: 640px) {
          .blog-hero-title { font-size: 26px !important; line-height: 1.25 !important; }
          .blog-hero-excerpt { font-size: 15px !important; }
          .blog-content-area { padding: 28px 16px 48px !important; }
          .blog-stat-row .blog-stat-grid { grid-template-columns: 1fr !important; gap: 24px 0 !important; }
          .blog-stat-row .blog-stat-lead-value { font-size: 44px !important; }
          .blog-stat-row .blog-stat-rest { border-left: none !important; padding-left: 0 !important; border-top: 1px solid ${T.borderLight} !important; margin-top: 4px; }
          .blog-stat-card { flex-direction: column !important; }
          .blog-contrast-pair { grid-template-columns: 1fr !important; }
          .bc-head { margin-left: 104px !important; font-size: 13.5px !important; }
          .bc-crowds { left: 104px !important; }
          .bc-plot { height: 360px !important; }
          .bc-line em { font-size: 11px !important; left: 2px !important; }
          .bc-up, .bc-down { max-width: 88px !important; gap: 2px !important; }
          .bc-up i, .bc-down i { width: 10px !important; height: 10px !important; }
          .bc-counts strong { font-size: 18px !important; }
          .vl-strip { grid-template-columns: repeat(12, 1fr) !important; }
          .cs-part { grid-template-columns: 1fr !important; gap: 6px !important; }
          .cs-margin { border-right: none !important; border-left: 2px solid #E9B9A8; padding: 0 0 0 10px !important; flex-direction: row !important; align-items: center; flex-wrap: wrap; }
          .blog-mirror-row { grid-template-columns: minmax(0, 1fr) 92px minmax(0, 1fr) !important; gap: 6px !important; }
          .blog-mirror-label { font-size: 12px !important; }
          .blog-waffle-wrap { grid-template-columns: 1fr !important; }
          .blog-scroll-hint { display: block !important; }
          .blog-heatmap th[scope="row"] { white-space: normal !important; min-width: 92px; max-width: 108px; font-size: 11.5px !important; line-height: 1.25; }
          .blog-waffle { justify-self: start !important; max-width: 220px !important; }
          .blog-dot-row { grid-template-columns: 104px minmax(0, 1fr) !important; gap: 8px !important; }
          .blog-ladder-axis { grid-template-columns: 104px minmax(0, 1fr) !important; gap: 8px !important; }
          .blog-catch-row { grid-template-columns: 1fr !important; gap: 8px !important; }
          .blog-fork { grid-template-columns: 1fr !important; }
          .blog-rewind-axis { grid-template-columns: 1fr !important; gap: 0 !important; }
          .blog-rewind-axis > div:first-child { display: none; }
          .blog-rewind-row { grid-template-columns: 1fr !important; gap: 4px !important; padding: 10px 0 6px !important; }
          .blog-ped-key { grid-template-columns: 1fr !important; }
          .blog-fork-node::after { display: none; }
          .blog-fork-branches { border-left: none !important; padding-left: 0 !important; }
          .blog-fork-card::before { display: none; }
          .blog-dot-label { font-size: 11.5px !important; }
          .blog-dot-track { margin-right: 70px !important; }
          .blog-zone-track { margin-right: 54px !important; }
          .blog-zone-head { display: none !important; }
          .blog-exon-word { display: none; }
          .blog-strip-row { grid-template-columns: 1fr 44px !important; }
          .blog-strip-row > div:first-child { grid-column: 1 / -1; }
          .blog-strip-blocks { grid-column: 1; }
          .blog-metric-head { display: none !important; }
          .blog-metric-row { grid-template-columns: 1fr 1fr !important; }
          .blog-metric-row > div:first-child { grid-column: 1 / -1; }
          .blog-metric-mlabel { display: block !important; }
          .blog-rail-item { grid-template-columns: 72px 18px 1fr !important; gap: 6px !important; }
          .blog-rail-item > div:first-child { font-size: 11px !important; }
          .blog-contrast-pair > div + div { border-left: none !important; border-top: 1px solid ${T.border} !important; }
          .blog-sources-grid { grid-template-columns: 1fr !important; }
          .series-card-badge { width: 60px !important; min-height: 80px !important; }
          .series-card-badge div:last-child { font-size: 28px !important; }
          .series-card-content { padding: 14px 16px 14px 14px !important; }
          .series-card-title { font-size: 15px !important; }
          .series-card-desc { font-size: 12px !important; margin-top: 4px !important; }
          .series-card-arrow { padding: 0 12px 0 0 !important; }
          .blog-hero-header { padding: 36px 16px 28px !important; }
          .blog-tag-row { gap: 6px !important; }
          .blog-divider-visual { margin: 28px 0 24px !important; }
          .blog-term-grid { grid-template-columns: 1fr !important; }
          .blog-table th, .blog-table td { padding: 10px 10px !important; font-size: 13px !important; }
          .blog-table th[scope="row"] { font-size: 12px !important; }
        }
        /* Tablet */
        @media (min-width: 641px) and (max-width: 1024px) {
          .blog-hero-title { font-size: 32px !important; }
          .blog-content-area { padding: 36px 20px 56px !important; }
          .blog-hero-header { padding: 44px 20px 36px !important; }
        }
        /* Divider visual (replaces hr) */
        .blog-divider-visual {
          display: flex; align-items: center; gap: 12px;
          margin: 36px 0 32px;
        }
        .blog-divider-visual::before,
        .blog-divider-visual::after {
          content: '';
          flex: 1;
          height: 1px;
          background: ${T.borderLight};
        }
        .blog-divider-visual .dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: ${T.primaryMedium};
        }
        /* Lead-paragraph drop cap — editorial flourish on the first body paragraph.
           Single large serif initial cap that sets visual entry into the article. */
        .blog-lead-paragraph::first-letter {
          float: left;
          font-family: ${T.serif};
          font-size: 64px;
          font-weight: 700;
          line-height: 0.92;
          padding: 6px 12px 0 0;
          margin-top: 4px;
          color: ${T.text};
        }
        @media (max-width: 540px) {
          .blog-lead-paragraph::first-letter {
            font-size: 52px;
            padding: 4px 10px 0 0;
          }
        }
        /* Related posts grid stacks on narrow viewports */
        @media (max-width: 640px) {
          .blog-related-grid {
            grid-template-columns: 1fr !important;
          }
        }
        /* Next-steps block: tighten on phones so it reads as a footnote, not
           a banner, and let the link drop under its label when the line is
           too narrow to hold both. */
        @media (max-width: 540px) {
          .blog-next-steps {
            margin: 28px 0 32px !important;
            padding: 14px 14px 10px !important;
          }
          .blog-next-steps li {
            font-size: 14px !important;
          }
        }
        /* Sources grid stacks on mobile too — already in responsive CSS but being explicit */
        @media (max-width: 540px) {
          .blog-sources-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}} />

      {/* Breadcrumb nav for SEO. Width matches the editorial header / hero
          image (900px) so the top of the page reads as a unified billboard
          before the article body indents inward to the narrower reading
          column. */}
      <div style={{ background: T.surface }}>
        <nav aria-label="Breadcrumb" style={{
          maxWidth: 900, margin: "0 auto", padding: "14px 24px 0",
        }}>
          <ol style={{
            display: "flex", alignItems: "center", gap: 6,
            listStyle: "none", margin: 0, padding: 0,
            fontSize: 12, fontFamily: T.sans, color: T.textMuted,
          }}>
            <li><a href="/" style={{ color: T.textMuted, textDecoration: "none" }}>Home</a></li>
            <li aria-hidden="true" style={{ color: T.textMuted }}>/</li>
            <li><a href="/blog" style={{ color: T.textMuted, textDecoration: "none" }}>News and Guides</a></li>
            <li aria-hidden="true" style={{ color: T.textMuted }}>/</li>
            <li style={{ color: T.textSecondary, fontWeight: 500 }} aria-current="page">
              {post.title.length > 50 ? post.title.slice(0, 50) + "..." : post.title}
            </li>
          </ol>
        </nav>
      </div>

      {/* Article header — editorial layout: small category/date mark, then a
          large serif title, then a bold serif deck paragraph that does the
          summary work, then an author byline. Hero image follows AFTER the
          header (Nature Reviews / NEJM / The Atlantic pattern), so the words
          establish the article before the visual supports it. */}
      <header
        className="blog-hero-header"
        style={{
          background: T.surface,
          padding: "32px 24px 28px",
        }}
      >
        {/* Header inner block widens to 900px to align with the hero image
            below it, creating a billboard effect. The article body then
            indents inward to 720px for comfortable reading line length. */}
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          {/* Category mark + date — small, all-caps, structural */}
          <div className="blog-category-mark" style={{
            display: "flex", alignItems: "center", gap: "6px 10px", flexWrap: "wrap" as const,
            whiteSpace: "nowrap" as const,
            marginBottom: 28,
            fontSize: 11, fontFamily: T.sans, fontWeight: 700,
            letterSpacing: "0.14em", textTransform: "uppercase" as const,
            color: T.textMuted,
          }}>
            <a
              href={`/blog?tag=${encodeURIComponent(post.tags[0])}`}
              style={{
                color: T.primary, textDecoration: "none",
              }}
            >
              {post.tags[0]}
            </a>
            <span aria-hidden="true" style={{ color: T.borderLight }}>|</span>
            <time dateTime={post.date} style={{ color: T.textMuted, fontWeight: 500, letterSpacing: "0.06em" }}>
              {formatDate(post.date)}
            </time>
            {post.updated && post.updated > post.date && (
              <>
                <span aria-hidden="true" style={{ color: T.borderLight }}>|</span>
                <span style={{ color: T.textMuted, fontWeight: 500, letterSpacing: "0.06em" }}>
                  Updated <time dateTime={post.updated}>{formatDate(post.updated)}</time>
                </span>
              </>
            )}
            <a href={correctionHref(post.title, `/blog/${post.slug}`)} className="blog-report-error"
              style={{ marginLeft: "auto", color: "#B42318", background: "#FDECEC", border: "1px solid #F7C9C9", borderRadius: 999, padding: "4px 10px", fontWeight: 700, letterSpacing: "0.06em", textDecoration: "none", whiteSpace: "nowrap" as const }}>
              Report an error
            </a>
          </div>

          {/* H1 Title — large editorial serif */}
          <h1
            className="blog-hero-title"
            style={{
              fontSize: "clamp(36px, 5.5vw, 52px)",
              fontWeight: 700, color: T.text,
              fontFamily: T.serif, lineHeight: 1.1,
              letterSpacing: "-0.03em",
              margin: "0 0 24px",
            }}
          >
            {post.title}
          </h1>

          {/* Bold serif deck — primary article summary, replaces small excerpt */}
          <p
            className="blog-hero-deck"
            style={{
              fontSize: "clamp(18px, 2.3vw, 22px)",
              fontWeight: 600, color: T.text,
              fontFamily: T.serif, lineHeight: 1.45,
              letterSpacing: "-0.005em",
              margin: "0 0 28px",
            }}
          >
            {post.excerpt}
          </p>

          {/* Author byline: name links to the author page, which links to the editorial standards */}
          <div className="blog-byline" style={{
            fontSize: 14, fontFamily: T.sans, color: T.textSecondary,
            margin: 0,
            paddingBottom: 4,
          }}>
            By{" "}
            <a
              href="/about/jason-hunter"
              rel="author"
              style={{
                color: T.text, fontWeight: 600,
                textDecoration: "underline",
                textDecorationColor: T.borderLight,
                textUnderlineOffset: 3,
              }}
            >
              Jason Hunter
            </a>
            , independent writer at Trial Friend
          </div>
        </div>
      </header>

      {/* Hero image / animation — placed AFTER the header so the words lead.
          The image-alt text doubles as a discreet caption underneath. */}
      {post.heroAnimation ? (
        <div style={{ borderBottom: `1px solid ${T.borderLight}`, background: "#FAF7F2", padding: "28px 16px" }}>
          <div style={{ maxWidth: 560, margin: "0 auto", overflow: "hidden", borderRadius: 16, boxShadow: "0 1px 3px rgba(14,95,86,0.08)" }}>
            <BlogHeroAnimation size="full" />
          </div>
        </div>
      ) : post.image && (
        <div style={{
          background: post.imageBg || T.surface,
          borderBottom: `1px solid ${T.borderLight}`,
        }}>
          <div style={{
            maxWidth: 900, margin: "0 auto",
            overflow: "hidden",
            padding: post.imageFit === "contain" ? "24px 48px" : 0,
          }}>
            {/* For "cover" images (the default photographic heroes), use a
                container with a 16:9 aspect-ratio so the image shows in its
                natural proportions rather than being clipped to a 400px cap.
                Source images are typically 1600x900, so 16:9 matches and
                shows the full frame. The "contain" path (logos, wordmarks)
                keeps its original maxHeight and padding behavior since the
                aspect ratio of those varies and contain fits any shape. */}
            <div
              className="blog-hero-img-wrap"
              style={{
                width: "100%",
                position: "relative",
                ...(post.imageFit === "contain"
                  ? { display: "block" }
                  : { aspectRatio: "16 / 9", display: "block" }),
              }}
            >
              {/* next/image handles WebP/AVIF conversion + responsive srcset.
                  Hero is the LCP element on every blog post, so priority + fetchPriority
                  are both set so the browser starts fetching it during HTML parse
                  rather than after layout. sizes is capped at 900px since the
                  container max-width is 900. */}
              <Image
                src={post.image}
                alt={post.imageAlt || post.title}
                className="blog-hero-img"
                width={1600}
                height={900}
                priority
                fetchPriority="high"
                sizes="(max-width: 900px) 100vw, 900px"
                style={{
                  width: "100%",
                  height: post.imageFit === "contain" ? "auto" : "100%",
                  maxHeight: post.imageFit === "contain" ? 420 : undefined,
                  objectFit: post.imageFit || "cover",
                  objectPosition: post.imagePosition || "center center",
                  display: "block",
                }}
              />
            </div>
          </div>
          {/* imageAlt stays on the <img> for screen readers and image search,
              but is no longer rendered as a visible caption: describing a
              decorative hero to sighted readers ("a red traffic light against
              a teal sky") is filler no real publication prints. If a photo
              ever needs a visible credit or context line, add a dedicated
              imageCaption field rather than reusing alt text. */}
        </div>
      )}

      {/* Article content */}
      <article
        className="blog-content-area"
        style={{ maxWidth: 720, margin: "0 auto", padding: "40px 24px 60px" }}
        itemScope
        itemType="https://schema.org/Article"
      >
        <meta itemProp="headline" content={post.title} />
        <meta itemProp="datePublished" content={post.date} />
        <meta itemProp="author" content="Jason Hunter" />
        <meta itemProp="description" content={post.seoDescription} />

        <GlossaryScope>
        <div itemProp="articleBody" className="blog-article-body">
          {(() => {
            // Index of the first paragraph block — used to apply a drop cap to
            // the lead paragraph (NEJM / The Atlantic editorial pattern). Only
            // applied to the very first paragraph in the article body.
            const firstParaIdx = post.content.findIndex((b) => b.type === "paragraph");
            return post.content.map((block, i) => {
              // Reader-continuation elements are spliced in ahead of the block
              // they precede, so the block itself still renders normally below.
              const before: ReactNode[] = [];
              if (i === articleEndIdx) {
                before.push(<ArticleEndMarker key={`end-${i}`} slug={post.slug} beforeFaq />);
              }
              if (i === nextStepsBefore) {
                before.push(
                  <BlogNextSteps key={`ns-${i}`} steps={nextSteps} postSlug={post.slug} conditionName={conditionLabel} postDate={post.date} postUpdated={post.updated} afterDivider={post.content[i - 1]?.type === "divider"} />,
                );
              }
              if (i === signupBefore) {
                before.push(<div key={`su-${i}`}>{signupEl}</div>);
              }
              const after: ReactNode[] = i === signupAfter ? [<div key={`sa-${i}`}>{signupEl}</div>] : [];
              const wrap = (node: ReactNode) =>
                before.length || after.length ? <div key={`w-${i}`}>{before}{node}{after}</div> : node;

              if (block.type === "alert-signup") {
                return wrap(<div key={i}>{signupEl}</div>);
              }
              if (block.type === "divider") {
                return wrap(
                  <div key={i} className="blog-divider-visual" aria-hidden="true">
                    <span className="dot" />
                  </div>
                );
              }
              if (block.type === "paragraph" && i === firstParaIdx) {
                return wrap(
                  <p key={i} className="blog-lead-paragraph" style={{
                    fontSize: 17, lineHeight: 1.85, color: T.textSecondary,
                    margin: "0 0 20px", fontFamily: T.sans,
                  }}>
                    {parseInlineLinks(block.text)}
                  </p>
                );
              }
              return wrap(renderBlock(block, i));
            });
          })()}
          {articleEndIdx >= post.content.length && <ArticleEndMarker slug={post.slug} beforeFaq={false} />}
          {/* Posts with fewer than two headings never hit the in-body slot,
              so the next steps land after the body instead. */}
          {nextStepsBefore < 0 && (
            <BlogNextSteps steps={nextSteps} postSlug={post.slug} conditionName={conditionLabel} postDate={post.date} postUpdated={post.updated} />
          )}
        </div>
        </GlossaryScope>

        {/* Per-post alert capture. Normally rendered in-body just before the
            FAQ heading (see signupBefore above); this is the fallback for
            posts that have no FAQ section, keeping the original placement. */}
        {!hasInlineSignup && signupBefore < 0 && signupAfter < 0 && signupEl}

        <CorrectionNote title={post.title} path={`/blog/${post.slug}`} />

        {/* Sources section */}
        {post.sources.length > 0 && (
          <section aria-label="Sources and references">
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              margin: "48px 0 24px",
            }}>
              <div style={{ flex: 1, height: 1, background: T.border }} />
              <h2 style={{
                fontSize: 11, fontWeight: 700, color: T.textMuted,
                fontFamily: T.sans, textTransform: "uppercase" as const,
                letterSpacing: "0.08em", margin: 0,
              }}>
                Sources
              </h2>
              <div style={{ flex: 1, height: 1, background: T.border }} />
            </div>

            <div
              className="blog-sources-grid"
              style={{
                display: "grid", gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
              role="list"
              aria-label="Article sources"
            >
              {post.sources.map((src, i) => (
                <a
                  key={i}
                  href={src.url}
                  target="_blank"
                  rel="noopener"
                  role="listitem"
                  style={{
                    display: "block", padding: "12px 14px",
                    background: T.surface, border: `1px solid ${T.borderLight}`,
                    borderRadius: T.r2, textDecoration: "none",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = T.primaryMedium;
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(14,95,86,0.06)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = T.borderLight;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div style={{
                    fontSize: 13, fontWeight: 500, color: T.text,
                    fontFamily: T.sans, lineHeight: 1.4,
                    marginBottom: 4,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical" as const,
                    overflow: "hidden",
                  }}>
                    {src.title}
                  </div>
                  <div style={{
                    fontSize: 11, color: T.textMuted, fontFamily: T.sans,
                  }}>
                    {src.publisher}{src.date ? ` \u00B7 ${src.date}` : ""}
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Tagged at bottom — full tag list with click-through filtering. The
            primary category appears in the header up top; the full tag set
            lives here so it doesn't compete for visual weight in the hero. */}
        {post.tags.length > 0 && (
          <section aria-label="Article tags" style={{
            marginTop: 56,
            paddingTop: 24,
            borderTop: `1px solid ${T.borderLight}`,
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8,
          }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: T.textMuted,
              fontFamily: T.sans, letterSpacing: "0.14em",
              textTransform: "uppercase" as const, marginRight: 4,
            }}>Tagged</span>
            {post.tags.map((tag) => (
              <a
                key={tag}
                href={`/blog?tag=${encodeURIComponent(tag)}`}
                style={{
                  fontSize: 12, fontWeight: 500, color: T.textSecondary,
                  textDecoration: "none", fontFamily: T.sans,
                  padding: "5px 12px", borderRadius: 16,
                  border: `1px solid ${T.borderLight}`,
                  background: T.surface,
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = T.primaryMedium;
                  e.currentTarget.style.color = T.primary;
                  e.currentTarget.style.background = T.primaryLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = T.borderLight;
                  e.currentTarget.style.color = T.textSecondary;
                  e.currentTarget.style.background = T.surface;
                }}
              >
                {tag}
              </a>
            ))}
          </section>
        )}

        {/* Related on Trial Friend — top 3 posts by tag overlap, computed
            on the server in page.tsx so we don't ship the entire BLOG_POSTS
            array (full content of every post) to the browser. */}
        {relatedPosts.length > 0 && (
          <section aria-label="Related posts" style={{ marginTop: 56 }}>
            <h2 style={{
              fontSize: 11, fontWeight: 700, color: T.textMuted,
              fontFamily: T.sans, textTransform: "uppercase" as const,
              letterSpacing: "0.14em", margin: "0 0 20px",
            }}>
              More on Trial Friend
            </h2>
            <div className="blog-related-grid" style={{
              display: "grid", gridTemplateColumns: `repeat(${relatedPosts.length}, 1fr)`,
              gap: 16,
            }}>
              {relatedPosts.map((rp) => (
                <a
                  key={rp.slug}
                  href={`/blog/${rp.slug}`}
                  style={{
                    display: "block", padding: "16px 18px",
                    background: T.surface, border: `1px solid ${T.borderLight}`,
                    borderRadius: T.r3, textDecoration: "none",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = T.primaryMedium;
                    e.currentTarget.style.boxShadow = "0 2px 12px rgba(14,95,86,0.07)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = T.borderLight;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: T.primary,
                    fontFamily: T.sans, letterSpacing: "0.12em",
                    textTransform: "uppercase" as const, marginBottom: 8,
                  }}>{rp.tag}</div>
                  <div style={{
                    fontSize: 16, fontWeight: 600, color: T.text,
                    fontFamily: T.serif, lineHeight: 1.3,
                    letterSpacing: "-0.01em",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical" as const,
                    overflow: "hidden",
                  }}>{rp.title}</div>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Back to blog */}
        <nav aria-label="News and analysis navigation" style={{ marginTop: 48, textAlign: "center" as const }}>
          <a
            href="/blog"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: 14, fontWeight: 600, color: T.primary,
              textDecoration: "none", fontFamily: T.sans,
              padding: "10px 20px", borderRadius: T.r2,
              border: `1.5px solid ${T.primaryMedium}`,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = T.primaryLight; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            All articles
          </a>
        </nav>

        {post.disclosure && (
          <p style={{ fontSize: 12.5, lineHeight: 1.6, color: T.textMuted, fontFamily: T.sans, margin: "40px 0 0", paddingTop: 14, borderTop: `1px solid ${T.borderLight}` }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 8 }}>Disclosure</span>
            {post.disclosure}
          </p>
        )}
      </article>
      </main>

      <Footer />

      <ScrollToggle />
    </div>
  );
}
